import { useState, useRef, useEffect, useMemo, useCallback, memo } from "react"
import { createPortal } from "react-dom"
import { debounce } from "lodash"; // Install lodash if not already installed
import "./ChatPage.css";
import DeleteConfirmationModal from "../modals/DeleteConfirmation";

// Image cache to prevent duplicate requests
const imageCache = new Map();
const loadingImages = new Set();

// Audio Waveform Component for real-time recording
const AudioWaveform = ({ analyserNode, isRecording, width = 200, height = 40, color = "#4A90E2" }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!analyserNode || !canvasRef.current || !isRecording) {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording || !analyserNode) {
        return;
      }

      animationFrameRef.current = requestAnimationFrame(draw);

      analyserNode.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = 40; // Number of bars in waveform
      const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
      const barGap = 2;
      const minBarHeight = 2; // Minimum bar height for visual consistency

      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor((i / barCount) * bufferLength);
        const normalizedValue = dataArray[dataIndex] / 255;
        const barHeight = Math.max(minBarHeight, normalizedValue * canvas.height * 0.9);
        const x = i * (barWidth + barGap);
        const y = (canvas.height - barHeight) / 2;

        // Create gradient for visual appeal
        const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color + '80');

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [analyserNode, isRecording, color]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block' }}
    />
  );
};



// WhatsApp-style Audio Player Component
const WhatsAppAudioPlayer = ({ src, isMe = false, duration = null }) => {
  // DO NOT use crossOrigin for S3 URLs - they don't have CORS configured
  // Only use crossOrigin for CDNs that have CORS properly configured
  const { t } = useTranslation();

  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const isSetupRef = useRef(false);
  const timeUpdateFrameRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [webAudioAvailable, setWebAudioAvailable] = useState(true); // Track if Web Audio API is available
  const loadingStartTimeRef = useRef(null);

  useEffect(() => {
    // Reset loading state when src changes
    setIsLoading(true);

    if (!src) {
      setIsLoading(false);
      return;
    }
  }, [src]);

  useEffect(() => {
    if (!src) {
      return;
    }

    // Wait for audio element to be in DOM
    const setupAudio = () => {
      if (!audioRef.current) {
        // Retry after a short delay if audio element isn't ready
        setTimeout(setupAudio, 50);
        return;
      }

      const audio = audioRef.current;
      let loadingTimeout = null;

      const handleLoadStart = () => {
        setIsLoading(true);
        loadingStartTimeRef.current = Date.now();
      };

      const finishLoading = () => {
        // Clear any existing timeout
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        // Show loading for minimum 500ms for better UX
        const elapsed = loadingStartTimeRef.current ? Date.now() - loadingStartTimeRef.current : 0;
        const minDisplayTime = 500;
        if (elapsed < minDisplayTime) {
          loadingTimeout = setTimeout(() => {
            setIsLoading(false);
            loadingTimeout = null;
          }, minDisplayTime - elapsed);
        } else {
          setIsLoading(false);
        }
      };

      const handleLoadedMetadata = () => {
        if (audio.duration) {
          setTotalDuration(audio.duration);
        }
        finishLoading();
      };

      const handleCanPlay = () => {
        finishLoading();
      };

      const handleTimeUpdate = () => {
        if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
        }
      };

      // Also update time more frequently for smoother progress
      const updateTime = () => {
        if (audio && !audio.paused && !audio.ended) {
          if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
            setCurrentTime(audio.currentTime);
          }
          timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
        } else {
          timeUpdateFrameRef.current = null;
        }
      };

      const startTimeUpdateLoop = () => {
        if (!timeUpdateFrameRef.current && audio && !audio.paused && !audio.ended) {
          timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
        }
      };

      const stopTimeUpdateLoop = () => {
        if (timeUpdateFrameRef.current) {
          cancelAnimationFrame(timeUpdateFrameRef.current);
          timeUpdateFrameRef.current = null;
        }
      };

      const handlePlay = () => {
        // Ensure audio is unmuted when playing
        audio.muted = false;
        audio.volume = 1.0;
        setIsPlaying(true);
        // Start the update loop immediately
        startTimeUpdateLoop();
      };

      const handlePlaying = () => {
        // This event fires when audio actually starts playing (after play event)
        // Ensure audio is unmuted when playing
        audio.muted = false;
        audio.volume = 1.0;
        setIsPlaying(true);
        // Ensure update loop is running
        startTimeUpdateLoop();
      };

      const handlePause = () => {
        setIsPlaying(false);
        stopTimeUpdateLoop();
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(audio.duration || 0);
      };

      const handleError = () => {
        const error = audio.error;


        // If error is due to CORS or source issues, try removing crossOrigin
        if (error && (error.code === 4 || error.code === 3)) {
          if (audio.crossOrigin) {
            audio.crossOrigin = null;
            audio.load();
          }
        }

        setIsLoading(false);
      };

      // Mark loading start time
      loadingStartTimeRef.current = Date.now();

      // Check if audio is already loaded
      if (audio.readyState >= 2) {
        // HAVE_CURRENT_DATA or higher means metadata is loaded
        if (audio.duration) {
          setTotalDuration(audio.duration);
        }
        // Show loading for minimum 500ms even if already loaded
        loadingTimeout = setTimeout(() => setIsLoading(false), 500);
      } else {
        setIsLoading(true);
      }

      // Attach event listeners
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('playing', handlePlaying);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      // Check initial playing state
      if (!audio.paused) {
        setIsPlaying(true);
      }

      // Set initial playback rate
      audio.playbackRate = playbackRate;

      // Ensure audio is unmuted and volume is set
      audio.muted = false;
      audio.volume = 1.0;

      // Force load if needed (this will trigger loadstart if not already loaded)
      if (audio.readyState < 2) {
        audio.load();
      }

      // Set unmuted again after load
      setTimeout(() => {
        if (audio) {
          audio.muted = false;
          audio.volume = 1.0;
        }
      }, 100);

      // Fallback: ensure loading state clears after 5 seconds max
      const fallbackTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 5000);

      return () => {
        if (fallbackTimeout) {
          clearTimeout(fallbackTimeout);
        }
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        stopTimeUpdateLoop();
        // Remove all event listeners
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('playing', handlePlaying);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    };

    const cleanup = setupAudio();
    return cleanup || (() => { });
  }, [src, playbackRate]);

  // Update playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Setup waveform visualization (optional - only if Web Audio API is available)
  useEffect(() => {
    if (!audioRef.current || !canvasRef.current || isLoading) return;

    const audio = audioRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // If Web Audio API is not available, draw a static waveform
    if (!webAudioAvailable) {
      // Draw a simple static waveform pattern
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barCount = 40;
      const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
      const barGap = 2;
      const color = '#EF4444';

      // Draw static bars with varying heights
      for (let i = 0; i < barCount; i++) {
        const barHeight = 4 + Math.random() * (canvas.height * 0.6);
        const x = i * (barWidth + barGap);
        const y = (canvas.height - barHeight) / 2;

        ctx.fillStyle = color;
        ctx.fillRect(x, y, barWidth, barHeight);
      }
      return; // Exit early - no need to setup Web Audio API
    }

    // Reset audio context when src changes
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
      sourceRef.current = null;
      analyserRef.current = null;
      isSetupRef.current = false;
    }

    const setupAudioContext = async () => {
      if (isSetupRef.current || audioContextRef.current) return;

      // If Web Audio API is not available, skip setup and allow direct playback
      if (!webAudioAvailable) {
        return;
      }

      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') {
          try {
            await audioCtx.resume();
          } catch (resumeError) {

          }
        }
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        if (!sourceRef.current) {
          // If this fails, audio won't play. So we need to be very careful here.
          // Check if audio is ready and can play before attempting this
          if (audio.readyState < 2) {

            return;
          }

          try {
            const source = audioCtx.createMediaElementSource(audio);
            source.connect(analyser);
            analyser.connect(audioCtx.destination);
            sourceRef.current = source;
          } catch (sourceError) {
            // but allow audio to play directly

            setWebAudioAvailable(false);
            audioContextRef.current?.close().catch(() => { });
            audioContextRef.current = null;
            return; // Exit early - audio can still play directly
          }
        }

        audioContextRef.current = audioCtx;
        analyserRef.current = analyser;
        isSetupRef.current = true;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
          if (!analyserRef.current || !canvasRef.current || !webAudioAvailable) return;

          if (audio.paused || audio.ended) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            animationFrameRef.current = requestAnimationFrame(draw);
            return;
          }

          animationFrameRef.current = requestAnimationFrame(draw);
          analyserRef.current.getByteFrequencyData(dataArray);
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          const barCount = 40;
          const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
          const barGap = 2;
          const minBarHeight = 2;
          const color = '#EF4444';

          for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.floor((i / barCount) * bufferLength);
            const normalizedValue = dataArray[dataIndex] / 255;
            const barHeight = Math.max(minBarHeight, normalizedValue * canvas.height * 0.8);
            const x = i * (barWidth + barGap);
            const y = (canvas.height - barHeight) / 2;

            const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
            gradient.addColorStop(0, color);
            gradient.addColorStop(1, color + '80');

            ctx.fillStyle = gradient;
            ctx.fillRect(x, y, barWidth, barHeight);
          }
        };

        draw();
      } catch (error) {
        // If Web Audio API setup fails completely, disable it but allow direct playback

        setWebAudioAvailable(false);
        // Ensure audio can still play directly
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => { });
          audioContextRef.current = null;
        }
        sourceRef.current = null;
        analyserRef.current = null;
        isSetupRef.current = false;
      }
    };

    const handlePlay = () => {
      // Only attempt Web Audio API setup if it's available and not already set up
      if (webAudioAvailable && !isSetupRef.current) {
        setupAudioContext();
      }
    };

    audio.addEventListener('play', handlePlay);

    // Only setup if audio is already playing and Web Audio API is available
    if (!audio.paused && webAudioAvailable) {
      setupAudioContext();
    }

    return () => {
      audio.removeEventListener('play', handlePlay);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => { });
        audioContextRef.current = null;
      }
      sourceRef.current = null;
      analyserRef.current = null;
      isSetupRef.current = false;
    };
  }, [isLoading, src, webAudioAvailable]);

  // Draw static waveform when Web Audio API is not available and audio is playing
  useEffect(() => {
    if (!canvasRef.current || !audioRef.current || webAudioAvailable) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const audio = audioRef.current;

    // Only draw static waveform when playing
    if (!audio.paused && !audio.ended) {
      const drawStatic = () => {
        if (audio.paused || audio.ended || webAudioAvailable) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barCount = 40;
        const barWidth = (canvas.width - (barCount - 1) * 2) / barCount;
        const barGap = 2;
        const color = '#EF4444';

        // Draw animated static bars
        for (let i = 0; i < barCount; i++) {
          const barHeight = 4 + Math.random() * (canvas.height * 0.6);
          const x = i * (barWidth + barGap);
          const y = (canvas.height - barHeight) / 2;

          ctx.fillStyle = color;
          ctx.fillRect(x, y, barWidth, barHeight);
        }

        if (!audio.paused && !audio.ended) {
          requestAnimationFrame(drawStatic);
        }
      };

      drawStatic();
    } else {
      // Clear canvas when paused
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isPlaying, webAudioAvailable]);

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) {

      return;
    }

    const audio = audioRef.current;

    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch((resumeError) => {

      });
    }

    // Check if audio has valid sources
    if (!audio.src || audio.readyState === 0) {

      // Try to reload the audio
      if (src) {
        audio.load();
        // Wait a bit for audio to load, then try playing
        setTimeout(() => {
          if (audio.readyState >= 2) {
            togglePlayPause();
          }
        }, 500);
      }
      return;
    }

    // Check actual audio state, not React state
    if (audio.paused) {
      // Audio is paused, so play it
      // Ensure audio is unmuted before playing
      audio.muted = false;
      audio.volume = 1.0;


      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Ensure still unmuted after play
            audio.muted = false;
            audio.volume = 1.0;
            setIsPlaying(true);

            // Start time update loop immediately
            if (audio && !audio.paused && !audio.ended) {
              const updateTime = () => {
                if (audio && !audio.paused && !audio.ended) {
                  if (audio.currentTime !== undefined && !isNaN(audio.currentTime)) {
                    setCurrentTime(audio.currentTime);
                  }
                  timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
                } else {
                  timeUpdateFrameRef.current = null;
                }
              };
              if (!timeUpdateFrameRef.current) {
                timeUpdateFrameRef.current = requestAnimationFrame(updateTime);
              }
            }
          })
          .catch(err => {

            setIsPlaying(false);

            // Handle different error types
            if (err.name === 'NotAllowedError') {
              // This is usually fine - user can click again
            } else if (err.name === 'NotSupportedError') {

              // Try removing crossOrigin if present
              if (audio.crossOrigin) {
                audio.crossOrigin = null;
                audio.load();
                // Retry after load
                setTimeout(() => {
                  if (audio.readyState >= 2) {
                    togglePlayPause();
                  }
                }, 500);
              }
            } else if (err.name === 'AbortError') {
              // Handle abort
            }
          });
      } else {
        // Fallback for browsers that don't return a promise
        setIsPlaying(true);
      }
    } else {
      // Audio is playing, so pause it
      audio.pause();
      setIsPlaying(false);
    }
  };

  // Handle playback speed change
  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
  };

  // Handle seeking when user clicks on waveform
  const handleWaveformClick = (e) => {
    if (!audioRef.current || !displayDuration || displayDuration <= 0) return;

    const waveformContainer = e.currentTarget;
    const rect = waveformContainer.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * displayDuration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Use provided duration or calculated duration
  const displayDuration = duration || totalDuration;

  // Loading state - show recording-indicator-style UI
  if (isLoading) {
    return (
      <div ref={containerRef} className={`whatsapp-audio-player loading ${isMe ? 'is-me' : ''}`}>
        <div className="whatsapp-audio-loading-content">
          <div className="whatsapp-audio-loading-animation">
            <div className="whatsapp-audio-loading-dot"></div>
          </div>
          <div className="whatsapp-audio-loading-waveform">
            <div className="whatsapp-audio-loading-waveform-placeholder">
              {/* Static waveform bars for loading state */}
              <div className="whatsapp-audio-loading-bars">
                {Array.from({ length: 20 }).map((_, i) => {
                  // Fixed heights for consistent appearance, with slight variation
                  const heights = [30, 50, 70, 40, 60, 45, 55, 35, 65, 50, 40, 60, 45, 55, 50, 35, 65, 40, 60, 50];
                  return (
                    <div
                      key={i}
                      className="whatsapp-audio-loading-bar"
                      style={{
                        height: `${heights[i % heights.length]}%`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="whatsapp-audio-loading-info">
            <span className="whatsapp-audio-loading-text">Loading...</span>
            {displayDuration > 0 && (
              <span className="whatsapp-audio-loading-duration">{formatTime(displayDuration)}</span>
            )}
          </div>
        </div>
        <audio
          crossOrigin="anonymous"
          ref={audioRef}
          src={src}
          preload="metadata"
          style={{ display: 'none' }}
          onLoadedMetadata={() => { }}
          onCanPlay={(e) => {
            const audio = e.target;
            audio.muted = false;
            audio.volume = 1.0;
          }}
          onError={() => { }}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`whatsapp-audio-player loaded ${isMe ? 'is-me' : ''}`}>
      <div className="whatsapp-audio-content">
        <button
          className="whatsapp-audio-play-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlayPause();
          }}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="4" y="2" width="3" height="12" />
              <rect x="9" y="2" width="3" height="12" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 2l10 6-10 6V2z" />
            </svg>
          )}
        </button>

        <div
          className="whatsapp-audio-waveform"
          onClick={handleWaveformClick}
          style={{ cursor: 'pointer' }}
          title="Click to seek"
        >
          <audio
            crossOrigin="anonymous"
            ref={audioRef}
            src={src}
            preload="metadata"
            style={{ display: 'none' }}
            onLoadedMetadata={() => { }}
            onCanPlay={(e) => {
              const audio = e.target;
              audio.muted = false;
              audio.volume = 1.0;
            }}
            onError={() => { }}
          />
          <canvas
            ref={canvasRef}
            width={200}
            height={40}
            className="whatsapp-audio-waveform-canvas"
          />
          <div className="whatsapp-audio-waveform-progress">
            <div
              className="whatsapp-audio-waveform-progress-bar"
              style={{ width: `${displayDuration > 0 ? (currentTime / displayDuration) * 100 : 0}%` }}
            />
          </div>
        </div>

        <div className="whatsapp-audio-info">
          <span className="whatsapp-audio-duration">{formatTime(currentTime)} / {formatTime(displayDuration)}</span>
        </div>

        <div className="whatsapp-audio-speed-selector">
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 0.5 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(0.5)}
            title={t('chat.audio.speed', { value: '0.5x' })}
          >
            0.5x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 1 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(1)}
            title={t('chat.audio.speed', { value: '1x' })}
          >
            1x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 1.5 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(1.5)}
            title={t('chat.audio.speed', { value: '1.5x' })}
          >
            1.5x
          </button>
          <button
            className={`whatsapp-audio-speed-btn ${playbackRate === 2 ? 'active' : ''}`}
            onClick={() => handleSpeedChange(2)}
            title={t('chat.audio.speed', { value: '2x' })}
          >
            2x
          </button>
        </div>
      </div>
    </div>
  );
};

// Cached image component
const CachedImage = ({ src, alt, style, ...props }) => {
  const [imageSrc, setImageSrc] = useState(() => {
    // Initialize with placeholder if src is invalid
    if (!src ||
      src === '' ||
      src === 'null' ||
      src === 'undefined' ||
      src === null ||
      src === undefined ||
      typeof src === 'object' ||
      (typeof src === 'string' && (src.trim() === '' || src.trim().toLowerCase() === 'null' || src.trim().toLowerCase() === 'undefined'))) {
      return getUserPlaceholderImage(null);
    }
    const trimmedSrc = String(src).trim();
    if (trimmedSrc.startsWith('null') || trimmedSrc.startsWith('undefined') || trimmedSrc.includes('null/') || trimmedSrc.includes('undefined/')) {
      return getUserPlaceholderImage(null);
    }
    return src;
  });
  const [isLoading, setIsLoading] = useState(() => {
    const trimmedSrc = src && typeof src === 'string' ? src.trim() : '';
    if (!src || trimmedSrc === '' || trimmedSrc === 'null' || trimmedSrc === 'undefined') {
      return false;
    }
    return !imageCache.has(src);
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Handle empty strings, null, undefined, or invalid URLs
    // Also check for string versions of null/undefined
    if (!src ||
      src === '' ||
      src === 'null' ||
      src === 'undefined' ||
      src === null ||
      src === undefined ||
      typeof src === 'object' ||
      (typeof src === 'string' && (src.trim() === '' || src.trim().toLowerCase() === 'null' || src.trim().toLowerCase() === 'undefined'))) {
      setImageSrc(getUserPlaceholderImage(null));
      setIsLoading(false);
      return;
    }

    // Additional validation - check if src looks like a valid URL or path
    const trimmedSrc = String(src).trim();
    if (trimmedSrc.startsWith('null') || trimmedSrc.startsWith('undefined') || trimmedSrc.includes('null/') || trimmedSrc.includes('undefined/')) {
      setImageSrc(getUserPlaceholderImage(null));
      setIsLoading(false);
      return;
    }

    // Check if image is already cached
    if (imageCache.has(src)) {
      setImageSrc(imageCache.get(src));
      setIsLoading(false);
      return;
    }

    // Check if image is currently loading
    if (loadingImages.has(src)) {
      const checkLoading = () => {
        if (imageCache.has(src)) {
          setImageSrc(imageCache.get(src));
          setIsLoading(false);
        } else {
          setTimeout(checkLoading, 50);
        }
      };
      checkLoading();
      return;
    }

    // Start loading the image
    loadingImages.add(src);
    const img = new Image();

    img.onload = () => {
      if (img.width > 0 && img.height > 0) {
        imageCache.set(src, src);
        loadingImages.delete(src);
        setImageSrc(src);
        setIsLoading(false);
      } else {
        // Image failed to load properly
        loadingImages.delete(src);
        setImageSrc(getUserPlaceholderImage(null));
        setIsLoading(false);
        setHasError(true);
      }
    };

    img.onerror = () => {
      loadingImages.delete(src);
      setImageSrc(getUserPlaceholderImage(null));
      setIsLoading(false);
      setHasError(true);
    };

    try {
      const url = new URL(src, window.location.origin);
      if (url.protocol === 'blob:' || url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'data:') {
        img.src = src;
      } else {
        // Invalid protocol, use placeholder
        loadingImages.delete(src);
        setImageSrc(getUserPlaceholderImage(null));
        setIsLoading(false);
      }
    } catch (e) {
      // Invalid URL, use placeholder
      loadingImages.delete(src);
      setImageSrc(getUserPlaceholderImage(null));
      setIsLoading(false);
    }
  }, [src]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      onError={(e) => {
        // Fallback to placeholder if image fails to load
        const placeholder = getUserPlaceholderImage(null);
        if (e.target.src !== placeholder) {
          e.target.src = placeholder;
        }
      }}
      style={{
        ...style,
        opacity: 1, // Always use full opacity for consistent appearance
        transition: 'opacity 0.2s ease-in-out'
      }}
      {...props}
    />
  );
};
import { useTranslation } from "react-i18next"
import RatingModal from "../modals/RatingModal"
import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { Search, Phone, Video, MoreHorizontal, Paperclip, Camera, Smile, Mic, Send, RefreshCw, ChevronUp, ChevronDown, MessageSquarePlus, Star, Trash2 } from "lucide-react"
import { conversationsAPI, updateTypingStatus, petAPI, clinicAppointmentsAPI, urgentCaseAPI } from "../../services/api"
import { getUserPlaceholderImage } from "../../utils/imageUtils"
import { authUtils } from "../../services/authAPI"
import { USER_TYPES } from "../../constants/userTypes"
import { getRTLStyles } from "../../utils/textDirection"
import { useWebSocket } from "../../hooks/useWebSocket"
import EmojiPicker from "./EmojiPicker"
import { useUserPresence } from "../../hooks/useUserPresence"
import { useTypingIndicator } from "../../hooks/useTypingIndicator"
import TypingIndicator from "./TypingIndicator"
import { getEcho } from "../../services/echo"
import AppointmentModal from "../modals/AppointmentModal"
import { useClinicData } from "../../hooks/useClinicData"
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils"
import SelectFriendModal from "./SelectFriendModal"
// Add CSS for smooth message animations
const messageAnimationStyles = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Helper function to check if URL is from S3 or external domain
// Note: S3 URLs should NOT use crossOrigin if CORS is not configured
const isExternalUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    const currentOrigin = window.location.origin;
    const urlOrigin = urlObj.origin;
    // Check if it's a different origin (but NOT S3, as S3 may not have CORS configured)
    // Only use crossOrigin for CloudFront or other CDNs that have CORS configured
    return urlOrigin !== currentOrigin &&
      !url.includes('amazonaws.com') &&
      !url.includes('s3.');
  } catch {
    return false;
  }
};

const MessageBubble = memo(({ message, currentChat, openMediaModal, getFileIcon, onDelete }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [videoLoading, setVideoLoading] = useState({});
  const [videoLoaded, setVideoLoaded] = useState({});
  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch {
      return {};
    }
  }, []);

  // Check if message is empty (no content and no media)
  const hasContent = message.content && typeof message.content === 'string' && message.content.trim().length > 0;
  const hasMedia = message.media && message.media.length > 0;

  if (!hasContent && !hasMedia) {
    return null;
  }

  // Strictly check if message is from current user
  // Primary check: isOwnMessage flag (most reliable, from API)
  // If isOwnMessage is explicitly false, it's NOT from current user
  // Fallback: check senderId === "me" or compare user IDs
  let isMe = false;

  if (message.isOwnMessage === true) {
    isMe = true;
  } else if (message.isOwnMessage === false) {
    isMe = false;
  } else {
    // isOwnMessage is undefined/null - use fallback checks
    if (message.senderId === "me") {
      isMe = true;
    } else if (userData && message.user) {
      // Compare user IDs as final fallback
      const currentUserId = userData.user_id || userData.id;
      const messageUserId = message.user.user_id || message.user.id;
      if (currentUserId && messageUserId) {
        isMe = String(currentUserId) === String(messageUserId);
      }
    }
  }

  const failedImageText = t('chat.media.failedImage');
  const videoLoadingText = t('chat.common.loading');

  // No observer needed - always show media directly
  return (
    <div
      id={message.id || message.messageId}
      className={`message-bubble-container ${isMe ? 'is-me' : ''} ${message.isDeleting ? 'is-deleting' : ''}`}
    >
      {!isMe && (() => {
        // Normalize and validate profile image URL
        let profileImageSrc = message.user?.profile_image_url;
        if (typeof profileImageSrc === 'string') {
          profileImageSrc = profileImageSrc.trim();
          if (profileImageSrc === '' ||
            profileImageSrc.toLowerCase() === 'null' ||
            profileImageSrc.toLowerCase() === 'undefined' ||
            profileImageSrc.startsWith('null') ||
            profileImageSrc.startsWith('undefined') ||
            profileImageSrc.includes('null/') ||
            profileImageSrc.includes('undefined/')) {
            profileImageSrc = null;
          }
        } else {
          profileImageSrc = null;
        }

        // Try currentChat avatar as fallback
        if (!profileImageSrc && currentChat?.avatar) {
          let chatAvatar = currentChat.avatar;
          if (typeof chatAvatar === 'string') {
            chatAvatar = chatAvatar.trim();
            if (chatAvatar !== '' &&
              chatAvatar.toLowerCase() !== 'null' &&
              chatAvatar.toLowerCase() !== 'undefined' &&
              !chatAvatar.startsWith('null') &&
              !chatAvatar.startsWith('undefined')) {
              profileImageSrc = chatAvatar;
            }
          }
        }

        // Final fallback to placeholder
        if (!profileImageSrc) {
          profileImageSrc = getUserPlaceholderImage(message.user || currentChat?.otherParticipant);
        }

        return (
          <CachedImage
            src={profileImageSrc}
            alt={message.senderName}
            className="message-bubble-avatar"
          />
        );
      })()}

      <div className="message-bubble-wrapper">
        <div className={`message-header-row ${isMe ? 'is-me' : ''}`}>
          {!isMe && (
            <div className="message-sender-name">
              {message.senderName || message.user?.first_name || message.user?.name || currentChat?.name || t('chat.list.unknownUser')}
            </div>
          )}
          {isMe && (
            <div className="message-sender-name">
              {t('chat.messages.you')}
            </div>
          )}

          {isMe && !message.is_deleted && onDelete && (
            <button
              className="message-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(message.id || message.messageId);
              }}
              title={t('chat.messages.delete')}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <div className={`message-bubble ${isMe ? 'is-me' : ''}`}
          style={{ backgroundColor: (message.media && message.media.length > 0) ? "transparent" : (isMe ? "#4A90E2" : "#E8E8E8") }}
        >
          {message.type === "text" && (
            <p className="message-text" style={getRTLStyles(message.content)}>
              {message.content}
            </p>
          )}
          {message.type !== "text" && (
            <div>
              {message.content && (
                <p className="message-text" style={{ margin: "0 0 8px 0", ...getRTLStyles(message.content) }}>
                  {message.content}
                </p>
              )}
              {message.media && message.media.length > 0 && message.type !== "audio" && !message.media.some(m => m.isAudio || (m.type && m.type.startsWith('audio/'))) && (
                <div
                  className={`message-media-grid ${message.media.length === 1 ? 'one' : (message.media.length === 2 ? 'two' : 'three-plus')}`}
                >
                  {message.media.map((media, index) => (
                    <div
                      key={`${media.url}-${index}`}
                      className="message-media-item"
                      onClick={() => openMediaModal(media)}
                    >
                      {media.isImage ? (
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <CachedImage
                            src={media.url}
                            alt={media.name || t('chat.media.sharedImage')}
                            loading="lazy"
                            decoding="async"
                            className="message-media-image"
                            onLoad={(e) => {
                              e.target.style.opacity = "1";
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = `
                                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 8px; background-color: rgba(0, 0, 0, 0.1);">
                                  <span style="font-size: 10px; color: #DC3545;">${failedImageText}</span>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : media.isVideo ? (
                        <div
                          className="message-media-video-container"
                          onClick={() => {
                            const videoId = `${message.id}-${index}`;
                            if (!videoLoaded[videoId] && !videoLoading[videoId]) {
                              setVideoLoading(prev => ({ ...prev, [videoId]: true }));
                              setTimeout(() => {
                                setVideoLoaded(prev => ({ ...prev, [videoId]: true }));
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }, 100);
                            }
                          }}
                        >
                          {videoLoaded[`${message.id}-${index}`] ? (
                            <video
                              ref={(videoEl) => {
                                if (videoEl) {
                                  // Immediately set unmuted and volume
                                  videoEl.muted = false;
                                  videoEl.volume = 1.0;
                                  // DO NOT set crossOrigin for S3 URLs - they don't have CORS configured
                                  // Set again after a short delay to override any browser defaults
                                  setTimeout(() => {
                                    videoEl.muted = false;
                                    videoEl.volume = 1.0;
                                  }, 100);
                                }
                              }}
                              controls
                              muted={false}
                              playsInline
                              className="message-media-video"
                              onLoadedMetadata={(e) => {
                                const video = e.target;
                                video.muted = false;
                                video.volume = 1.0;
                              }}
                              onLoadedData={(e) => {
                                const video = e.target;
                                video.muted = false;
                                video.volume = 1.0;
                                const videoId = `${message.id}-${index}`;
                                setVideoLoaded(prev => ({ ...prev, [videoId]: true }));
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }}
                              onCanPlay={(e) => {
                                const video = e.target;
                                video.muted = false;
                                video.volume = 1.0;
                              }}
                              onPlay={(e) => {
                                const video = e.target;
                                video.muted = false;
                                video.volume = 1.0;
                              }}
                              onVolumeChange={(e) => {
                                const video = e.target;
                                if (video.muted) {
                                  video.muted = false;
                                }
                                if (video.volume < 1.0) {
                                  video.volume = 1.0;
                                }
                              }}
                              onError={(e) => {
                                const videoId = `${message.id}-${index}`;
                                setVideoLoading(prev => ({ ...prev, [videoId]: false }));
                              }}
                            >
                              <source src={media.url} type={media.type} />
                            </video>
                          ) : videoLoading[`${message.id}-${index}`] ? (
                            <div className="message-media-video-loading">
                              <div className="message-media-video-spinner" />
                              <span style={{ fontSize: "10px" }}>{videoLoadingText}</span>
                            </div>
                          ) : (
                            <div className="message-media-video-placeholder">
                              <div className="message-media-video-play-btn">
                                <span style={{ fontSize: "20px", marginLeft: "2px" }}>▶️</span>
                              </div>
                              <span style={{ fontSize: "10px", fontWeight: "500" }}>{t('chat.media.clickToPlay')}</span>
                            </div>
                          )}
                        </div>
                      ) : (media.type && media.type.startsWith('audio/')) || media.isAudio ? (
                        <div className="message-media-audio">
                          <WhatsAppAudioPlayer
                            src={media.url}
                            isMe={isMe}
                            duration={media.duration}
                          />
                        </div>
                      ) : !((media.type && media.type.startsWith('audio/')) || media.isAudio) ? (
                        <div className="message-media-file">
                          <div className="message-media-file-content">
                            <span className="message-media-file-icon">
                              {getFileIcon(media.type)}
                            </span>
                            <p className="message-media-file-name">
                              {media.name || 'Unknown file'}
                            </p>
                          </div>
                          {(media.isDocument || (!media.isImage && !media.isVideo && !media.isAudio)) && (
                            <a
                              href={media.url}
                              download={media.name || 'file'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="message-media-file-download-btn"
                              title="Download file"
                            >
                              Download
                            </a>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(message.type === "audio" || (message.media && message.media.some(m => m.isAudio || (m.type && m.type.startsWith('audio/'))))) && (
            <div className="message-audio">
              {message.media && message.media.length > 0 ? (
                <div className="message-audio-player-container">
                  <WhatsAppAudioPlayer
                    src={message.media[0].url}
                    isMe={isMe}
                    duration={message.duration || message.media[0]?.duration}
                  />
                </div>
              ) : (
                <div className="message-audio">
                  <button className="message-audio-btn">
                    <Mic className="message-audio-icon" />
                  </button>
                  <div className="message-audio-progress">
                    <div className="message-audio-progress-bar" />
                  </div>
                  <span className="message-audio-duration">
                    {message.duration || '0:00'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`message-footer ${isMe ? 'is-me' : ''}`}>
          <span>{message.timestamp}</span>
          {isMe && (
            <div className="message-status-container">
              {message.isSending ? (
                <div className="message-status-spinner" />
              ) : (
                <span className={`message-status-check ${message.isRead ? 'read' : ''}`}>
                  ✓
                </span>
              )}
              {message.isRead && !message.isSending && (
                <span className="message-status-text read">
                  {t('chat.messages.read')}
                </span>
              )}
              {message.isSending && (
                <span className="message-status-text sending">
                  {t('chat.messages.sending')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {
        isMe && (() => {
          // Normalize and validate profile image URL for own messages
          let profileImageSrc = message.user?.profile_image_url;
          if (typeof profileImageSrc === 'string') {
            profileImageSrc = profileImageSrc.trim();
            if (profileImageSrc === '' ||
              profileImageSrc.toLowerCase() === 'null' ||
              profileImageSrc.toLowerCase() === 'undefined' ||
              profileImageSrc.startsWith('null') ||
              profileImageSrc.startsWith('undefined') ||
              profileImageSrc.includes('null/') ||
              profileImageSrc.includes('undefined/')) {
              profileImageSrc = null;
            }
          } else {
            profileImageSrc = null;
          }

          // Try to get from localStorage as fallback for own messages
          if (!profileImageSrc) {
            try {
              const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
              const storedImage = userData.profile_image_url;
              if (storedImage && typeof storedImage === 'string') {
                const trimmed = storedImage.trim();
                if (trimmed !== '' &&
                  trimmed.toLowerCase() !== 'null' &&
                  trimmed.toLowerCase() !== 'undefined' &&
                  !trimmed.startsWith('null') &&
                  !trimmed.startsWith('undefined')) {
                  profileImageSrc = trimmed;
                }
              }
            } catch (e) {
              // Ignore localStorage errors
            }
          }

          // Final fallback to placeholder
          if (!profileImageSrc) {
            profileImageSrc = getUserPlaceholderImage(message.user || currentChat?.otherParticipant);
          }

          return (
            <CachedImage
              src={profileImageSrc}
              alt={message.senderName}
              className="message-bubble-avatar"
            />
          );
        })()
      }
    </div >
  );
}, (prevProps, nextProps) => {
  const prev = prevProps.message;
  const next = nextProps.message;
  return (
    prev.id === next.id &&
    prev.content === next.content &&
    prev.timestamp === next.timestamp &&
    prev.isRead === next.isRead &&
    prev.senderId === next.senderId &&
    prev.senderName === next.senderName &&
    prev.isOwnMessage === next.isOwnMessage &&
    prev.isSending === next.isSending &&
    prev.user?.profile_image_url === next.user?.profile_image_url &&
    JSON.stringify(prev.media) === JSON.stringify(next.media) &&
    prev.is_deleted === next.is_deleted &&
    prev.isDeleting === next.isDeleting
  );
});

const DeleteMessageModal = ({ isOpen, onClose, onConfirm, t, isRTL }) => {
  if (!isOpen) return null;

  return (
    <div className="custom-modal-overlay" onClick={onClose}>
      <div className={`custom-modal-content delete-modal ${isRTL ? 'rtl' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="delete-modal-icon">
          <Trash2 size={32} />
        </div>
        <h3>{t('chat.messages.deleteTitle') || "Delete Message?"}</h3>
        <p>{t('chat.messages.confirmDelete') || "Are you sure you want to delete this message? This action cannot be undone."}</p>
        <div className="delete-modal-actions">
          <button className="delete-modal-cancel" onClick={onClose}>
            {t('common.cancel') || "Cancel"}
          </button>
          <button className="delete-modal-confirm" onClick={onConfirm}>
            {t('chat.messages.delete') || "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatPage = ({ initialChatId = null, onBack = null }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get route parameters
  const { receiverId, conversationType, conversationId } = useParams();

  // Check if we came from an adoption request
  const adoptionRequestId = searchParams.get('fromAdoptionRequest');

  const tabLabels = useMemo(() => ({
    all: t('chat.tabs.all'),
    social: t('chat.tabs.social'),
    medical: t('chat.tabs.medical')
  }), [t]);
  const youLabel = t('chat.messages.you');
  const voiceMessageLabel = t('chat.messages.voiceMessage');

  // Initialize WebSocket connection
  const { isConnected, subscribe, unsubscribe, listen, stopListening } = useWebSocket();

  // Get Echo instance for typing indicators and presence
  const [echo, setEcho] = useState(null);

  // Initialize Echo on mount
  useEffect(() => {
    const echoInstance = getEcho();
    setEcho(echoInstance);
  }, []);

  // Initialize user presence tracking (heartbeat for online status)
  useUserPresence();

  // Get current user type to determine which tabs to show
  const currentUserType = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      return userData.user_type || userData.userType || USER_TYPES.PET_GUARDIAN;
    } catch (error) {

      return USER_TYPES.PET_GUARDIAN;
    }
  }, []);


  // Inject CSS animation styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = messageAnimationStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [selectedChat, setSelectedChat] = useState(null)
  // Ref to track current selectedChat without triggering re-renders
  const selectedChatRef = useRef(null)
  const [isRecording, setIsRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [audioChunks, setAudioChunks] = useState([])
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [recordedAudio, setRecordedAudio] = useState(null)
  const [recordingTimer, setRecordingTimer] = useState(null)
  const [audioContext, setAudioContext] = useState(null)
  const [analyserNode, setAnalyserNode] = useState(null)
  const [audioStream, setAudioStream] = useState(null)
  const [waveformData, setWaveformData] = useState([])
  const [chats, setChats] = useState([])
  const [chatUpdates, setChatUpdates] = useState({}) // Separate state for chat updates
  const [recentlyUpdatedChats, setRecentlyUpdatedChats] = useState(new Set()) // Track chats that were recently updated for animation
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [ratingCaseId, setRatingCaseId] = useState(null)
  const [messages, setMessages] = useState({})
  const setMessagesRef = useRef(setMessages); // always fresh reference
  useEffect(() => { setMessagesRef.current = setMessages; }, [setMessages]);
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState(null)

  // Ref to track processed messages to prevent duplicates
  const processedMessages = useRef(new Set())

  // ────────────────────────────────────────────────────────────────────
  // GLOBAL deletion handler — registered once, never stale
  // Searches ALL conversation buckets so chatId mismatch is irrelevant
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onDeleteSync = (event) => {
      const { messageId, message_id, conversationId } = event.detail || {};
      const msgId = messageId || message_id;
      if (!msgId) return;

      console.log('🗑️ [GLOBAL] Deleting message:', msgId, 'in conversation:', conversationId);

      // Phase 1: animate out (search ALL buckets)
      setMessagesRef.current(prev => {
        const next = {};
        for (const [key, msgs] of Object.entries(prev)) {
          // Debug: print all IDs to find the mismatch
          const idList = msgs.map(m => `id="${m.id}" msgId="${m.messageId}"`).join(' | ');
          console.log(`🗑️ [GLOBAL] Bucket "${key}" IDs: ${idList}`);
          next[key] = msgs.map(msg =>
            (String(msg.id) === String(msgId) ||
              String(msg.messageId) === String(msgId) ||
              String(msg.message_id) === String(msgId))
              ? { ...msg, isDeleting: true }
              : msg
          );
        }
        console.log('🗑️ [GLOBAL] State after animate-out pass');
        return next;
      });

      // Phase 2: remove after animation
      setTimeout(() => {
        setMessagesRef.current(prev => {
          const next = {};
          for (const [key, msgs] of Object.entries(prev)) {
            next[key] = msgs.filter(msg =>
              String(msg.id) !== String(msgId) &&
              String(msg.messageId) !== String(msgId) &&
              String(msg.message_id) !== String(msgId)
            );
          }
          return next;
        });
      }, 400);
    };

    window.addEventListener('message-deleted-sync', onDeleteSync);
    return () => window.removeEventListener('message-deleted-sync', onDeleteSync);
  }, []); // empty deps — never re-registered, never stale

  const [selectedFiles, setSelectedFiles] = useState([])
  const [isSending, setIsSending] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [messageToDelete, setMessageToDelete] = useState(null)

  // Appointment modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [isSavingAppointment, setIsSavingAppointment] = useState(false)
  const [targetClinicData, setTargetClinicData] = useState(null)

  // Get clinic data for appointments
  const { data: clinicData, clinicMembers, clinic, ownedClinics } = useClinicData()

  // Handle clinic selection/fresh fetch
  const handleClinicSelected = async (selectedClinic) => {
    try {
      // Show loading equivalent (using saving state for now to block UI or just proceed)
      setIsSavingAppointment(true);

      const { clinicAPI } = await import('../../services/api');
      const response = await clinicAPI.getClinicSetupData({ clinic_id: selectedClinic.id });
      const { clinicService } = await import('../../services/clinicService');
      const responseData = clinicService.transformClinicSetupResponse(response);

      setTargetClinicData(responseData);
      setShowAppointmentModal(true);
    } catch (error) {

      showErrorToast(t('errors.generic') || 'Failed to load clinic data');
    } finally {
      setIsSavingAppointment(false);
    }
  }

  // Initialize typing indicator for the selected conversation
  const { usersTyping: wsUsersTyping, onTyping } = useTypingIndicator(selectedChat, echo)

  // Track typing users from notifications per conversation (fallback when WebSocket fails)
  // Maps conversationId -> Set of user IDs who are typing
  const [conversationTypingUsers, setConversationTypingUsers] = useState({})
  const typingTimeoutsRef = useRef({}) // Track timeouts for auto-removal (keys: conversationId_userId)

  // Helper function to get typing users for a specific conversation
  const getTypingUsersForConversation = useCallback((conversationId) => {
    if (!conversationId) return [];
    const typingSet = conversationTypingUsers[conversationId.toString()];
    return typingSet ? Array.from(typingSet) : [];
  }, [conversationTypingUsers]);

  // Function to stop typing indicator for a specific user in a conversation
  const stopTypingForUser = useCallback((conversationId, userId) => {
    if (!conversationId || !userId) return;

    const convIdStr = conversationId.toString();
    const timeoutKey = `${convIdStr}_${userId}`;

    // Remove from notification-based typing for this conversation
    setConversationTypingUsers(prev => {
      const updated = { ...prev };
      if (updated[convIdStr]) {
        const newSet = new Set(updated[convIdStr]);
        newSet.delete(userId);
        if (newSet.size === 0) {
          delete updated[convIdStr];
        } else {
          updated[convIdStr] = newSet;
        }
      }
      return updated;
    });

    // Clear timeout if exists
    if (typingTimeoutsRef.current[timeoutKey]) {
      clearTimeout(typingTimeoutsRef.current[timeoutKey]);
      delete typingTimeoutsRef.current[timeoutKey];
    }
  }, []);

  // Function to stop typing for current user (when sending message or input is empty)
  const stopCurrentUserTyping = useCallback(async () => {
    if (!selectedChat) return;

    try {
      // Stop typing via API
      await updateTypingStatus(selectedChat, false);
    } catch (error) {

    }
  }, [selectedChat]);

  // Merge WebSocket and notification-based typing indicators for selected conversation only
  const usersTyping = useMemo(() => {
    if (!selectedChat) return [];
    const notificationTyping = conversationTypingUsers[selectedChat.toString()] || new Set();
    const merged = new Set([...wsUsersTyping, ...Array.from(notificationTyping)]);
    return Array.from(merged);
  }, [wsUsersTyping, conversationTypingUsers, selectedChat]);

  // Track online status for users in conversations
  const [onlineUsers, setOnlineUsers] = useState(new Set())
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState({})
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [newMessages, setNewMessages] = useState({}) // Separate state for new messages
  const [isUserAtBottom, setIsUserAtBottom] = useState(true)
  const [messageInputText, setMessageInputText] = useState("") // Track message input for RTL detection

  // Watch for empty input and stop typing (safety net)
  // Note: onTyping handler also handles this, but this ensures it stops even if hook fails
  const wasTypingRef = useRef(false);
  useEffect(() => {
    const isEmpty = !messageInputText.trim();
    if (isEmpty && selectedChat && wasTypingRef.current) {
      // Input became empty and we were typing, stop typing
      stopCurrentUserTyping();
      wasTypingRef.current = false;
    } else if (!isEmpty) {
      // Input has content, mark as typing
      wasTypingRef.current = true;
    }
  }, [messageInputText, selectedChat, stopCurrentUserTyping]);

  // Keep selectedChatRef in sync with selectedChat to avoid stale closures
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiButtonRef = useRef(null)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraMode, setCameraMode] = useState('photo') // 'photo' or 'video'
  const [isRecordingVideo, setIsRecordingVideo] = useState(false)
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isDeleteChatModalOpen, setIsDeleteChatModalOpen] = useState(false);
  const [isHideChatModalOpen, setIsHideChatModalOpen] = useState(false);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [isHidingChat, setIsHidingChat] = useState(false);
  // New state for header dropdown menu
  const headerMenuRef = useRef(null) // Ref for header dropdown menu

  // Close header menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setShowHeaderMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [videoRecorder, setVideoRecorder] = useState(null)
  const [videoChunks, setVideoChunks] = useState([])
  const [cameraLoading, setCameraLoading] = useState(false)
  const cameraVideoRef = useRef(null)
  const cameraCanvasRef = useRef(null)
  const [activeTab, setActiveTab] = useState(() => {

    // Set initial active tab based on conversationType parameter
    if (conversationType && ['all', 'medical', 'social'].includes(conversationType)) {
      return conversationType;
    }
    return 'all'; // Default fallback
  })

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(() => {
    const isMobileInitial = window.innerWidth <= 768;
    const hasInitialTarget = !!(conversationId || receiverId || initialChatId);
    return isMobileInitial && !hasInitialTarget;
  })
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const swipeStartRef = useRef(null)
  const swipeThreshold = 50 // Minimum swipe distance in pixels

  // Action buttons menu state for small screens (425px and below)
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 425)
  const actionMenuDragStartRef = useRef(null)
  const actionMenuContainerRef = useRef(null)

  // Select friend modal state
  const [showSelectFriendModal, setShowSelectFriendModal] = useState(false)

  // Handle window resize and close sidebar when resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768
      const small = window.innerWidth <= 425
      setIsMobile(mobile)
      setIsSmallScreen(small)
      if (!mobile) {
        setIsMobileSidebarOpen(false)
      }
      if (!small) {
        setIsActionMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMobileSidebarOpen])

  const messagesEndRef = useRef(null)
  const messagesStartRef = useRef(null)
  const fileInputRef = useRef(null)
  const scrollPositionRef = useRef(null)
  const isAddingNewMessageRef = useRef(false)
  const messagesContainerRef = useRef(null)
  const processedReceiverIdRef = useRef(null)
  const isProcessingNotificationRef = useRef(false) // Track if we're processing a notification to prevent auto-selection


  // Transform API response to match expected format
  const transformChatData = (apiChat) => {
    // Validate required fields
    if (!apiChat || !apiChat.other_participant) {

      return null
    }

    const otherParticipant = apiChat.other_participant
    const latestMessage = apiChat.latest_message

    // Build safe display name even if last_name is missing
    const firstName = otherParticipant.first_name || ''
    const lastName = otherParticipant.last_name || ''
    const displayName = `${firstName} ${lastName}`.trim() || t('chat.list.unknownUser')

    // Handle profile_image_url - check for empty strings and invalid values
    const participantProfileImage = otherParticipant.profile_image_url;
    const validAvatar = (participantProfileImage &&
      participantProfileImage !== '' &&
      participantProfileImage !== 'null' &&
      participantProfileImage !== 'undefined')
      ? participantProfileImage
      : "/icons/person.png";

    return {
      id: apiChat.id,
      name: displayName,
      avatar: validAvatar,
      lastMessage: latestMessage ? latestMessage.content : t('chat.list.noMessages'),
      isempty: latestMessage ? false : true,
      timestamp: latestMessage ? formatTimestamp(latestMessage.created_at) : formatTimestamp(apiChat.updated_at),
      unreadCount: apiChat.unread_count || 0,
      isOnline: apiChat.is_online || false, // Use online status from API
      lastSeenAt: apiChat.last_seen_at || null, // Use last seen from API
      userType: "user", // Default user type
      type: apiChat.type || "all", // Preserve conversation type from API
      otherParticipant: otherParticipant,
      isClosed: apiChat.closed_at !== null, // Add closed status
      closedAt: apiChat.closed_at, // Store closed timestamp
      can_send_message: apiChat.can_send_message !== undefined ? apiChat.can_send_message : true, // Add permission flag
      // Preserve case_id and case_type for SOS conversations
      case_id: apiChat.case_id,
      case_type: apiChat.case_type,
      caseId: apiChat.case_id,
      caseType: apiChat.case_type,
      // Use backend provided is_sos flag if available, otherwise check case data
      // Note: Use != null to handle both null and undefined (missing property)
      isSOS: !!apiChat.is_sos || apiChat.case_type === 'UrgentMedicalCase' || (apiChat.case_id != null && apiChat.case_id !== ''),
    }
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return t('timeAgo.justNow')
      if (diffInMinutes < 60) return t('timeAgo.minutes', { count: diffInMinutes })

      const diffInHours = Math.floor(diffInMinutes / 60)
      if (diffInHours < 24) return t('timeAgo.hours', { count: diffInHours })

      const diffInDays = Math.floor(diffInHours / 24)
      if (diffInDays < 7) return t('timeAgo.days', { count: diffInDays })

      return date.toLocaleDateString(i18n.language, { calendar: 'gregory' })
    } catch (error) {

      return t('timeAgo.unknown')
    }
  }

  // Memoized chats that combine base chats with updates to prevent unnecessary re-renders
  const memoizedChats = useMemo(() => {
    return chats.map(chat => {
      const update = chatUpdates[chat.id];
      if (update) {
        return { ...chat, ...update };
      }
      return chat;
    });
  }, [chats, chatUpdates]);

  const scrollToBottomSmooth = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const markConversationAsRead = async (conversationId) => {
    try {
      const currentChat = memoizedChats.find(chat => chat.id === conversationId)
      const currentMessages = messages[conversationId] || []

      if (currentChat?.unreadCount > 0 || currentMessages.some(msg => !msg.isRead)) {
        await conversationsAPI.markAsRead(conversationId, { mark_all: true })

        setChatUpdates(prev => ({
          ...prev,
          [conversationId]: {
            unreadCount: 0
          }
        }))

        setMessages(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map(msg => {
            // Only update read status for received messages (read_for_me tracking)
            // For sent messages, keep existing read_at status (depends on receiver's privacy)
            if (!msg.isOwnMessage) {
              return { ...msg, isRead: true }
            }
            return msg
          })
        }))
      } else {
      }
    } catch (err) {

    }
  }

  const addNewMessageToConversation = useCallback((conversationId, messageData, skipProcessedCheck = false) => {

    // Extract and normalize message ID — ALWAYS prioritize the numeric DB `message_id`
    // over the `id` field which may be a notification UUID (e.g. "844a72bd-...")
    const messageId = messageData.message_id || messageData.messageId || messageData.id;
    const normalizedMessageId = messageId ? String(messageId) : null;

    // Check if message was already processed - early return to prevent duplicates
    // Skip this check if skipProcessedCheck is true (e.g., when called from handleMedicalConversationNotification)
    if (!skipProcessedCheck && normalizedMessageId && processedMessages.current.has(normalizedMessageId)) {

      return;
    }

    // Mark message as processed IMMEDIATELY to prevent race conditions (if not already processed)
    if (normalizedMessageId && !processedMessages.current.has(normalizedMessageId)) {
      processedMessages.current.add(normalizedMessageId);
      // Clean up old processed messages to prevent memory leaks
      if (processedMessages.current.size > 100) {
        const firstMessage = processedMessages.current.values().next().value;
        processedMessages.current.delete(firstMessage);
      }
    }

    // Stop typing indicator for the sender when message is received
    const senderId = messageData.senderId || messageData.sender_id || messageData.sender?.id;
    if (senderId && conversationId) {
      stopTypingForUser(conversationId, senderId);
    }

    // Determine if message has media and set appropriate type
    const hasMedia = messageData.media && messageData.media.length > 0;

    // Helper function to detect if a media item is audio
    const isAudioMedia = (mediaItem) => {
      // Check explicit flags
      if (mediaItem.is_audio || mediaItem.isAudio || mediaItem.type === 'audio') {
        return true;
      }

      // Check mime type
      const mimeType = mediaItem.mime_type || mediaItem.type;
      if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
        return true;
      }

      // Check URL extension for common audio formats
      const url = mediaItem.url || '';
      if (typeof url === 'string') {
        const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
        const lowerUrl = url.toLowerCase();
        if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
          return true;
        }
      }

      // Check if message content indicates voice message
      const messageContent = messageData.message || messageData.content || '';
      if (typeof messageContent === 'string' &&
        (messageContent.toLowerCase().includes('voice') ||
          messageContent.toLowerCase().includes('audio'))) {
        return true;
      }

      return false;
    };

    // Check if any media item is audio
    const hasAudio = hasMedia && messageData.media.some(isAudioMedia);
    const messageType = hasAudio ? 'audio' : (hasMedia ? 'media' : 'text');

    // Format media data if present
    const formattedMedia = hasMedia ? messageData.media.map(mediaItem => {
      const isAudio = isAudioMedia(mediaItem);
      const mimeType = mediaItem.mime_type || mediaItem.type;

      return {
        url: mediaItem.url,
        name: mediaItem.file_name || mediaItem.name,
        type: mimeType || (isAudio ? 'audio/webm' : null),
        size: mediaItem.file_size || mediaItem.size,
        isImage: mediaItem.is_image || (mimeType && mimeType.startsWith('image/')),
        isVideo: mediaItem.is_video || (mimeType && mimeType.startsWith('video/')),
        isDocument: !isAudio && !mediaItem.is_image && !mediaItem.is_video &&
          !(mimeType && (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/'))),
        isAudio: isAudio,
        caption: mediaItem.caption || null
      };
    }) : null;

    // Determine if this is the current user's message
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const currentUserId = userData.user_id || userData.id;
    const messageUserId = messageData.senderId;

    // Check if message is from current user (use isOwnMessage flag if provided, otherwise compare IDs)
    const isOwnMessage = messageData.isOwnMessage !== undefined
      ? messageData.isOwnMessage
      : (messageData.isOwn !== undefined
        ? messageData.isOwn
        : (currentUserId && messageUserId && String(currentUserId) === String(messageUserId)));

    // Create a new message object in the expected format
    // Create a new message object in the expected format
    // Use normalized message ID to ensure consistency with processedMessages
    const newMessage = {
      id: normalizedMessageId || messageData.id || `msg-${Date.now()}-${Math.random()}`, // Use normalized ID first, then provided ID, or generate stable one
      messageId: normalizedMessageId || messageData.id || messageData.messageId, // Explicitly store the ID for deletion matching
      senderId: messageData.senderId || (isOwnMessage ? currentUserId : messageUserId),
      senderName: isOwnMessage
        ? 'You'
        : (messageData.sender?.name || messageData.sender || 'Someone'),
      content: messageData.message || messageData.content,
      timestamp: formatTimestamp(new Date()),
      originalTimestamp: new Date().toISOString(),
      type: messageType,
      user: {
        id: messageData.senderId || (isOwnMessage ? currentUserId : messageUserId),
        first_name: isOwnMessage
          ? 'You'
          : (messageData.sender?.name || messageData.sender || 'Someone'),
        last_name: '',
        profile_image_url: (messageData.sender?.image &&
          messageData.sender?.image !== '' &&
          messageData.sender?.image !== 'null' &&
          messageData.sender?.image !== 'undefined')
          ? messageData.sender.image
          : '/icons/person.png'
      },
      isOwnMessage: isOwnMessage,
      // For own messages (sent): use read_at if available (read receipt from receiver)
      // For received messages: use read_for_me if available, otherwise true (viewing conversation)
      isRead: isOwnMessage
        ? (messageData.read_at || false)
        : (messageData.read_for_me !== undefined ? messageData.read_for_me : true),
      media: formattedMedia
    };

    // Use selectedChatRef.current to get the current value without stale closure
    const currentSelectedChat = selectedChatRef.current;

    // Check if this conversation is currently open
    const isCurrentConversation = currentSelectedChat && conversationId && currentSelectedChat.toString() === conversationId.toString();


    if (isCurrentConversation) {


      // Use direct state update with duplicate prevention to minimize re-renders
      setMessages(prev => {
        const currentMessages = prev[conversationId] || [];
        // Extract all possible message identifiers for comparison
        const newMessageId = newMessage.id;
        const newMessageIdAlt = messageData.id || messageData.messageId;
        const newMessageContent = newMessage.content;
        const newMessageTimestamp = newMessage.originalTimestamp;
        const newMessageSenderId = newMessage.senderId;

        // Only update if message doesn't already exist (prevent duplicates)
        // Check multiple identifiers to catch all duplicate cases
        const messageExists = currentMessages.some(msg =>
          msg.id === newMessageId ||
          (newMessageIdAlt && (msg.id === String(newMessageIdAlt) || msg.id === newMessageIdAlt)) ||
          (msg.content === newMessageContent && msg.originalTimestamp === newMessageTimestamp && msg.senderId === newMessageSenderId)
        );
        if (messageExists) {

          return prev; // No change needed
        }


        return {
          ...prev,
          [conversationId]: [...currentMessages, newMessage]
        };
      });

      // Mark as read immediately since we are in the conversation
      markConversationAsRead(conversationId);

      // Smart scroll to bottom for new messages
      setTimeout(() => {
        scrollToBottomSmooth();
      }, 100);

    } else {

      // If conversation is not open, add to newMessages for later merging
      setNewMessages(prev => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), newMessage]
      }));
    }

    // Update only the specific chat using chatUpdates to prevent re-rendering the entire list
    setChatUpdates(prev => ({
      ...prev,
      [conversationId]: {
        lastMessage: messageData.message,
        timestamp: formatTimestamp(new Date()),
        unreadCount: 0 // Set to 0 since user is actively viewing the conversation
      }
    }));

    // Automatically mark conversation as read since user is actively viewing it
    setTimeout(async () => {
      try {
        const currentChat = memoizedChats.find(chat => chat.id === conversationId);
        const currentMessages = messages[conversationId] || [];
        // if (currentChat?.unreadCount > 0 || currentMessages.some(msg => !msg.isRead)) {

        await conversationsAPI.markAsRead(conversationId, { mark_all: true });

        // Update the unread count in the chat list without triggering reload
        setChatUpdates(prev => ({
          ...prev,
          [conversationId]: {
            unreadCount: 0
          }
        }));

        // Update read status for all messages in this conversation
        setMessages(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map(msg => {
            // Only update read status for received messages (read_for_me tracking)
            // For sent messages, keep existing read_at status (depends on receiver's privacy)
            if (!msg.isOwnMessage) {
              return { ...msg, isRead: true }
            }
            return msg
          })
        }));
        // }
      } catch (error) {

      }
    }, 500); // Small delay to ensure message is added first
  }, [stopTypingForUser]); // Removed selectedChat from dependencies since we use selectedChatRef

  // Filter chats based on active tab
  const filteredChats = useMemo(() => {
    if (activeTab === 'all') {
      return memoizedChats;
    }



    return memoizedChats.filter(chat => chat.type === activeTab);
  }, [memoizedChats, activeTab, currentUserType]);

  const getEmptyStateMessage = useCallback(() => {

    if (activeTab === 'all') {
      return t('chat.list.emptyAll');
    }

    return t('chat.list.emptyType', { type: tabLabels[activeTab] });
  }, [activeTab, currentUserType, tabLabels, t]);

  // Load chats from API
  const loadChats = async () => {
    try {
      setLoading(true)
      setError(null)
      let response = null

      response = await conversationsAPI.getChats()
      const transformedChats = response.data.data.map(transformChatData).filter(chat => chat !== null)
      setChats(transformedChats)
      setChatUpdates({})
    } catch (err) {
      let errorMessage = t('chat.errors.loadChats')

      if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.message || t('chat.errors.server', { code: err.response.status })
      } else if (err.request) {
        // Network error
        errorMessage = t('chat.errors.network')
      } else {
        // Other error
        errorMessage = err.message || errorMessage
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const updateChatListWithNewMessage = (messageData) => {
    const senderId = messageData.senderId;
    const conversationId = messageData.conversationId || messageData.conversation_id;
    const conversationType = messageData.conversationType || messageData.conversation_type;

    // Strictly find by conversationId and conversationType to prevent leakage
    let existingChat = null;
    if (conversationId) {
      existingChat = memoizedChats.find((chat) =>
        chat.id?.toString() === conversationId?.toString() &&
        chat.type === conversationType
      );
    }

    if (existingChat) {

      // Update the chat in the list and move it to the top (most recent first)
      setChats(prev => {
        // Find the chat in the current state (not memoized)
        const currentChat = prev.find(chat => chat.id === existingChat.id);
        if (!currentChat) {

          return prev;
        }

        const updatedChat = {
          ...currentChat,
          lastMessage: messageData.message || messageData.content || messageData.lastMessage,
          timestamp: formatTimestamp(new Date()),
          unreadCount: selectedChat === existingChat.id ? 0 : (currentChat.unreadCount || 0) + 1,
        };

        // Mark this chat as recently updated for animation
        setRecentlyUpdatedChats(prev => {
          const newSet = new Set(prev);
          newSet.add(existingChat.id);
          return newSet;
        });

        // Remove the animation class after animation completes
        setTimeout(() => {
          setRecentlyUpdatedChats(prev => {
            const newSet = new Set(prev);
            newSet.delete(existingChat.id);
            return newSet;
          });
        }, 500); // Match animation duration

        // Remove the chat from its current position and add it to the top
        const otherChats = prev.filter(chat => chat.id !== existingChat.id);
        return [updatedChat, ...otherChats];
      });

      // Clear any pending updates for this chat since we updated it directly
      setChatUpdates((prev) => {
        const { [existingChat.id]: removed, ...rest } = prev;
        return rest;
      });
    } else {

      // For medical conversations, if not found, try to reload chats or add it
      if (conversationType === 'medical' && conversationId) {
        // Try to get the conversation from API
        handleChatWithReceiver(conversationId, 'medical').catch(err => {

        });
        return;
      }

      if (conversationId) {
        fetchAndAddConversationForNotification(conversationId, messageData, conversationType);
      }
    }
  };

  const fetchAndAddConversationForNotification = async (conversationId, messageData = {}, conversationType) => {
    if (!conversationId || loadingConversations.has(conversationId)) {
      return;
    }

    try {
      setLoadingConversations(prev => {
        const newSet = new Set(prev);
        newSet.add(conversationId);
        return newSet;
      });

      const response = await conversationsAPI.getConversation(conversationId);
      const conversation = response?.data?.data || response?.data;

      if (!conversation) {

        return;
      }

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const currentUserId = userData.user_id || userData.id;
      const otherParticipant = conversation.participants?.find(participant =>
        participant.id?.toString() !== currentUserId?.toString()
      );

      if (!otherParticipant) {

        return;
      }

      const latestMessageContent = messageData.message || messageData.content || conversation.latest_message?.content;
      const latestMessageTimestamp = messageData.timestamp || messageData.created_at || conversation.latest_message?.created_at || new Date().toISOString();

      const transformedChat = transformChatData({
        ...conversation,
        other_participant: otherParticipant,
        latest_message: {
          content: latestMessageContent,
          created_at: latestMessageTimestamp
        },
        // Preserve case_id and case_type for SOS conversations
        case_id: conversation.case_id,
        case_type: conversation.case_type,
        caseId: conversation.case_id,
        caseType: conversation.case_type,
      });

      if (!transformedChat) {
        return;
      }

      const chatWithNewMessage = {
        ...transformedChat,
        type: conversationType || transformedChat.type,
        lastMessage: latestMessageContent || transformedChat.lastMessage,
        timestamp: formatTimestamp(new Date()),
        unreadCount: selectedChat && selectedChat.toString() === conversationId.toString() ? 0 : 1
      };

      setRecentlyUpdatedChats(prev => {
        const newSet = new Set(prev);
        newSet.add(conversationId);
        return newSet;
      });

      setTimeout(() => {
        setRecentlyUpdatedChats(prev => {
          const newSet = new Set(prev);
          newSet.delete(conversationId);
          return newSet;
        });
      }, 500);

      setChats(prev => {
        const exists = prev.find(chat => chat.id === conversationId);
        if (exists) {
          const otherChats = prev.filter(chat => chat.id !== conversationId);
          return [chatWithNewMessage, ...otherChats];
        }
        return [chatWithNewMessage, ...prev];
      });

      setChatUpdates(prev => {
        const { [conversationId]: removed, ...rest } = prev;
        return rest;
      });

    } catch (error) {

    } finally {
      setLoadingConversations(prev => {
        const newSet = new Set(prev);
        newSet.delete(conversationId);
        return newSet;
      });
    }
  };

  // Subscribe to WebSocket channels for all conversations
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // Subscribe to all conversation channels from current chats
    const channelSubscriptions = [];

    memoizedChats.forEach((chat) => {


      const channelName = `conversation.${chat.id}`;

      try {
        // Subscribe to the channel
        const channel = subscribe(channelName, null);

        if (channel) {
          // Listen for message.sent events
          listen(channelName, 'message.sent', (data) => {

            // Helper function to detect if a media item is audio
            const isAudioMediaItem = (item) => {
              // Check explicit flags
              if (item.type === 'audio' || item.is_audio || item.isAudio) {
                return true;
              }

              // Check mime type
              const mimeType = item.mime_type || item.type;
              if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
                return true;
              }

              // Check URL extension for common audio formats
              const url = item.url || '';
              if (typeof url === 'string') {
                const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
                const lowerUrl = url.toLowerCase();
                if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
                  return true;
                }
              }

              // Check if message content indicates voice message
              const messageContent = data.content || '';
              if (typeof messageContent === 'string' &&
                (messageContent.toLowerCase().includes('voice') ||
                  messageContent.toLowerCase().includes('audio'))) {
                return true;
              }

              return false;
            };

            // Check if message is audio type
            const isAudioMessage = data.media && data.media.some(isAudioMediaItem);

            // Transform WebSocket event data to match existing message format
            const messageData = {
              id: data.id,
              messageId: data.id,
              content: data.content,
              senderId: data.user_id,
              senderName: `${data.user.first_name} ${data.user.last_name}`.trim(),
              user: data.user,
              timestamp: data.created_at,
              originalTimestamp: data.created_at,
              type: isAudioMessage ? 'audio' : (data.media && data.media.length > 0 ? 'media' : 'text'),
              isOwnMessage: false,
              isRead: false,
              media: data.media ? data.media.map((mediaItem) => {
                const isAudio = isAudioMediaItem(mediaItem);
                const mediaType = mediaItem.mime_type || mediaItem.type;

                return {
                  url: mediaItem.url,
                  name: mediaItem.name,
                  type: mediaType || (isAudio ? 'audio/webm' : null),
                  size: mediaItem.size,
                  isImage: mediaItem.type === 'image' || (mediaType && mediaType.startsWith('image/')),
                  isVideo: mediaItem.type === 'video' || (mediaType && mediaType.startsWith('video/')),
                  isDocument: !isAudio &&
                    !['image', 'video', 'audio'].includes(mediaItem.type) &&
                    !(mediaType && (mediaType.startsWith('image/') || mediaType.startsWith('video/') || mediaType.startsWith('audio/'))),
                  isAudio: isAudio,
                };
              }) : null,
            };

            // Prevent duplicate processing
            const messageId = messageData.id;
            if (messageId && processedMessages.current.has(messageId)) {
              return;
            }

            // Mark message as processed
            if (messageId) {
              processedMessages.current.add(messageId);
              if (processedMessages.current.size > 100) {
                const firstMessage = processedMessages.current.values().next().value;
                processedMessages.current.delete(firstMessage);
              }
            }

            // Stop typing indicator for sender when message is received via WebSocket
            if (messageData.senderId && chat.id) {
              stopTypingForUser(chat.id, messageData.senderId);
            }

            // If this is the currently selected chat, add message to conversation
            if (selectedChat && selectedChat.toString() === chat.id.toString()) {
              addNewMessageToConversation(selectedChat, messageData);
            } else {
              // Otherwise, update chat list
              updateChatListWithNewMessage(messageData);
            }
          });

          // Handle message deletion
          listen(channelName, 'message.deleted', (data) => {
            const messageId = data.id || data.messageId || data.message_id;
            const conversationId = data.conversation_id || data.conversationId;

            if (messageId) {
              // Bypass processedMessages check for deletions
              const chatId = String(conversationId || selectedChatRef.current);

              // 1. Set isDeleting: true for animation
              setMessages(prev => {
                const currentMessages = prev[chatId] || [];
                return {
                  ...prev,
                  [chatId]: currentMessages.map(msg =>
                    (String(msg.id) === String(messageId) ||
                      String(msg.messageId) === String(messageId) ||
                      String(msg.message_id) === String(messageId))
                      ? { ...msg, isDeleting: true }
                      : msg
                  )
                };
              });

              // 2. Remove message after animation completes (400ms)
              setTimeout(() => {
                setMessages(prev => {
                  const currentMessages = prev[chatId] || [];
                  const updatedMessages = currentMessages.filter(msg =>
                    String(msg.id) !== String(messageId) &&
                    String(msg.messageId) !== String(messageId) &&
                    String(msg.message_id) !== String(messageId)
                  );
                  return { ...prev, [chatId]: updatedMessages };
                });
              }, 400);

              // 3. Also update last message in chat list if needed
              setChats(prev => prev.map(c => {
                if (String(c.id) === String(conversationId) &&
                  (String(c.lastMessage?.id) === String(messageId) ||
                    String(c.lastMessage?.messageId) === String(messageId) ||
                    String(c.lastMessage?.message_id) === String(messageId))) {
                  return {
                    ...c,
                    lastMessage: { ...c.lastMessage, is_deleted: true, content: t('chat.messages.deleted') || "Message deleted" }
                  };
                }
                return c;
              }));
            }
          });


          channelSubscriptions.push(channelName);
        }
      } catch (error) {

      }
    });

    // Cleanup: unsubscribe from all channels
    return () => {
      channelSubscriptions.forEach((channelName) => {
        unsubscribe(channelName);
      });
    };
  }, [isConnected, memoizedChats, selectedChat, subscribe, unsubscribe, listen, stopTypingForUser]);

  // Legacy event listeners for backward compatibility (keep for now)
  useEffect(() => {
    // Handle medical conversation notification
    // This handler processes medical conversation notifications in the format:
    // {
    //   "type": "message_received",
    //   "conversation_id": 33,
    //   "conversation_type": "medical",
    //   "content": "message content",
    //   "sender_id": 1,
    //   "sender_name": "Sulhafa VET",
    //   "sender_image": "https://...",
    //   "media": [],
    //   "action_url": "/chat/33",
    //   ...
    // }
    const handleMedicalConversationNotification = async (notification) => {
      // Check if this is a medical conversation
      const conversationType = notification.conversation_type || notification.conversationType;
      if (conversationType && conversationType !== 'medical') {
        return;
      }

      try {

        // Skip typing notifications - they should not be added as messages
        if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
          return;
        }

        // Mark that we're processing a notification to prevent auto-selection
        isProcessingNotificationRef.current = true;

        // Handle unified message_received format with conversation_type: 'medical'
        // Supports both unified format (type: 'message_received', conversation_type: 'medical') 
        // and legacy format for backward compatibility

        // Extract conversation ID from notification
        const conversationId = notification.conversation_id ||
          notification.target_id ||
          (notification.action_url ? parseInt(notification.action_url.split('/chat/')[1]?.split('/')[0]) : null);

        if (!conversationId) {

          isProcessingNotificationRef.current = false;
          return;
        }

        // Extract message ID early to check for duplicates
        // Check for duplicate BEFORE processing - extract message ID early
        const messageId = notification.id || notification.messageId || notification.message_id;
        const normalizedMessageId = messageId ? String(messageId) : null;

        // Only check for duplicates for new messages, NOT for deletions
        if (notification.type !== 'message_deleted' &&
          normalizedMessageId &&
          processedMessages.current.has(normalizedMessageId)) {
          isProcessingNotificationRef.current = false;
          return;
        }

        // Check if this is a sender notification (message sent by current user)
        // Unified format uses is_sender_notification flag
        const isSenderNotification = notification.is_sender_notification === true;

        // Get current user information
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const currentUserId = userData.user_id || userData.id;

        // Build sender object
        const sender = {
          id: notification.sender_id,
          name: notification.sender_name || 'Medical Professional',
          image: notification.sender_image || null
        };

        // Transform media array if present
        const formattedMedia = notification.media && notification.media.length > 0
          ? notification.media.map(mediaItem => ({
            url: mediaItem.url,
            name: mediaItem.name || mediaItem.file_name,
            type: mediaItem.type || mediaItem.mime_type,
            mime_type: mediaItem.mime_type || mediaItem.type,
            size: mediaItem.size || mediaItem.file_size,
            file_size: mediaItem.file_size || mediaItem.size,
            file_name: mediaItem.file_name || mediaItem.name,
            is_image: mediaItem.is_image || mediaItem.isImage,
            is_video: mediaItem.is_video || mediaItem.isVideo,
            is_document: mediaItem.is_document || mediaItem.isDocument,
            is_audio: mediaItem.is_audio || mediaItem.isAudio,
            isImage: mediaItem.is_image || mediaItem.isImage,
            isVideo: mediaItem.is_video || mediaItem.isVideo,
            isDocument: mediaItem.is_document || mediaItem.isDocument,
            isAudio: mediaItem.is_audio || mediaItem.isAudio
          }))
          : [];

        // Determine if message is from current user
        const isOwnMessage = isSenderNotification ||
          (currentUserId && sender.id && String(currentUserId) === String(sender.id));

        // Create message data in expected format
        const messageData = {
          id: normalizedMessageId || notification.id || `medical-${Date.now()}`,
          messageId: notification.id,
          content: notification.content || '',
          message: notification.content || '',
          media: formattedMedia,
          senderId: sender.id,
          sender: sender,
          timestamp: notification.created_at || new Date().toISOString(),
          isOwn: isOwnMessage,
          isOwnMessage: isOwnMessage,
          hasMedia: formattedMedia.length > 0,
          mediaCount: formattedMedia.length,
          conversationType: 'medical',
          conversationId: conversationId
        };

        // Check if conversation exists and update it (always update, even if not selected)

        // Check if conversation is selected - use both selectedChat state and URL path for reliability
        const currentPath = window.location.pathname;
        const currentSelectedChat = selectedChatRef.current;
        const isCurrentlySelected = currentSelectedChat && conversationId &&
          String(currentSelectedChat) === String(conversationId);

        // If this is the currently selected chat, check if we need to update it
        let chatWasFound = false;
        setChats(prev => {
          const chatIndex = prev.findIndex(chat => chat.id === conversationId);
          if (chatIndex !== -1) {
            chatWasFound = true;
            const existingChat = prev[chatIndex];
            const updatedChat = {
              ...existingChat,
              lastMessage: notification.content || notification.message || existingChat.lastMessage,
              lastMessageTimestamp: notification.created_at || existingChat.lastMessageTimestamp,
              // Only increment unread count if this is NOT the currently selected chat
              unreadCount: isCurrentlySelected ? 0 : ((existingChat.unreadCount || 0) + 1)
            };

            // Add to recently updated chats set for animation
            setRecentlyUpdatedChats(prev => {
              const newSet = new Set(prev);
              newSet.add(conversationId);
              return newSet;
            });

            // Remove from recently updated after animation completes
            setTimeout(() => {
              setRecentlyUpdatedChats(prev => {
                const newSet = new Set(prev);
                newSet.delete(conversationId);
                return newSet;
              });
            }, 500); // Match animation duration

            // Move to top
            const otherChats = prev.filter(chat => chat.id !== conversationId);
            const newChatsList = [updatedChat, ...otherChats];
            return newChatsList;
          } else {
            // Don't modify state if not found - will be handled below
            return prev;
          }
        });

        // Clear any pending updates since we updated directly
        setChatUpdates(prev => {
          const { [conversationId]: removed, ...rest } = prev;
          return rest;
        });

        // Mark message as processed AFTER creating messageData but BEFORE adding to conversation
        // This prevents duplicates while still allowing the message to be added
        if (normalizedMessageId) {
          processedMessages.current.add(normalizedMessageId);
          // Clean up old processed messages to prevent memory leaks
          if (processedMessages.current.size > 100) {
            const firstMessage = processedMessages.current.values().next().value;
            processedMessages.current.delete(firstMessage);
          }
        }

        // If chat was found and updated, we're done with the list update
        if (chatWasFound) {
          // If this is the currently selected chat, also add message to conversation
          if (isCurrentlySelected) {

            // Pass skipProcessedCheck=true because message is already marked as processed above
            addNewMessageToConversation(conversationId, messageData, true);
          } else {
            // If not selected, just update the chat list (already done above)
          }
          // Reset notification processing flag
          isProcessingNotificationRef.current = false;
          return; // Exit early since we've updated the list
        }

        // If chat was not found, just update chat list with new message data
        // Don't call conversationsAPI.getConversation() API - update state directly from notification

        // Update chat list directly with message data from notification
        updateChatListWithNewMessage(messageData);

        // If conversation is currently selected, also add message to conversation
        if (isCurrentlySelected) {
          // Pass skipProcessedCheck=true because message is already marked as processed above
          addNewMessageToConversation(conversationId, messageData, true);
        }

        // Reset notification processing flag
        isProcessingNotificationRef.current = false;

        // Reset notification processing flag after a delay to allow state updates to complete
        setTimeout(() => {
          isProcessingNotificationRef.current = false;
        }, 1000);

        // DO NOT auto-open conversations when receiving notifications
        // Only update the chat list - let the user manually open the conversation if they want
        // This prevents opening incorrect conversations when multiple notifications arrive

      } catch (error) {

        // Reset flag on error
        isProcessingNotificationRef.current = false;
      }
    };

    const handleNewMessageReceived = (event) => {
      const messageData = event.detail;


      // Skip typing notifications - they should not be added as messages
      if (messageData.type === 'user_typing' || messageData.type === 'user_stop_typing') {
        return;
      }

      // Handle message deletion flags early
      if (messageData.is_deleted === true || messageData.type === 'message_deleted') {
        // Redirect to deletion handler logic (or let useNotifications handle it and trigger sync event)
        return;
      }

      // Handle unified message_received notifications
      // Check if this is a medical conversation using conversation_type field
      const conversationType = messageData.conversation_type || messageData.conversationType;

      if (conversationType === 'medical') {

        // Transform the event data to unified notification format
        const notification = {
          type: 'message_received',
          conversation_id: messageData.conversationId || messageData.conversation_id || messageData.senderId,
          conversation_type: 'medical',
          content: messageData.message || messageData.content,
          sender_id: messageData.senderId || messageData.sender?.id || messageData.sender_id,
          sender_name: messageData.senderName || messageData.sender?.name || messageData.sender_name,
          sender_image: messageData.sender?.image || messageData.sender_image,
          media: messageData.media || [],
          action_url: `/chat/${messageData.conversationId || messageData.conversation_id || messageData.senderId}`,
          id: messageData.id || messageData.messageId,
          created_at: messageData.timestamp || messageData.created_at || new Date().toISOString(),
          is_sender_notification: messageData.isOwn || messageData.isOwnMessage || messageData.is_sender_notification || false
        };
        handleMedicalConversationNotification(notification);
        return;
      }

      // NOTE: Duplicate checking is handled inside addNewMessageToConversation
      // Don't check processedMessages here or messages will be rejected before being added

      // Extract conversation ID from unified format
      const incomingConversationId = messageData.conversationId || messageData.conversation_id;
      const senderId = messageData.senderId || messageData.sender_id || messageData.sender?.id;

      // Transform notification data to match expected format
      const transformedMessageData = {
        ...messageData,
        content: messageData.message || messageData.content,
        conversationId: incomingConversationId,
        conversation_id: incomingConversationId,
        // Ensure media array is properly formatted
        media: messageData.media ? messageData.media.map(mediaItem => ({
          ...mediaItem,
          url: mediaItem.url,
          name: mediaItem.name || mediaItem.file_name,
          type: mediaItem.type || mediaItem.mime_type,
          mime_type: mediaItem.mime_type || mediaItem.type,
          size: mediaItem.size || mediaItem.file_size,
          file_size: mediaItem.file_size || mediaItem.size,
          file_name: mediaItem.file_name || mediaItem.name,
          is_image: mediaItem.is_image || mediaItem.isImage,
          is_video: mediaItem.is_video || mediaItem.isVideo,
          is_document: mediaItem.is_document || mediaItem.isDocument,
          is_audio: mediaItem.is_audio || mediaItem.isAudio,
          // Keep original properties for compatibility
          isImage: mediaItem.is_image || mediaItem.isImage,
          isVideo: mediaItem.is_video || mediaItem.isVideo,
          isDocument: mediaItem.is_document || mediaItem.isDocument,
          isAudio: mediaItem.is_audio || mediaItem.isAudio
        })) : null
      };

      // Determine if the message belongs to the currently selected chat
      // Use selectedChatRef to avoid stale closure
      const currentSelectedChat = selectedChatRef.current;
      let isForSelectedChat = false;
      if (currentSelectedChat) {
        if (incomingConversationId && currentSelectedChat.toString() === incomingConversationId.toString()) {
          isForSelectedChat = true;
        }
      }

      // If the message is for the open chat, update it
      if (isForSelectedChat) {

        // Stop typing indicator for sender when message is received
        if (senderId) {
          stopTypingForUser(currentSelectedChat, senderId);
        }
        // Add the new message directly to the conversation instead of reloading
        addNewMessageToConversation(currentSelectedChat, transformedMessageData);
        // Also update chat list to move conversation to top
        updateChatListWithNewMessage(transformedMessageData);
      } else {

        // Not for open chat, just update the chat list (sidebar)
        updateChatListWithNewMessage(transformedMessageData);
      }
    };

    // Handle message sent sync events (for syncing conversations across devices)
    const handleMessageSentSync = (event) => {
      const syncData = event.detail;

      // Only process if this is a sender notification (message sent by current user)
      // Check unified format: type === 'message_received' and is_sender_notification === true
      const isSenderNotification = syncData.isSenderNotification === true ||
        syncData.is_sender_notification === true ||
        (syncData.type === 'message_received' && syncData.is_sender_notification === true);

      if (!isSenderNotification) {
        return;
      }

      // Prevent duplicate processing by checking if this message was already processed
      const syncMessageId = syncData.messageId || syncData.id;
      if (syncMessageId && processedMessages.current.has(syncMessageId)) {
        return;
      }

      // Mark message as processed
      if (syncMessageId) {
        processedMessages.current.add(syncMessageId);
        // Clean up old processed messages to prevent memory leaks
        if (processedMessages.current.size > 100) {
          const firstMessage = processedMessages.current.values().next().value;
          processedMessages.current.delete(firstMessage);
        }
      }

      // Extract data from unified format
      const conversationId = syncData.conversationId || syncData.conversation_id;
      const message = syncData.message || syncData.content || '';
      const content = syncData.content || syncData.message || '';
      const media = syncData.media || [];
      const messageContent = message || content || '';

      // Also check conversation_type for proper handling
      const conversationType = syncData.conversation_type || syncData.conversationType || 'social';

      // Get current user information
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const currentUserId = userData.user_id || userData.id;
      const currentUserName = userData.first_name && userData.last_name
        ? `${userData.first_name} ${userData.last_name}`.trim()
        : userData.first_name || userData.name || 'You';
      const currentUserImage = (userData.profile_image_url &&
        userData.profile_image_url !== '' &&
        userData.profile_image_url !== 'null' &&
        userData.profile_image_url !== 'undefined')
        ? userData.profile_image_url
        : '/icons/person.png';

      // Find the chat that matches this conversation
      const targetChat = memoizedChats.find(chat => chat.id === conversationId);

      if (targetChat) {

        // Format media properly
        const formattedMedia = media && media.length > 0 ? media.map(mediaItem => ({
          url: mediaItem.url,
          name: mediaItem.name || mediaItem.file_name,
          type: mediaItem.type || mediaItem.mime_type,
          size: mediaItem.size || mediaItem.file_size,
          isImage: mediaItem.is_image || mediaItem.isImage,
          isVideo: mediaItem.is_video || mediaItem.isVideo,
          isDocument: mediaItem.is_document || mediaItem.isDocument,
          caption: mediaItem.caption || null
        })) : [];

        // Create message data in the expected format - THIS IS A MESSAGE SENT BY CURRENT USER
        const messageData = {
          id: syncMessageId || `sync-${Date.now()}`,
          messageId: syncMessageId,
          content: messageContent,
          message: messageContent,
          media: formattedMedia,
          senderId: currentUserId, // Current user is the sender
          sender: {
            id: currentUserId,
            name: currentUserName,
            image: currentUserImage
          },
          timestamp: syncData.timestamp || new Date().toISOString(),
          isOwn: true, // This is a message sent by the current user
          isOwnMessage: true, // Important: mark as own message
          hasMedia: formattedMedia.length > 0,
          mediaCount: formattedMedia.length,
          conversationId: conversationId,
        };

        // Add the message to the conversation
        addNewMessageToConversation(conversationId, messageData);

        // Update the chat list to show the new message
        updateChatListWithNewMessage(messageData);
      } else {
        // For medical conversations, don't call loadChats() API - update state directly from notification
        if (conversationType === 'medical') {
          updateChatListWithNewMessage(messageData);
        } else {
          // For social conversations, reload the chat list
          loadChats();
        }
      }
    };

    // Handle chat message refresh events (when actively viewing a chat)
    const handleRefreshChatMessages = (event) => {
      const refreshData = event.detail;

      // Check if this is a social conversation (not medical)
      const conversationType = refreshData.conversationType || refreshData.conversation_type;
      if (conversationType === 'medical') {
        return;
      }

      const { conversationId, messageId, message, content, media, isSenderNotification } = refreshData;
      const messageContent = message || content || '';

      // Only refresh if this is the currently selected chat
      // Use selectedChatRef to avoid stale closure
      const currentSelectedChat = selectedChatRef.current;
      if (currentSelectedChat && currentSelectedChat.toString() === conversationId.toString()) {

        // Get current user information if this is a sender notification
        let senderInfo;
        if (isSenderNotification) {
          const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
          const currentUserId = userData.user_id || userData.id;
          const currentUserName = userData.first_name && userData.last_name
            ? `${userData.first_name} ${userData.last_name}`.trim()
            : userData.first_name || userData.name || 'You';
          const currentUserImage = (userData.profile_image_url &&
            userData.profile_image_url !== '' &&
            userData.profile_image_url !== 'null' &&
            userData.profile_image_url !== 'undefined')
            ? userData.profile_image_url
            : '/icons/person.png';

          senderInfo = {
            id: currentUserId,
            name: currentUserName,
            image: currentUserImage
          };
        } else {
          // For received messages, use other participant info
          const targetChat = memoizedChats.find(chat => chat.id === conversationId);
          senderInfo = {
            id: targetChat?.otherParticipant?.id,
            name: targetChat?.otherParticipant?.name || 'Someone',
            image: targetChat?.otherParticipant?.image || null
          };
        }

        // Format media properly
        const formattedMedia = media && media.length > 0 ? media.map(mediaItem => ({
          url: mediaItem.url,
          name: mediaItem.name || mediaItem.file_name,
          type: mediaItem.type || mediaItem.mime_type,
          size: mediaItem.size || mediaItem.file_size,
          isImage: mediaItem.is_image || mediaItem.isImage,
          isVideo: mediaItem.is_video || mediaItem.isVideo,
          isDocument: mediaItem.is_document || mediaItem.isDocument,
          caption: mediaItem.caption || null
        })) : [];

        // Create message data
        const messageData = {
          id: messageId || `refresh-${Date.now()}`,
          messageId: messageId,
          content: messageContent,
          message: messageContent,
          media: formattedMedia,
          senderId: senderInfo.id,
          sender: senderInfo,
          timestamp: new Date().toISOString(),
          isOwn: isSenderNotification || false,
          isOwnMessage: isSenderNotification || false
        };

        // Add the message to the current conversation
        addNewMessageToConversation(conversationId, messageData);
      }
    };

    // Handle conversation closed event
    const handleConversationClosed = (event) => {
      const { conversationId, message, closedBy, timestamp } = event.detail;

      // IMMEDIATELY update the chat to mark it as closed (optimistic update)
      setChatUpdates(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          isClosed: true,
          closedAt: timestamp,
          closedBy: closedBy,
          lastMessage: message || 'Conversation closed'
        }
      }));


      // Optional: You can also update the main chats state for immediate UI feedback
      setChats(prev => prev.map(chat =>
        chat.id === conversationId
          ? {
            ...chat,
            isClosed: true,
            closedAt: timestamp,
            closedBy: closedBy,
            lastMessage: message || 'Conversation closed'
          }
          : chat
      ));


      // Note: Conversation closure is typically a one-way action, so we don't need
      // to implement rollback logic. The UI will show the conversation as closed
      // immediately, providing instant feedback to the user.
    };

    // Handle update-chat-list-direct event to update chat list directly without API call
    const handleUpdateChatListDirect = (event) => {
      const updateData = event.detail;

      // Check if this is a social conversation (not medical)
      const conversationType = updateData.conversationType || updateData.conversation_type;
      if (conversationType === 'medical') {
        return;
      }

      // Skip if this update is redundant (will be handled by new-message-received)
      // This prevents double unread count increment
      if (updateData.isRedundant) {
        return;
      }

      // Transform notification data to format expected by updateChatListWithNewMessage
      const messageData = {
        senderId: updateData.senderId || updateData.sender?.id,
        conversationId: updateData.conversationId || updateData.conversation_id,
        conversation_id: updateData.conversationId || updateData.conversation_id,
        conversationType: updateData.conversationType || updateData.conversation_type,
        conversation_type: updateData.conversationType || updateData.conversation_type,
        message: updateData.message || updateData.content,
        content: updateData.message || updateData.content,
        media: updateData.media || [],
        unreadCount: updateData.unreadCount // Pass unreadCount if available
      };

      // Skip if no content and no media and not an explicit unread count update
      // This prevents "ghost" empty messages from read receipts
      const hasContent = messageData.content && typeof messageData.content === 'string' && messageData.content.trim().length > 0;
      const hasMedia = messageData.media && messageData.media.length > 0;
      const isUnreadCountUpdate = typeof messageData.unreadCount !== 'undefined';

      if (!hasContent && !hasMedia && !isUnreadCountUpdate) {
        return;
      }

      // If it's just an unread count update (like from read receipt), strictly update the list/unread count
      // DO NOT add a new message to conversation
      if (isUnreadCountUpdate && !hasContent && !hasMedia) {
        if (messageData.conversationId) {
          const chatIdStr = String(messageData.conversationId);

          setChatUpdates(prev => ({
            ...prev,
            [messageData.conversationId]: {
              unreadCount: messageData.unreadCount
            }
          }));

          // ALSO trigger read status update here as a fallback/redundancy
          if (messageData.unreadCount === 0) {
            setMessages(prev => {
              const conversationMessages = prev[chatIdStr] || prev[Number(chatIdStr)];
              if (!conversationMessages || conversationMessages.length === 0) {
                return prev;
              }

              return {
                ...prev,
                [chatIdStr]: conversationMessages.map(msg => {
                  if (msg.isOwnMessage && !msg.isRead) {
                    return { ...msg, isRead: true };
                  }
                  return msg;
                })
              };
            });
          }
        }
        return;
      }

      // Update chat list directly using existing function
      updateChatListWithNewMessage(messageData);

      // Robustness check: If this message belongs to the currently open chat, 
      // but new-message-received wasn't fired (or was missed), update the main window too.
      let isForSelectedChat = false;
      const incomingConversationId = messageData.conversationId;
      const incomingSenderId = messageData.senderId;

      // Use selectedChatRef to avoid stale closure
      const currentSelectedChat = selectedChatRef.current;
      if (currentSelectedChat) {
        // Match by conversation ID
        if (incomingConversationId && currentSelectedChat.toString() === incomingConversationId.toString()) {
          isForSelectedChat = true;
        }
        // Match by participant ID if conversation ID is missing
        else if (incomingSenderId) {
          const openChatObj = memoizedChats.find(chat => chat.id && chat.id.toString() === currentSelectedChat.toString());
          const openChatParticipantId = openChatObj?.otherParticipant?.id || openChatObj?.senderId;

          if (openChatParticipantId && openChatParticipantId.toString() === incomingSenderId.toString()) {
            isForSelectedChat = true;
          }
        }
      }

      if (isForSelectedChat) {
        // Use processedMessages ref to prevent redundant processing if new-message-received also fired
        const messageId = updateData.id || updateData.messageId;
        if (messageId && !processedMessages.current.has(messageId)) {
          processedMessages.current.add(messageId);
          addNewMessageToConversation(currentSelectedChat, messageData);
        } else if (!messageId) {
          // Only add if it looks like a real message (has content or media)
          if (hasContent || hasMedia) {
            addNewMessageToConversation(currentSelectedChat, messageData);
          }
        }
      }
    };

    // Handle message deletion sync from notifications
    const handleMessageDeletedSync = (event) => {
      const { messageId, message_id, id, conversationId, conversationType } = event.detail;
      const actualMessageId = messageId || message_id || id;
      // Bypass processedMessages check for deletions
      // (a deletion is NOT a duplicate of an arrival)

      // Process both social and medical deletions with the same animation logic

      if (actualMessageId && conversationId) {
        console.log('🗑️ [ChatPage] Syncing deletion for:', { actualMessageId, conversationId, conversationType });
        const chatId = String(conversationId);
        // 1. Set isDeleting: true for animation
        setMessages(prev => {
          const currentMessages = prev[chatId] || [];
          console.log(`🗑️ [ChatPage] Current messages count for ${chatId}:`, currentMessages.length);
          console.log(`🗑️ [ChatPage] Available message keys in state:`, Object.keys(prev));

          const updatedBucket = currentMessages.map(msg => {
            const matches = (String(msg.id) === String(actualMessageId) ||
              String(msg.messageId) === String(actualMessageId) ||
              String(msg.message_id) === String(actualMessageId));

            if (matches) console.log('🗑️ [ChatPage] Found message to animate:', msg.id);
            return matches ? { ...msg, isDeleting: true } : msg;
          });

          return {
            ...prev,
            [chatId]: updatedBucket
          };
        });

        // 2. Remove message after animation completes (400ms)
        setTimeout(() => {
          setMessages(prev => {
            const currentMessages = prev[chatId] || [];
            const updatedMessages = currentMessages.filter(msg =>
              String(msg.id) !== String(actualMessageId) &&
              String(msg.messageId) !== String(actualMessageId) &&
              String(msg.message_id) !== String(actualMessageId)
            );
            return { ...prev, [chatId]: updatedMessages };
          });
        }, 400);

        // 3. Also update last message in chat list if needed
        setChats(prev => prev.map(c => {
          if (String(c.id) === chatId) {
            // Check if lastMessage is the one being deleted
            // Handle both string and object cases for lastMessage
            const lastMsgId = typeof c.lastMessage === 'object' ? (c.lastMessage?.id || c.lastMessage?.messageId) : null;
            const isLastMsgMatch = lastMsgId && (String(lastMsgId) === String(actualMessageId));

            // For social chats, lastMessage is often just a string. 
            // We update it regardless if it's the current chat and we just deleted something.
            return {
              ...c,
              lastMessage: t('chat.messages.deleted') || "Message deleted"
            };
          }
          return c;
        }));
      }
    };



    // Add event listeners
    window.addEventListener('new-message-received', handleNewMessageReceived);
    window.addEventListener('message-sent-sync', handleMessageSentSync);
    window.addEventListener('refresh-chat-messages', handleRefreshChatMessages);
    window.addEventListener('message-deleted-sync', handleMessageDeletedSync);
    window.addEventListener('conversationClosed', handleConversationClosed);
    window.addEventListener('update-chat-list-direct', handleUpdateChatListDirect);


    // Listen for medical conversation notifications
    // This can be triggered by the notification system or directly
    const handleMedicalNotificationEvent = (event) => {
      handleMedicalConversationNotification(event.detail || event);
    };

    window.addEventListener('medical-conversation-notification', handleMedicalNotificationEvent);

    // Also listen for the notification format from useNotifications hook
    const handleNotificationEvent = (event) => {
      const notification = event.detail || event;

      // Skip typing notifications - they should not be added as messages
      if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
        return;
      }

      // ✅ UNIFIED: Handle message_deleted for BOTH social AND medical via notification-received
      if (notification.type === 'message_deleted') {
        const messageId = notification.message_id || notification.messageId || notification.id;
        const conversationId = notification.conversation_id || notification.conversationId || notification.target_id;

        console.log('🗑️ [ChatPage handleNotificationEvent] message_deleted received:', { messageId, conversationId });

        if (messageId && conversationId) {
          const chatId = String(conversationId);

          // 1. Set isDeleting: true for animation
          setMessages(prev => {
            const currentMessages = prev[chatId] || [];
            console.log(`🗑️ [ChatPage handleNotifEvent] Keys in state:`, Object.keys(prev), `| chatId:`, chatId, `| count:`, currentMessages.length);
            return {
              ...prev,
              [chatId]: currentMessages.map(msg => {
                const matches = (String(msg.id) === String(messageId) ||
                  String(msg.messageId) === String(messageId) ||
                  String(msg.message_id) === String(messageId));
                if (matches) console.log('🗑️ [ChatPage handleNotifEvent] Matched msg to delete:', msg.id);
                return matches ? { ...msg, isDeleting: true } : msg;
              })
            };
          });

          // 2. Remove after animation
          setTimeout(() => {
            setMessages(prev => ({
              ...prev,
              [chatId]: (prev[chatId] || []).filter(msg =>
                String(msg.id) !== String(messageId) &&
                String(msg.messageId) !== String(messageId) &&
                String(msg.message_id) !== String(messageId)
              )
            }));
          }, 400);
        }
        return; // handled, no need to process further
      }

      if ((notification.type === 'message_received' && notification.conversation_type === 'medical') ||
        notification.conversation_type === 'medical') {
        handleMedicalConversationNotification(notification);
      }
    };


    window.addEventListener('notification-received', handleNotificationEvent);

    // Also listen for raw notification objects that might come directly from Echo/backend
    // This handles notifications that haven't been processed by useNotifications yet
    const handleRawNotification = (event) => {
      const notification = event.detail || event;

      // Skip typing notifications - they should not be added as messages
      if (notification.type === 'user_typing' || notification.type === 'user_stop_typing') {
        return;
      }

      // Handle read receipts - when other user reads our messages
      if (notification.type === 'messages_read') {
        const conversationId = notification.conversation_id || notification.data?.conversation_id;

        if (conversationId) {
          // Update messages state to mark own messages as read
          setMessages(prev => {
            const chatMessages = prev[conversationId];
            if (!chatMessages) return prev;

            // Check if we actually need to update anything to avoid unnecessary re-renders
            const hasUnreadOwnMessages = chatMessages.some(msg => msg.isOwnMessage && !msg.isRead);

            if (!hasUnreadOwnMessages) return prev;

            return {
              ...prev,
              [conversationId]: chatMessages.map(msg => {
                if (msg.isOwnMessage && !msg.isRead) {
                  return { ...msg, isRead: true };
                }
                return msg;
              })
            };
          });
        }
        return;
      }

      // Check if it's a medical conversation notification
      if ((notification.type === 'message_received' && notification.conversation_type === 'medical') ||
        (notification.conversation_type === 'medical' && notification.conversation_id)) {
        handleMedicalConversationNotification(notification);
      }
    };

    // Handle typing notifications from notification system
    const handleTypingNotification = (event) => {
      const notification = event.detail || event;

      // Check if it's a typing notification
      if (notification.type !== 'user_typing') {
        return;
      }

      // Extract notification data - handle both nested and flat structures
      let notificationData;
      if (typeof notification.data === 'string') {
        try {
          notificationData = JSON.parse(notification.data);
        } catch (e) {
          notificationData = notification;
        }
      } else if (notification.data && typeof notification.data === 'object') {
        notificationData = notification.data;
      } else {
        notificationData = notification;
      }

      const conversationType = notificationData.conversation_type || notificationData.conversationType ||
        notification.conversation_type || notification.conversationType;

      if (conversationType === 'medical') {
        return;
      }

      if (!conversationId) {

        return;
      }

      if (!senderId) {

        return;
      }

      const convIdStr = conversationId.toString();
      const timeoutKey = `${convIdStr}_${senderId}`;


      // Clear existing timeout for this user in this conversation
      if (typingTimeoutsRef.current[timeoutKey]) {
        clearTimeout(typingTimeoutsRef.current[timeoutKey]);
        delete typingTimeoutsRef.current[timeoutKey];
      }

      if (isTyping) {
        // Add user to typing set for this conversation
        setConversationTypingUsers(prev => {
          const updated = { ...prev };
          if (!updated[convIdStr]) {
            updated[convIdStr] = new Set();
          }
          updated[convIdStr] = new Set(updated[convIdStr]);
          updated[convIdStr].add(senderId);
          return updated;
        });

        // Auto-remove after 5 seconds if no stopped typing notification
        typingTimeoutsRef.current[timeoutKey] = setTimeout(() => {
          stopTypingForUser(conversationId, senderId);
        }, 5000);
      } else {
        // Remove user from typing set for this conversation
        stopTypingForUser(conversationId, senderId);
      }
    };

    // Handle user_stop_typing event from notification system
    const handleStopTypingNotification = (event) => {
      const notification = event.detail || event;

      // Extract notification data - handle both nested and flat structures
      let notificationData;
      if (typeof notification.data === 'string') {
        try {
          notificationData = JSON.parse(notification.data);
        } catch (e) {
          notificationData = notification;
        }
      } else if (notification.data && typeof notification.data === 'object') {
        notificationData = notification.data;
      } else {
        notificationData = notification;
      }

      // Try multiple paths to get the values
      const conversationId = notificationData.conversation_id ||
        notification.conversation_id ||
        notificationData.target_id ||
        notification.target_id;
      const senderId = notificationData.sender_id ||
        notification.sender_id ||
        notificationData.user_id ||
        notification.user_id;

      if (!conversationId) {

        return;
      }

      if (!senderId) {

        return;
      }


      // Remove user from typing set for this conversation
      stopTypingForUser(conversationId, senderId);
    };

    // Handle messages-read event (when another user marks messages as read)
    const handleMessagesRead = (event) => {
      const eventData = event.detail;


      const conversationId = eventData.conversationId || eventData.conversation_id;

      if (!conversationId) {

        return;
      }

      const chatIdStr = String(conversationId);

      // Update messages state to mark all messages as read
      setMessages(prev => {
        const conversationMessages = prev[chatIdStr] || prev[Number(chatIdStr)];

        if (!conversationMessages || conversationMessages.length === 0) {

          return prev;
        }



        // Mark sent messages (own messages) as read if receiver allowed read receipts
        // This event is triggered when the receiver marks messages as read
        return {
          ...prev,
          [chatIdStr]: conversationMessages.map(msg => {
            // Only update read status for sent messages (read_at from receiver)
            // For received messages, keep existing read_for_me status
            if (msg.isOwnMessage && !msg.isRead) {

              // When receiver marks as read, read_at is set (if privacy allows)
              // So we mark it as read to show the read receipt
              return { ...msg, isRead: true }
            }
            return msg
          })
        };
      });

      // Update chat list unread count to 0
      setChatUpdates(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          unreadCount: 0
        }
      }));

    };

    // Listen for notifications that might come directly from the notification system
    window.addEventListener('notification', handleRawNotification);

    // Listen for typing notifications
    window.addEventListener('notification-received', handleTypingNotification);
    window.addEventListener('notification', handleTypingNotification);

    // Listen for stop typing notifications
    window.addEventListener('user_stop_typing', handleStopTypingNotification);

    // Listen for messages-read event (when another user marks messages as read)
    window.addEventListener('messages-read', handleMessagesRead);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('new-message-received', handleNewMessageReceived);
      window.removeEventListener('message-sent-sync', handleMessageSentSync);
      window.removeEventListener('refresh-chat-messages', handleRefreshChatMessages);
      window.removeEventListener('message-deleted-sync', handleMessageDeletedSync);
      window.removeEventListener('conversationClosed', handleConversationClosed);
      window.removeEventListener('update-chat-list-direct', handleUpdateChatListDirect);

      window.removeEventListener('medical-conversation-notification', handleMedicalNotificationEvent);
      window.removeEventListener('notification-received', handleNotificationEvent);
      window.removeEventListener('notification', handleRawNotification);
      window.removeEventListener('notification-received', handleTypingNotification);
      window.removeEventListener('notification', handleTypingNotification);
      window.removeEventListener('user_stop_typing', handleStopTypingNotification);
      window.removeEventListener('messages-read', handleMessagesRead);

      // Clear all typing timeouts
      Object.values(typingTimeoutsRef.current).forEach(timeout => clearTimeout(timeout));
      typingTimeoutsRef.current = {};
    };
  }, [selectedChat, stopTypingForUser, memoizedChats, addNewMessageToConversation]); // Added memoizedChats and addNewMessageToConversation to prevent stale closures

  // Load chats on component mount
  const chatsLoadedRef = useRef(false);
  useEffect(() => {
    if (!chatsLoadedRef.current) {
      chatsLoadedRef.current = true;
    }
  }, [])
  useEffect(() => {
    loadChats();
  }, [])
  // Handle conversationType parameter changes and vet tab hiding
  useEffect(() => {


    if (conversationType && ['all', 'medical', 'social'].includes(conversationType)) {
      // If medical tab is hidden for vets and conversationType is medical, default to 'all'
      setActiveTab(conversationType);
    } else if (conversationType) {

      setActiveTab('all');
    }
  }, [conversationType, currentUserType]);


  // Add test function for development
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Expose test function globally for development
      window.testMessageSentSync = () => {
        const testData = {
          conversationId: 38,
          messageId: 573,
          message: "Test sync message",
          media: [],
          recipient: { id: 139 }
        };

        const syncEvent = new CustomEvent('message-sent-sync', {
          detail: testData
        });
        window.dispatchEvent(syncEvent);
      };

      // Test function for media display
      window.testMediaDisplay = () => {
        const testMessageData = {
          id: 999,
          message: "Test message with media",
          media: [
            {
              url: "http://192.168.1.115:8000/media/2025/09/16/test-image.png",
              file_name: "test-image.png",
              mime_type: "image/png",
              file_size: 1016127,
              is_image: true,
              is_video: false,
              is_document: false,
              caption: "Test image caption"
            }
          ],
          sender: {
            id: 137,
            name: "Test User",
            image: "http://192.168.1.115:8000/profile-images/test.png"
          },
          senderId: 137,
          timestamp: new Date().toISOString()
        };

        // Add the test message to the current conversation or first available chat
        if (selectedChat) {
          addNewMessageToConversation(selectedChat, testMessageData);
        } else if (memoizedChats && memoizedChats.length > 0) {
          const firstChat = memoizedChats[0];
          addNewMessageToConversation(firstChat.id, testMessageData);
        } else {
        }
      };

      // Test function to simulate notification event (exact format from notification system)
      window.testNotificationMedia = () => {
        const testNotificationData = {
          message: "Check out this image!",
          sender: {
            id: 137,
            name: "Test User",
            image: "http://192.168.1.115:8000/profile-images/test.png"
          },
          senderId: 137,
          media: [
            {
              url: "http://192.168.1.115:8000/media/2025/09/16/notification-test.png",
              file_name: "notification-test.png",
              mime_type: "image/png",
              file_size: 500000,
              is_image: true,
              is_video: false,
              is_document: false
            }
          ],
          hasMedia: true,
          conversationId: selectedChat || 35,
          messageId: 999,
          timestamp: new Date().toISOString()
        };

        // Simulate the new-message-received event
        const messageEvent = new CustomEvent('new-message-received', {
          detail: testNotificationData
        });
        window.dispatchEvent(messageEvent);
      };

      // Debug function to check current messages
      window.debugMessages = () => {
        if (messages[selectedChat]) {
          messages[selectedChat].forEach((msg, index) => {
            if (msg.media && msg.media.length > 0) {
            }
          });
        }
      };

      // Test function for medical conversation notifications
      window.testMedicalNotification = () => {
        const testNotification = {
          "title": "Message from Sulhafa VET",
          "content": "asd",
          "type": "message_received",
          "priority": "high",
          "action_url": "/chat/33",
          "image": null,
          "sender_id": 1,
          "sender_name": "Sulhafa VET",
          "sender_image": "https://sulhafa-dev.s3.amazonaws.com/200x200.png/00aacc",
          "media": [],
          "is_sender_notification": false,
          "conversation_id": 33,
          "conversation_type": "medical",
          "id": "e948a0ca-5e3f-4859-bf50-b99caf22cc25",
          "created_at": new Date().toISOString()
        };

        // Test all event types
        window.dispatchEvent(new CustomEvent('medical-conversation-notification', { detail: testNotification }));

        window.dispatchEvent(new CustomEvent('notification-received', { detail: testNotification }));

        const transformedEvent = {
          id: testNotification.id,
          messageId: testNotification.id,
          message: testNotification.content,
          content: testNotification.content,
          senderId: testNotification.sender_id,
          sender: {
            id: testNotification.sender_id,
            name: testNotification.sender_name,
            image: testNotification.sender_image
          },
          media: testNotification.media,
          conversationType: 'medical',
          conversationId: testNotification.conversation_id,
          timestamp: testNotification.created_at,
          isOwn: false,
          isOwnMessage: false
        };
        window.dispatchEvent(new CustomEvent('new-message-received', { detail: transformedEvent }));

      };

      // Function to check available chats
      window.checkChats = () => {
        if (memoizedChats && memoizedChats.length > 0) {
          memoizedChats.forEach((chat, index) => {
          });
        } else {
        }
      };

      // Function to select first chat for testing
      window.selectFirstChat = () => {
        if (memoizedChats && memoizedChats.length > 0) {
          const firstChat = memoizedChats[0];
          setSelectedChat(firstChat.id);
        } else {
        }
      };
    }
  }, []);

  // Transform API message response to match expected format
  const transformMessageData = (apiMessage) => {
    // Helper function to detect if a media item is audio
    const isAudioMediaItem = (item) => {
      // Check explicit flags first (most reliable)
      if (item.is_audio || item.isAudio || item.file_type === 'audio') {
        return true;
      }

      // Check mime type
      const mimeType = item.mime_type || item.type;
      if (mimeType && typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
        return true;
      }

      // Check URL extension for common audio formats
      const url = item.url || '';
      if (typeof url === 'string') {
        const audioExtensions = ['.webm', '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
        const lowerUrl = url.toLowerCase();
        if (audioExtensions.some(ext => lowerUrl.includes(ext))) {
          return true;
        }
      }

      return false;
    };

    // Check if message is audio type - prioritize message_type from API
    const isAudio = apiMessage.message_type === 'audio' ||
      apiMessage.is_audio ||
      apiMessage.isAudio ||
      (apiMessage.media && apiMessage.media.some(isAudioMediaItem));

    // Transform media objects to match expected format
    const transformedMedia = apiMessage.media ? apiMessage.media.map(mediaItem => {
      const isAudio = isAudioMediaItem(mediaItem);
      let mimeType = mediaItem.mime_type || mediaItem.type;
      const fileType = mediaItem.file_type || mediaItem.type;

      // If it's audio but mime type is video/webm (common for webm audio), convert to audio/webm
      if (isAudio && mimeType && mimeType.startsWith('video/')) {
        // Check if URL suggests it's audio (webm audio files)
        const url = mediaItem.url || '';
        if (url.toLowerCase().includes('.webm') || fileType === 'audio') {
          mimeType = 'audio/webm';
        }
      }

      // If no mime type but we know it's audio, set appropriate audio mime type
      if (isAudio && !mimeType) {
        const url = mediaItem.url || '';
        if (url.toLowerCase().includes('.mp3')) {
          mimeType = 'audio/mpeg';
        } else if (url.toLowerCase().includes('.wav')) {
          mimeType = 'audio/wav';
        } else if (url.toLowerCase().includes('.ogg')) {
          mimeType = 'audio/ogg';
        } else {
          mimeType = 'audio/webm'; // Default for webm audio
        }
      }

      return {
        url: mediaItem.url,
        type: mimeType || (isAudio ? 'audio/webm' : fileType),
        name: mediaItem.file_name || mediaItem.name,
        size: mediaItem.file_size || mediaItem.size,
        isImage: mediaItem.is_image || (mimeType && mimeType.startsWith('image/')),
        isVideo: mediaItem.is_video || (mimeType && mimeType.startsWith('video/') && !isAudio),
        isDocument: mediaItem.is_document || (!isAudio && !mediaItem.is_image && !mediaItem.is_video &&
          !(mimeType && (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/')))),
        isAudio: isAudio,
        duration: mediaItem.duration || null
      };
    }) : null

    // Ensure user object has profile_image_url, handle empty strings and invalid values
    let userProfileImage = apiMessage.user?.profile_image_url;

    // Normalize the value - convert null/undefined to null, trim strings
    if (typeof userProfileImage === 'string') {
      userProfileImage = userProfileImage.trim();
      if (userProfileImage === '' || userProfileImage.toLowerCase() === 'null' || userProfileImage.toLowerCase() === 'undefined') {
        userProfileImage = null;
      }
    } else if (!userProfileImage || userProfileImage === null || userProfileImage === undefined) {
      userProfileImage = null;
    }

    // For own messages, if profile_image_url is missing, get it from localStorage
    if (apiMessage.is_own_message && !userProfileImage) {
      try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const storedProfileImage = userData.profile_image_url;
        if (storedProfileImage && typeof storedProfileImage === 'string') {
          const trimmed = storedProfileImage.trim();
          if (trimmed !== '' && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined') {
            userProfileImage = trimmed;
          }
        }
      } catch (e) {

      }
    }

    // Final validation - ensure we have a valid URL/path string
    const validProfileImage = (userProfileImage &&
      typeof userProfileImage === 'string' &&
      userProfileImage !== '' &&
      userProfileImage.toLowerCase() !== 'null' &&
      userProfileImage.toLowerCase() !== 'undefined' &&
      !userProfileImage.startsWith('null') &&
      !userProfileImage.startsWith('undefined') &&
      !userProfileImage.includes('null/') &&
      !userProfileImage.includes('undefined/'))
      ? userProfileImage
      : null;

    // Determine if message is from current user
    // Check is_own_message flag first, then fall back to user ID comparison
    let isOwnMessage = apiMessage.is_own_message;

    // If is_own_message is missing, determine it by comparing user IDs
    if (isOwnMessage === undefined || isOwnMessage === null) {
      try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const currentUserId = userData.user_id || userData.id;
        const messageUserId = apiMessage.user?.user_id || apiMessage.user?.id;

        if (currentUserId && messageUserId) {
          isOwnMessage = String(currentUserId) === String(messageUserId);
        }
      } catch (e) {
        // If we can't determine, default to false
        isOwnMessage = false;
      }
    }

    // Determine message type - prioritize message_type from API, then check audio/media
    let messageType = "text";

    // Use message_type from API if available (most reliable)
    if (apiMessage.message_type) {
      messageType = apiMessage.message_type;
    } else if (isAudio) {
      // Fallback: detect audio from media items
      messageType = "audio";
    } else if (apiMessage.has_media || (apiMessage.media && apiMessage.media.length > 0)) {
      messageType = "media";
    }

    // Extract duration if available (from message or media)
    const duration = apiMessage.duration ||
      (transformedMedia && transformedMedia[0]?.duration) ||
      null;

    return {
      id: apiMessage.id,
      senderId: isOwnMessage ? "me" : (apiMessage.user?.id || apiMessage.user_id),
      senderName: isOwnMessage ? youLabel : `${apiMessage.user?.first_name || ""} ${apiMessage.user?.last_name || ""}`.trim(),
      content: apiMessage.content,
      timestamp: formatTimestamp(apiMessage.created_at),
      originalTimestamp: apiMessage.created_at, // Store original timestamp for sorting
      type: messageType,
      user: {
        ...apiMessage.user,
        profile_image_url: validProfileImage || "/icons/person.png"
      },
      isOwnMessage: isOwnMessage,
      // For own messages (sent): use read_at (read receipt from receiver, respects privacy)
      // For received messages: use read_for_me (internal tracking, always updated)
      isRead: isOwnMessage
        ? (apiMessage.read_at || false)
        : (apiMessage.read_for_me || false),
      media: transformedMedia, // Add transformed media array if present
      duration: duration, // Add duration for audio messages
    }
  }

  // Load messages for a conversation
  const loadMessages = async (conversationId, page = 1, limit = 20) => {
    // Prevent duplicate calls for the same conversation
    if (loadingConversations.has(conversationId)) {
      return
    }

    try {
      // Mark conversation as loading
      setLoadingConversations(prev => new Set(prev).add(conversationId))

      if (page === 1) {
        setMessagesLoading(true)
        setIsLoadingOlderMessages(false)
      } else {
        setLoadingOlderMessages(true)
        setIsLoadingOlderMessages(true)
      }
      setMessagesError(null)

      const response = await conversationsAPI.getMessages(conversationId, { page, limit })


      const transformedMessages = response.data.messages.map(transformMessageData)

      // Ensure messages are in chronological order (oldest first)
      const sortedMessages = transformedMessages.sort((a, b) => {
        const dateA = new Date(a.originalTimestamp)
        const dateB = new Date(b.originalTimestamp)
        return dateA - dateB
      })

      setMessages(prev => {
        const existingMessages = prev[conversationId] || []

        if (page === 1) {
          // First page - replace all messages
          isAddingNewMessageRef.current = true
          return {
            ...prev,
            [conversationId]: sortedMessages
          }
        } else {
          // Subsequent pages - prepend older messages
          isAddingNewMessageRef.current = false
          return {
            ...prev,
            [conversationId]: [...sortedMessages, ...existingMessages]
          }
        }
      })

      // When loading the first page, clear any queued "new" messages 
      // since they should now be included in the server response
      if (page === 1) {
        setNewMessages(prev => {
          const updated = { ...prev };
          delete updated[conversationId];
          return updated;
        });
      }

      // Check if there are more messages to load using pagination data
      const hasMore = response.data.pagination && response.data.pagination.current_page < response.data.pagination.last_page
      setHasMoreMessages(prev => ({
        ...prev,
        [conversationId]: hasMore
      }))

    } catch (err) {
      let errorMessage = t('chat.errors.loadMessages')

      if (err.response) {
        errorMessage = err.response.data?.message || t('chat.errors.server', { code: err.response.status })
      } else if (err.request) {
        errorMessage = t('chat.errors.network')
      } else {
        errorMessage = err.message || errorMessage
      }

      setMessagesError(errorMessage)
    } finally {
      // Remove conversation from loading set
      setLoadingConversations(prev => {
        const newSet = new Set(prev)
        newSet.delete(conversationId)
        return newSet
      })

      if (page === 1) {
        setMessagesLoading(false)
        setIsLoadingOlderMessages(false)
      } else {
        setLoadingOlderMessages(false)
        setIsLoadingOlderMessages(false)
      }
    }
  }

  // Get current messages directly without memoization to prevent re-rendering issues
  const currentMessages = messages[selectedChat] || [];

  // Get current chat directly to prevent re-rendering issues
  const currentChat = memoizedChats.find((chat) => chat.id === selectedChat);

  // Separate new messages for smooth rendering
  const currentNewMessages = newMessages[selectedChat] || [];

  // Create a stable message list that doesn't cause re-renders
  const stableMessageList = useMemo(() => {
    // If no new messages, return current messages directly
    if (currentNewMessages.length === 0) {
      return currentMessages;
    }

    // Combine messages and sort by timestamp
    const combined = [...currentMessages, ...currentNewMessages];
    return combined.sort((a, b) => {
      const timeA = new Date(a.originalTimestamp || a.timestamp).getTime();
      const timeB = new Date(b.originalTimestamp || b.timestamp).getTime();
      return timeA - timeB;
    });
  }, [currentMessages, currentNewMessages]);

  // Use the stable list for rendering
  const memoizedMessageList = stableMessageList;

  // Helper functions that need to be defined before useMemo
  const getFileIcon = (fileType) => {
    if (!fileType) return '📄'
    if (fileType.startsWith('image/')) return '🖼️'
    if (fileType.startsWith('video/')) return '🎥'
    if (fileType.startsWith('audio/')) return '🎵'
    return '📄'
  }

  const handleDeleteMessage = useCallback((messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteMessage = async () => {
    if (!messageToDelete || !selectedChat) return;

    try {
      const messageId = messageToDelete;
      setShowDeleteModal(false);
      setMessageToDelete(null);

      // 1. Set isDeleting: true for immediate animation feedback
      setMessages(prev => {
        const chatId = String(selectedChat);
        const currentMessages = prev[chatId] || [];
        return {
          ...prev,
          [chatId]: currentMessages.map(msg =>
            (String(msg.id) === String(messageId) || String(msg.messageId) === String(messageId))
              ? { ...msg, isDeleting: true }
              : msg
          )
        };
      });

      // 2. Call API
      await conversationsAPI.deleteMessage(selectedChat, messageId);

      // 3. Remove message after animation completes (400ms)
      setTimeout(() => {
        setMessages(prev => {
          const chatId = String(selectedChat);
          const currentMessages = prev[chatId] || [];
          const updatedMessages = currentMessages.filter(msg =>
            String(msg.id) !== String(messageId) && String(msg.messageId) !== String(messageId)
          );
          return { ...prev, [chatId]: updatedMessages };
        });
      }, 400);

    } catch (err) {
      console.error("Failed to delete message:", err);
      // Optional: Revert isDeleting state on error
      setMessages(prev => {
        const chatId = String(selectedChat);
        const currentMessages = prev[chatId] || [];
        return {
          ...prev,
          [chatId]: currentMessages.map(msg =>
            (String(msg.id) === String(messageId) || String(msg.messageId) === String(messageId))
              ? { ...msg, isDeleting: false }
              : msg
          )
        };
      });
    }
  };

  const openMediaModal = (media) => {
    setSelectedMedia(media)
    setShowMediaModal(true)
  }

  // Memoized messages container to prevent re-renders
  const MemoizedMessagesContainer = useMemo(() => {
    return memoizedMessageList.length === 0 ? (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <p style={{ color: "#666", fontSize: "14px" }}>{t('chat.messages.none')}</p>
      </div>
    ) : (
      <>
        {/* Render messages with stable keys and React.memo optimization */}
        {memoizedMessageList.map((msg, index) => (
          <MessageBubble
            key={msg.id || `temp-${index}`}
            message={msg}
            currentChat={currentChat}
            openMediaModal={openMediaModal}
            getFileIcon={getFileIcon}
            onDelete={handleDeleteMessage}
          />
        ))}
      </>
    );
  }, [memoizedMessageList, currentChat, openMediaModal, getFileIcon, handleDeleteMessage, t]);

  // Clear new messages when conversation changes and merge them with regular messages
  useEffect(() => {
    if (selectedChat) {
      // Get new messages for this conversation
      const newMessagesForChat = newMessages[selectedChat] || [];

      if (newMessagesForChat.length > 0) {
        // Merge new messages with existing messages
        setMessages(prev => ({
          ...prev,
          [selectedChat]: [...(prev[selectedChat] || []), ...newMessagesForChat]
        }));

        // Clear new messages for this conversation
        setNewMessages(prev => {
          const { [selectedChat]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [selectedChat]); // Removed newMessages dependency to prevent infinite loop

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      } catch (e) {
        // fallback if smooth scroll not supported
        messagesEndRef.current.scrollIntoView();
      }
    }
  }, []);




  useEffect(() => {
    // If chats are still loading, don't process route parameters yet
    // This prevents falling back to handleChatWithReceiver when conversationId is not in the (empty) initial chat list
    if (loading) return;

    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("user");

    // Reset ref if receiverId has changed to a different value (or become null/undefined)
    // This allows new receiverIds to be processed while preventing re-processing on tab changes
    if (processedReceiverIdRef.current !== receiverId) {
      processedReceiverIdRef.current = null;
    }

    // 1) If receiverId is present in route, open or create/select that conversation
    if (receiverId) {
      // Only process if this receiverId hasn't been processed yet
      // This prevents re-processing when activeTab changes
      if (processedReceiverIdRef.current !== receiverId) {
        processedReceiverIdRef.current = receiverId;
        handleChatWithReceiver(receiverId, conversationType, adoptionRequestId);
        return;
      }
      // If already processed, skip to avoid re-processing on tab changes
      return;
    } else {
      // If receiverId is null/undefined, reset the ref to allow processing new receiverIds
      processedReceiverIdRef.current = null;
    }

    // 2) If path is exactly /chat (no params), show chat list only
    if (!receiverId && !conversationId && !conversationType) {
      setSelectedChat(null);
      return;
    }

    // 3) If conversationId + conversationType provided, set tab and open conversation
    if (conversationId && conversationType) {
      if (["all", "medical", "social"].includes(conversationType)) {
        setActiveTab(conversationType);
      }
      // First, try to find by conversation ID in existing chats
      // Enforce type matching to avoid ID collisions between User IDs and Conversation IDs
      const targetChat = memoizedChats.find((chat) =>
        chat.id.toString() === conversationId.toString() &&
        (conversationType === 'all' || chat.type === conversationType)
      );
      if (targetChat) {
        setSelectedChat(parseInt(conversationId));
        return;
      }

      // If not found in memory (common on refresh), try to fetch this specific conversation by ID
      // This prevents treating conversationId as receiverId on refresh
      const fetchAndSetSpecificChat = async () => {
        try {
          const response = await conversationsAPI.getConversation(conversationId);
          // Verify that the fetched conversation matches the requested type
          // This prevents ID collisions where a User ID (used in URL) matches a different Conversation ID
          const isTargetType = !conversationType || conversationType === 'all' || response.data.type === conversationType;

          if (isTargetType && response.data) {
            const transformedChat = transformChatData(response.data);
            if (transformedChat) {
              setChats(prev => {
                const alreadyExists = prev.find(c => c.id === transformedChat.id);
                if (alreadyExists) return prev;
                return [...prev, transformedChat];
              });
              setSelectedChat(parseInt(conversationId));
              return;
            }
          } else {
            // Type mismatch - this ID likely refers to a User, not this specific Conversation
            // Fall through to the catch/fallback logic
            throw new Error('Conversation type mismatch');
          }
        } catch (err) {
          // If 404 or error, then it might actually be a receiverId fallback (legacy behavior)
          // or the user doesn't have access. Fall back to finding by receiver.
          const chatByReceiver = memoizedChats.find((chat) =>
            chat.otherParticipant?.id?.toString() === conversationId.toString() &&
            chat.type === conversationType
          );
          if (chatByReceiver) {
            setSelectedChat(chatByReceiver.id);
            return;
          }

          // If still not found, treat conversationId as receiverId and fetch/create conversation
          handleChatWithReceiver(conversationId, conversationType, adoptionRequestId);
        }
      };

      fetchAndSetSpecificChat();
      return;
    }

    // 4) Support optional query param ?user=... for quick-open by name/id
    if (userId) {
      const existingChat = memoizedChats.find(
        (chat) => chat.id.toString() === userId || chat.name.toLowerCase().includes(userId)
      );
      if (existingChat) {
        setSelectedChat(existingChat.id);
      }
      // Note: Auto-selection for userId fallback moved to separate useEffect
      return;
    }

    // 5) Fallbacks
    if (conversationId) {
      // Enforce type matching here as well to prevent ID collisions
      const targetChat = memoizedChats.find((chat) =>
        chat.id.toString() === conversationId.toString() &&
        (!conversationType || conversationType === 'all' || chat.type === conversationType)
      );

      if (targetChat) {
        setSelectedChat(parseInt(conversationId));
      } else if (!receiverId) {
        // If it's not a conversation ID, it might be a receiver ID
        // (This handles cases where the route is /chat/45 and 45 is a user ID)
        handleChatWithReceiver(conversationId, conversationType, adoptionRequestId);
      }
      return;
    }

    if (initialChatId) {
      setSelectedChat(initialChatId);
      return;
    }

    // Note: Auto-selection logic moved to separate useEffect to prevent re-running on tab changes
  }, [initialChatId, conversationId, conversationType, receiverId, memoizedChats, loading]); // Added loading to dependencies

  // Separate useEffect for auto-selection logic that depends on activeTab
  // This prevents route parameter processing from re-running when tabs change
  useEffect(() => {
    // Don't auto-select if we're currently processing a notification
    if (isProcessingNotificationRef.current) {
      return;
    }

    // Don't auto-select if we're just viewing the chat list (no route params indicating a specific conversation)
    // Only auto-select when there are explicit route parameters (receiverId, conversationId, conversationType, or userId)
    const hasRouteParams = receiverId || conversationId || conversationType;
    if (!hasRouteParams) {
      // No route params - user is just viewing the chat list, don't auto-select
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("user");

    // Auto-select first chat unless on /chat or social tab
    // Only run if no chat is selected and we have route params or userId
    // IMPORTANT: Don't auto-select when just viewing the chat list (no route params)
    if (memoizedChats.length > 0 && !selectedChat && (receiverId || conversationId || conversationType || userId)) {
      if (activeTab !== "social") {
        setSelectedChat(memoizedChats[0].id);
      } else {
      }
    }

    // Handle userId fallback auto-selection
    if (userId && memoizedChats.length > 0 && !selectedChat) {
      const existingChat = memoizedChats.find(
        (chat) => chat.id.toString() === userId || chat.name.toLowerCase().includes(userId)
      );
      if (!existingChat && activeTab !== "social") {
        setSelectedChat(memoizedChats[0].id);
      }
    }
  }, [activeTab, memoizedChats, selectedChat, receiverId, conversationId, conversationType]);

  // Handle friend selection from modal
  const handleFriendSelect = async (friend) => {
    if (!friend || !friend.id) {

      return;
    }

    // Use existing handleChatWithReceiver function to create/select conversation
    await handleChatWithReceiver(friend.id, 'social');
  };

  // Handle chat with specific receiver
  const handleChatWithReceiver = async (receiverId, conversationType = null, adoptionRequestId = null) => {
    if (conversationLoading) {
      return;
    }

    // Check if the conversation already exists in chats - MUST MATCH TYPE if provided
    const existingChat = memoizedChats.find((chat) => {
      const matchReceiver = chat.otherParticipant?.id?.toString() === receiverId.toString();

      // If conversationType is strict (not null/all), we must match it
      if (conversationType && conversationType !== 'all') {
        const typeMatch = chat.type === conversationType;
        if (adoptionRequestId) {
          return typeMatch && matchReceiver && chat.case_id?.toString() === adoptionRequestId.toString() && chat.case_type?.includes('AdoptionRequest');
        }
        return matchReceiver && typeMatch;
      }

      if (adoptionRequestId) {
        return matchReceiver && chat.case_id?.toString() === adoptionRequestId.toString() && chat.case_type?.includes('AdoptionRequest');
      }

      return matchReceiver;
    });

    if (existingChat) {
      setSelectedChat(existingChat.id);
      // Update URL with new routing structure
      let targetUrl = `/chat/${existingChat.id}/${existingChat.type || 'all'}`;
      if (adoptionRequestId) {
        targetUrl += `?fromAdoptionRequest=${adoptionRequestId}`;
      }
      navigate(targetUrl);
      return;
    }

    try {
      setConversationLoading(true);

      // Try to get existing conversation, but if it fails (403 or 404), create a new one
      let conversation = null;
      // Map 'all' to null (defaults to social in backend) to avoid SQL enum errors
      const apiType = conversationType === 'all' ? null : conversationType;

      try {
        const caseType = adoptionRequestId ? 'App\\Models\\AdoptionRequest' : null;
        const response = await conversationsAPI.getConversationByReceiver(receiverId, apiType || 'social', adoptionRequestId, caseType);
        // Handle both legacy array response and future object response
        if (response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            conversation = response.data[0];
          } else if (!Array.isArray(response.data) && response.data.id) {
            conversation = response.data;
          }

          // Verify that the conversation returned matches our requested type
          // If we requested social and got medical (ID collision or backend switch), ignore it
          if (conversation && apiType && conversation.type !== apiType) {

            conversation = null;
          }
        }
      } catch (getErr) {
        // Security Check: If 403 Forbidden for medical chat, do not proceed
        if (getErr.response && getErr.response.status === 403 && apiType === 'medical') {
          if (window.showErrorToast) {
            window.showErrorToast(t("chat.errors.consultationRequired", { defaultValue: "Access Denied: Medical chats require an active SOS case." }));
          }
          setConversationLoading(false);
          return; // Stop execution, do not create new conversation
        }

        // If we get 404, it means the conversation doesn't exist, so we proceeded to create it
        // If we get other errors, we might still try to create (or maybe we should stop? For now keeping parity)
      }

      if (conversation) {
        // Existing conversation found
        const transformedChat = await transformReceiverConversationData(conversation, receiverId, conversationType);

        if (transformedChat) {
          setChats((prev) => {
            const existingChat = prev.find((chat) => chat.id === conversation.id);
            if (!existingChat) {
              return [...prev, transformedChat];
            }
            return prev;
          });

          setChatUpdates((prev) => {
            const { [conversation.id]: removed, ...rest } = prev;
            return rest;
          });

          setSelectedChat(conversation.id);
          // Update URL with new routing structure
          let targetUrl = `/chat/${conversation.id}/${conversationType || 'all'}`;
          if (adoptionRequestId) {
            targetUrl += `?fromAdoptionRequest=${adoptionRequestId}`;
          }
          navigate(targetUrl);
        }
      } else {
        // No existing conversation found - create a new one
        try {
          const caseType = adoptionRequestId ? 'App\\Models\\AdoptionRequest' : null;
          const createResponse = await conversationsAPI.createConversation([receiverId], apiType || 'social', adoptionRequestId, caseType);

          if (createResponse.data && createResponse.data.id) {
            const newConversation = createResponse.data;
            const transformedChat = await transformReceiverConversationData(newConversation, receiverId, conversationType);

            if (transformedChat) {
              setChats((prev) => {
                const existingChat = prev.find((chat) => chat.id === newConversation.id);
                if (!existingChat) {
                  return [...prev, transformedChat];
                }
                return prev;
              });

              setChatUpdates((prev) => {
                const { [newConversation.id]: removed, ...rest } = prev;
                return rest;
              });

              setSelectedChat(newConversation.id);
              // Update URL with new routing structure
              let targetUrl = `/chat/${newConversation.id}/${conversationType || 'all'}`;
              if (adoptionRequestId) {
                targetUrl += `?fromAdoptionRequest=${adoptionRequestId}`;
              }
              navigate(targetUrl);
            }
          }
        } catch {


          // Show user-friendly error for 500s (likely invalid user ID from URL manipulation) or other errors
          if (window.showErrorToast) {
            if (createErr.response && createErr.response.status === 500) {
              window.showErrorToast(t("chat.errors.invalidUser", { defaultValue: "Failed to create conversation: Invalid User or Server Error" }));
            } else {
              window.showErrorToast(t("chat.errors.createFailed", { defaultValue: "Failed to create conversation" }));
            }
          }

          // if (createErr.response?.data?.message) {

          // }
        }
      }
    } catch (err) {

      // No longer falling back to chats[0] to avoid accidental context switch to medical chats
    } finally {
      setConversationLoading(false);
    }
  };

  const transformReceiverConversationData = async (conversation, receiverId, conversationType) => {
    const receiver = conversation.participants.find(participant =>
      participant.id.toString() === receiverId.toString()
    )

    if (!receiver) {

      return null
    }

    try {
      if (loadingConversations.has(conversation.id)) {
        const receiverProfileImage = receiver.profile_image_url;
        const validReceiverAvatar = (receiverProfileImage &&
          receiverProfileImage !== '' &&
          receiverProfileImage !== 'null' &&
          receiverProfileImage !== 'undefined')
          ? receiverProfileImage
          : "/icons/person.png";

        return {
          id: conversation.id,
          name: `${receiver.first_name} ${receiver.last_name}`,
          avatar: validReceiverAvatar,
          lastMessage: "No messages yet",
          timestamp: formatTimestamp(conversation.updated_at),
          unreadCount: 0,
          isOnline: false,
          userType: "user",
          type: conversationType || "all",
          otherParticipant: receiver,
          isClosed: conversation.closed_at !== null, // Add closed status
          closedAt: conversation.closed_at, // Store closed timestamp
        }
      }

      setLoadingConversations(prev => new Set(prev).add(conversation.id))

      const response = await conversationsAPI.getMessages(conversation.id, { page: 1, limit: 20 })

      const transformedMessages = response.data.messages.map(transformMessageData)

      const sortedMessages = transformedMessages.sort((a, b) => {
        const dateA = new Date(a.originalTimestamp)
        const dateB = new Date(b.originalTimestamp)
        return dateA - dateB
      })

      setMessages(prev => ({
        ...prev,
        [conversation.id]: sortedMessages
      }))

      const hasMore = response.data.pagination && response.data.pagination.current_page < response.data.pagination.last_page
      setHasMoreMessages(prev => ({
        ...prev,
        [conversation.id]: hasMore
      }))

      setCurrentPages(prev => ({
        ...prev,
        [conversation.id]: 1
      }))

      const latestMessage = sortedMessages.length > 0 ? sortedMessages[sortedMessages.length - 1] : null

      const receiverProfileImage = receiver.profile_image_url;
      const validReceiverAvatar = (receiverProfileImage &&
        receiverProfileImage !== '' &&
        receiverProfileImage !== 'null' &&
        receiverProfileImage !== 'undefined')
        ? receiverProfileImage
        : "/icons/person.png";

      return {
        id: conversation.id,
        name: `${receiver.first_name} ${receiver.last_name}`,
        avatar: validReceiverAvatar,
        lastMessage: latestMessage ? latestMessage.content : "No messages yet",
        timestamp: latestMessage ? latestMessage.timestamp : formatTimestamp(conversation.updated_at),
        unreadCount: 0,
        isOnline: false,
        userType: "user",
        type: conversationType || "all",
        otherParticipant: receiver,
        isClosed: conversation.closed_at !== null,
        closedAt: conversation.closed_at,
      }
    } catch (err) {

      const receiverProfileImage = receiver.profile_image_url;
      const validReceiverAvatar = (receiverProfileImage &&
        receiverProfileImage !== '' &&
        receiverProfileImage !== 'null' &&
        receiverProfileImage !== 'undefined')
        ? receiverProfileImage
        : "/icons/person.png";

      return {
        id: conversation.id,
        name: `${receiver.first_name} ${receiver.last_name}`,
        avatar: validReceiverAvatar,
        lastMessage: "No messages yet",
        timestamp: formatTimestamp(conversation.updated_at),
        unreadCount: 0,
        isOnline: false,
        userType: "user",
        type: conversationType || "all",
        otherParticipant: receiver,
        isClosed: conversation.closed_at !== null,
        closedAt: conversation.closed_at,
      }
    } finally {
      setLoadingConversations(prev => {
        const newSet = new Set(prev)
        newSet.delete(conversation.id)
        return newSet
      })
    }
  }

  const debouncedLoadMessages = debounce((conversationId, page, limit) => {
    loadMessages(conversationId, page, limit);
  }, 300);

  useEffect(() => {
    if (selectedChat && (!messages[selectedChat] || messages[selectedChat].length === 0)) {
      debouncedLoadMessages(selectedChat, 1);
    }

    if (selectedChat) {
      markConversationAsRead(selectedChat);
      // Store active conversation ID in localStorage for notification handling
      localStorage.setItem('active_conversation_id', selectedChat.toString());
    } else {
      // Clear active conversation ID when no chat is selected
      localStorage.removeItem('active_conversation_id');
    }
  }, [selectedChat]);
  const [currentPages, setCurrentPages] = useState({})

  const [loadingConversations, setLoadingConversations] = useState(new Set())
  const [conversationLoading, setConversationLoading] = useState(false)

  const loadOlderMessages = async () => {
    if (!selectedChat || loadingOlderMessages || !hasMoreMessages[selectedChat]) {
      return
    }

    const currentPage = currentPages[selectedChat] || 1
    const nextPage = currentPage + 1

    const messagesBeforeLoad = messages[selectedChat]?.length || 0

    try {
      await loadMessages(selectedChat, nextPage)
      setCurrentPages(prev => ({
        ...prev,
        [selectedChat]: nextPage
      }))

      setTimeout(() => {
        const messagesAfterLoad = messages[selectedChat]?.length || 0
        const newMessagesCount = messagesAfterLoad - messagesBeforeLoad
        if (newMessagesCount > 0 && messagesContainerRef.current) {
          const container = messagesContainerRef.current
          const scrollHeight = container.scrollHeight
          const newScrollTop = scrollHeight - container.clientHeight
          container.scrollTop = newScrollTop
        }
      }, 100)

    } catch (error) {

    }
  }



  const markMessageAsRead = async (conversationId, messageId) => {
    try {
      await conversationsAPI.markAsRead(conversationId, { message_id: messageId })

      setMessages(prev => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map(msg => {
          // Only update read status for received messages (read_for_me tracking)
          // For sent messages, keep existing read_at status (depends on receiver's privacy)
          if (msg.id === messageId && !msg.isOwnMessage) {
            return { ...msg, isRead: true }
          }
          return msg
        })
      }))
    } catch (err) {

    }
  }

  // Handle ending conversation - show confirmation
  const handleEndConversation = () => {
    if (!selectedChat) {
      return;
    }

    // Check if conversation is already closed
    if (currentChat && currentChat.isClosed) {
      return;
    }

    // Show confirmation dialog
    if (window.confirm(t('chat.endConversation.confirm') || 'Are you sure you want to end this conversation?')) {
      confirmEndConversation();
    }
  }

  // Actually end the conversation
  const confirmEndConversation = async () => {
    if (!selectedChat) return;

    // IMMEDIATELY update UI to show conversation as closed (optimistic update)

    // Dispatch conversation closed event for instant UI update
    const conversationClosedEvent = new CustomEvent('conversationClosed', {
      detail: {
        conversationId: selectedChat,
        message: 'Conversation ended by you',
        closedBy: 'You',
        timestamp: new Date().toISOString()
      }
    });

    // Dispatch the event IMMEDIATELY for instant UI update
    window.dispatchEvent(conversationClosedEvent);


    try {

      // Call the API to close the conversation
      const response = await conversationsAPI.closeConversation(selectedChat);

      if (response.status === 200 || response.status === 204) {
        // Show success message
        if (window.showSuccessToast) {
          window.showSuccessToast('Conversation ended successfully');
        }

        // If it's an urgent case, show the rating modal after closure
        if (currentChat && (currentChat.urgent_case_id || currentChat.case_id || currentChat.type === 'medical')) {
          const caseId = currentChat.urgent_case_id || currentChat.case_id || selectedChat;
          setRatingCaseId(caseId);
          // Small delay to let the closure toast be seen
          setTimeout(() => {
            setShowRatingModal(true);
          }, 1000);
        }
      } else {
        if (window.showErrorToast) {
          window.showErrorToast('Failed to end conversation');
        }
      }

    } catch (error) {

    }
  }

  // Handle opening appointment modal
  const handleOpenAppointmentModal = () => {
    // Get current chat directly in case it's not available in scope
    const chat = memoizedChats.find((chat) => chat.id === selectedChat);

    if (!chat || !chat.otherParticipant) {
      showErrorToast(t('chat.appointment.noParticipant') || 'Unable to open appointment modal: No participant found');
      return;
    }

    // If vet user, ensure fresh clinic data is fetched to get latest appointment types
    if (currentUserType === USER_TYPES.VETERINARIAN) {
      if (ownedClinics && ownedClinics.length > 1) {
        // We don't have a SelectClinicModal in ChatPage yet, so just proceed with current clinic 
        // but try to fetch its fresh data if we have one.
        // For now, if multiple clinics, we'll use the current one or first one.
        const currentClinic = clinic || (ownedClinics.length > 0 ? ownedClinics[0] : null);
        if (currentClinic) {
          handleClinicSelected(currentClinic);
          return;
        }
      } else if (ownedClinics && ownedClinics.length === 1) {
        handleClinicSelected(ownedClinics[0]);
        return;
      }
    }

    setShowAppointmentModal(true);
  }

  // Get initial values for appointment modal
  const [initialAppointmentValues, setInitialAppointmentValues] = useState({ initialVet: null, initialPetOwner: null, initialPet: null });

  useEffect(() => {
    const chat = memoizedChats.find((chat) => chat.id === selectedChat);
    if (!chat || !chat.otherParticipant) {
      setInitialAppointmentValues({ initialVet: null, initialPetOwner: null, initialPet: null });
      return;
    }

    // Get current user (vet)
    const currentUser = authUtils.getCurrentUser();
    let initialVet = null;
    if (currentUser && clinicMembers) {
      // Find the vet that matches the current user
      initialVet = clinicMembers.find(member =>
        member.role === 'doctor' &&
        (member.user_id === currentUser.id || member.user?.id === currentUser.id)
      );
    }

    // Get pet owner from otherParticipant
    const initialPetOwner = chat.otherParticipant ? {
      id: chat.otherParticipant.id,
      first_name: chat.otherParticipant.first_name,
      last_name: chat.otherParticipant.last_name,
      full_name: `${chat.otherParticipant.first_name || ''} ${chat.otherParticipant.last_name || ''}`.trim(),
      display_name: `${chat.otherParticipant.first_name || ''} ${chat.otherParticipant.last_name || ''}`.trim(),
    } : null;

    // Check if this is an SOS conversation and fetch pet information
    const fetchPetFromSOS = async () => {
      // Check if conversation has case_id or case_type indicating it's an SOS conversation
      const caseId = chat.case_id || chat.caseId;
      const caseType = chat.case_type || chat.caseType;

      if (caseId && (caseType === 'UrgentMedicalCase' || chat.type === 'medical' || chat.isSOS)) {
        try {
          // Import urgentCaseAPI dynamically to avoid circular dependencies
          const { urgentCaseAPI } = await import('../../services/api');
          const response = await urgentCaseAPI.getUrgentCase(caseId);
          const caseData = response.data?.data || response.data;

          let firstPet = null;
          if (caseData?.pets && caseData.pets.length > 0) {
            firstPet = caseData.pets[0];
          } else if (caseData?.pet) {
            firstPet = caseData.pet;
          }

          if (firstPet) {
            setInitialAppointmentValues({
              initialVet,
              initialPetOwner,
              initialPet: {
                id: firstPet.id,
                name: firstPet.name,
                species: firstPet.species,
                breed: firstPet.breed,
                ...firstPet
              }
            });
            return;
          }
        } catch (error) {

          // Continue to fallback
        }
      } else if (chat.type === 'medical') {
        // Medical conversation without case_id - fetch guardian's pets and auto-select first one
        try {
          const { petAPI } = await import('../../services/api');
          const response = await petAPI.getOtherPets(initialPetOwner.id);
          const pets = response.data?.data || [];

          if (pets.length > 0) {
            const firstPet = pets[0];
            setInitialAppointmentValues({
              initialVet,
              initialPetOwner,
              initialPet: {
                id: firstPet.id,
                name: firstPet.name,
                species: firstPet.species,
                breed: firstPet.breed,
                ...firstPet
              }
            });
            return;
          }
        } catch (error) {

        }
      }

      // If not SOS or no pet found, set without pet
      setInitialAppointmentValues({ initialVet, initialPetOwner, initialPet: null });
    };

    fetchPetFromSOS();
  }, [selectedChat, memoizedChats, clinicMembers]);

  // Handle saving appointment
  const handleSaveAppointment = async (appointment) => {
    if (!currentChat || !currentChat.otherParticipant) {
      showErrorToast(t('chat.appointment.noParticipant') || 'Unable to save appointment: No participant found');
      return false;
    }

    if (!clinic || !clinic.id) {
      showErrorToast(t('chat.appointment.noClinic') || 'Unable to save appointment: No clinic selected');
      return false;
    }

    try {
      setIsSavingAppointment(true);

      // Validate required data
      if (!appointment.pet_id) {
        showErrorToast(t('receptionAppointments.toasts.petRequired') || 'Pet is required');
        return false;
      }
      if (!appointment.vet_id) {
        showErrorToast(t('receptionAppointments.toasts.vetRequired') || 'Veterinarian is required');
        return false;
      }
      if (!appointment.appointment_type_id) {
        showErrorToast(t('receptionAppointments.toasts.typeRequired') || 'Appointment type is required');
        return false;
      }

      // Combine date and time
      const appointment_at =
        appointment.date && appointment.time
          ? `${appointment.date} ${appointment.time}:00`
          : null;

      if (!appointment_at) {
        showErrorToast(t('receptionAppointments.toasts.datetimeRequired') || 'Date and time are required');
        return false;
      }

      const payload = {
        pet_id: appointment.pet_id,
        clinic_id: clinic.id,
        vet_id: appointment.vet_id,
        appointment_at: appointment_at,
        pet_owner_id: currentChat.otherParticipant.id,
        reason: appointment.reason || '',
        appointment_type_id: appointment.appointment_type_id,
        // Include conversation_id if available (for SOS conversations that should auto-close)
        ...(currentChat?.id && { conversation_id: currentChat.id }),
      };


      // Send to backend
      const response = await clinicAppointmentsAPI.createAppointment(payload);

      if (response.data && response.data.status === 'success') {
        showSuccessToast(t('receptionAppointments.toasts.saveSuccess') || 'Appointment scheduled successfully');
        setShowAppointmentModal(false);

        // If this was created from an SOS chat (has conversation_id), refresh the chat to show closure status
        if (currentChat?.id && payload.conversation_id) {
          const conversationId = currentChat.id;

          // Optimistically update the current chat to show it's closed immediately
          // This provides instant feedback
          setChats(prev => prev.map(chat =>
            chat.id === conversationId
              ? {
                ...chat,
                isClosed: true,
                closedAt: new Date().toISOString(),
                lastMessage: 'Conversation closed - Appointment scheduled'
              }
              : chat
          ));

          // Update chat updates state as well
          setChatUpdates(prev => ({
            ...prev,
            [conversationId]: {
              ...prev[conversationId],
              isClosed: true,
              closedAt: new Date().toISOString(),
              lastMessage: 'Conversation closed - Appointment scheduled'
            }
          }));

          // Refresh the conversation from API to get accurate closure status
          // Use a small delay to ensure backend has processed the closure
          setTimeout(async () => {
            try {
              const conversationResponse = await conversationsAPI.getConversation(conversationId);
              if (conversationResponse?.data?.data) {
                const updatedChat = transformChatData(conversationResponse.data.data);
                if (updatedChat) {
                  setChats(prev => prev.map(chat =>
                    chat.id === conversationId ? updatedChat : chat
                  ));
                }
              }
            } catch (err) {

              // Fallback to reloading all chats if single conversation refresh fails
              loadChats();
            }
          }, 500);
        }

        return true;
      } else {
        throw new Error(response.data?.message || 'Failed to save appointment');
      }
    } catch (error) {

      showErrorToast(error.response?.data?.message || error.message || t('receptionAppointments.toasts.saveFailed') || 'Failed to save appointment');
      return false;
    } finally {
      setIsSavingAppointment(false);
    }
  }

  useEffect(() => {
    if (isAddingNewMessageRef.current) {
      setTimeout(() => {
        isAddingNewMessageRef.current = false
      }, 100)
    }
  }, [currentMessages])

  const handleSendMessage = async () => {
    const messageValue = messageInputText;
    if ((messageValue.trim() || selectedFiles.length > 0) && selectedChat) {
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const rawProfileImage = userData.profile_image_url;
      const currentUserProfileImage = (rawProfileImage &&
        rawProfileImage !== '' &&
        rawProfileImage !== 'null' &&
        rawProfileImage !== 'undefined')
        ? rawProfileImage
        : "/icons/person.png";

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        senderId: "me",
        senderName: "You",
        content: messageValue,
        timestamp: formatTimestamp(new Date()),
        originalTimestamp: new Date().toISOString(),
        type: selectedFiles.length > 0 ? "media" : "text",
        user: {
          id: "me",
          first_name: "You",
          last_name: "",
          profile_image_url: currentUserProfileImage
        },
        isOwnMessage: true,
        isRead: false,
        isSending: true,
        media: selectedFiles.length > 0 ? selectedFiles.map(file => ({
          url: URL.createObjectURL(file),
          type: file.type,
          name: file.name,
          size: file.size,
          isImage: file.type.startsWith('image/'),
          isVideo: file.type.startsWith('video/'),
          isDocument: !file.type.startsWith('image/') && !file.type.startsWith('video/')
        })) : null
      }

      try {
        setIsSending(true)
        isAddingNewMessageRef.current = true
        setMessages(prev => {
          const currentMessages = prev[selectedChat] || [];
          const messageExists = currentMessages.some(msg => msg.id === optimisticMessage.id);
          if (messageExists) {
            return prev;
          }
          return {
            ...prev,
            [selectedChat]: [...currentMessages, optimisticMessage]
          };
        })

        if (messageInputRef.current) {
          messageInputRef.current.value = "";
        }
        setMessageInputText("")
        setSelectedFiles([])

        // Stop typing indicator when message is sent
        stopCurrentUserTyping()

        setTimeout(() => {
          scrollToBottomSmooth();
        }, 100);

        const currentPath = window.location.pathname
        // Skip URL reset to preserve parameters as requested

        let response
        if (selectedFiles.length > 0) {
          response = await conversationsAPI.sendMessageWithMedia(selectedChat, messageValue, selectedFiles)
        } else {
          response = await conversationsAPI.sendMessage(selectedChat, {
            content: messageValue
          })
        }


        const realMessage = {
          id: response.data.id,
          senderId: "me",
          senderName: youLabel,
          content: response.data.content || messageValue,
          timestamp: formatTimestamp(response.data.created_at),
          originalTimestamp: response.data.created_at,
          type: selectedFiles.length > 0 ? "media" : "text",
          user: {
            id: "me",
            first_name: youLabel,
            last_name: "",
            profile_image_url: (response.data.user?.profile_image_url &&
              response.data.user.profile_image_url !== '' &&
              response.data.user.profile_image_url !== 'null' &&
              response.data.user.profile_image_url !== 'undefined')
              ? response.data.user.profile_image_url
              : currentUserProfileImage
          },
          isOwnMessage: true,
          isRead: false,
          isSending: false,
          media: response.data.media ? response.data.media.map(mediaItem => ({
            url: mediaItem.url,
            type: mediaItem.mime_type,
            name: mediaItem.file_name,
            size: mediaItem.file_size,
            isImage: mediaItem.is_image,
            isVideo: mediaItem.is_video,
            isDocument: mediaItem.is_document
          })) : null
        }

        setMessages(prev => {
          const currentMessages = prev[selectedChat] || [];
          const optimisticIndex = currentMessages.findIndex(msg => msg.id === optimisticMessage.id);

          if (optimisticIndex === -1) {
            return {
              ...prev,
              [selectedChat]: [...currentMessages, realMessage]
            };
          }

          const optimisticMsg = currentMessages[optimisticIndex];
          const isDifferent = optimisticMsg.content !== realMessage.content ||
            optimisticMsg.timestamp !== realMessage.timestamp ||
            JSON.stringify(optimisticMsg.media) !== JSON.stringify(realMessage.media);

          if (!isDifferent) {
            return prev;
          }

          const updatedMessages = [...currentMessages];
          updatedMessages[optimisticIndex] = realMessage;
          return {
            ...prev,
            [selectedChat]: updatedMessages
          };
        })

        setChats(prev => {
          let currentChatIndex = prev.findIndex(chat => chat.id === selectedChat);

          // If chat is not in the list (e.g., new conversation), trigger a fetch to add it
          // We can't fetch synchronously inside setChats, so we return prev for now
          // and fetch asynchronously outside
          if (currentChatIndex === -1) {
            // Trigger fetch outside of state update
            conversationsAPI.getConversation(selectedChat)
              .then(response => {
                const newChatData = response.data;
                if (newChatData) {
                  const transformedChat = transformChatData(newChatData);
                  if (transformedChat) {
                    setChats(currentChats => {
                      // Check if it's already added to avoid duplicates
                      if (currentChats.some(c => c.id === selectedChat)) return currentChats;

                      const newChatWithUpdate = {
                        ...transformedChat,
                        lastMessage: messageValue,
                        timestamp: new Date().toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }),
                        unreadCount: 0
                      };
                      return [newChatWithUpdate, ...currentChats];
                    });
                  }
                }
              })
              .catch(() => { });

            return prev;
          }

          const currentChat = prev[currentChatIndex];
          const otherChats = prev.filter((_, index) => index !== currentChatIndex);

          const updatedCurrentChat = {
            ...currentChat,
            lastMessage: messageValue,
            timestamp: new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            unreadCount: 0
          };

          return [updatedCurrentChat, ...otherChats];
        });

      } catch (err) {


        setMessages(prev => {
          const currentMessages = prev[selectedChat] || [];
          const updatedMessages = currentMessages.filter(msg => msg.id !== optimisticMessage.id);
          return {
            ...prev,
            [selectedChat]: updatedMessages
          };
        })

        alert(t('chat.errors.sendMessage'))
      } finally {
        setIsSending(false)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && selectedFiles.length === 0) {
      e.preventDefault()
      handleSendMessage()
      scrollToBottom();
    }
  }

  // Insert emoji into message input at cursor position
  const insertEmoji = (emoji) => {
    const input = messageInputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const text = messageInputText;
    const newText = text.substring(0, start) + emoji + text.substring(end);

    setMessageInputText(newText);

    // Set cursor position after inserted emoji
    setTimeout(() => {
      input.focus();
      const newPosition = start + emoji.length;
      input.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    setSelectedFiles(prev => [...prev, ...files])
    event.target.value = '' // Reset input
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const closeMediaModal = () => {
    setShowMediaModal(false)
    setSelectedMedia(null)
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showMediaModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showMediaModal])


  // Memoize mic button color
  const micButtonColor = useMemo(() => {
    return isRecording ? "#EF4444" : "#999999";
  }, [isRecording]);

  // Start audio recording
  const startRecording = async () => {
    try {
      const startTime = Date.now();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      // Create AudioContext for waveform visualization
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') {
        try {
          await audioCtx.resume();
        } catch (resumeError) {

        }
      }
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      setAudioContext(audioCtx);
      setAnalyserNode(analyser);

      // Try to find a supported mime type
      let mimeType = 'audio/webm;codecs=opus';
      const options = { mimeType };

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Try fallback options
        const fallbackTypes = [
          'audio/webm',
          'audio/mp4',
          'audio/ogg;codecs=opus',
          'audio/wav'
        ];

        for (const type of fallbackTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            mimeType = type;
            options.mimeType = type;
            break;
          }
        }
      }

      const recorder = new MediaRecorder(stream, options);

      const chunks = [];
      const finalMimeType = mimeType; // Store mimeType for use in callback

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Determine file extension based on mime type
        let extension = 'webm';
        if (finalMimeType.includes('mp4')) extension = 'mp4';
        else if (finalMimeType.includes('ogg')) extension = 'ogg';
        else if (finalMimeType.includes('wav')) extension = 'wav';

        const audioBlob = new Blob(chunks, { type: finalMimeType });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Create a File object from the blob
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.${extension}`, {
          type: finalMimeType
        });

        // Calculate fallback duration
        const fallbackDuration = Math.max(0, (Date.now() - startTime) / 1000);

        const audio = new Audio();
        audio.src = audioUrl;

        audio.onloadedmetadata = () => {
          let duration = audio.duration;

          if (duration === Infinity || isNaN(duration) || duration === 0) {
            duration = fallbackDuration;
          }

          setRecordedAudio({
            blob: audioBlob,
            url: audioUrl,
            file: audioFile,
            duration: duration,
            mimeType: finalMimeType
          });
        };

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.onerror = (event) => {

        stopRecording();
        alert(t('chat.errors.recordAudio'));
      };

      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer for recording duration
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);

    } catch (error) {

      alert(t('chat.errors.micAccess'));
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }

    // Clean up audio context
    if (audioContext) {
      audioContext.close();
      setAudioContext(null);
    }
    setAnalyserNode(null);

    // Stop audio stream tracks
    if (audioStream) {
      audioStream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      setAudioStream(null);
    }

    setIsRecording(false);
    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingDuration(0);

  };

  // Cancel recording
  // Handle hiding a conversation
  const handleHideChat = async () => {
    if (!selectedChat) return;

    try {
      setIsHidingChat(true);
      await conversationsAPI.hideConversation(selectedChat);

      // Update local state to remove the hidden chat
      setChats(prev => prev.filter(c => c.id !== selectedChat));
      // Also update memoized chats if applicable
      setSelectedChat(null);
      selectedChatRef.current = null;
      setIsHideChatModalOpen(false);

      if (window.showSuccessToast) {
        window.showSuccessToast(t("chat.actions.hidden", { defaultValue: "Conversation hidden successfully" }));
      }

      // Refresh chats list to ensure sync
      loadChats();

    } catch (error) {
      if (window.showErrorToast) {
        window.showErrorToast(t("chat.errors.hideFailed", { defaultValue: "Failed to hide conversation" }));
      }
    } finally {
      setIsHidingChat(false);
    }
  };

  // Handle deleting a conversation
  const handleDeleteChat = async () => {
    if (!selectedChat) return;

    const chatToDelete = selectedChat;

    try {
      setIsDeletingChat(true);
      await conversationsAPI.deleteConversation(chatToDelete);

      // Update local state to remove the deleted chat
      setChats(prev => prev.filter(c => c.id !== chatToDelete));

      // Clear message history for this conversation in frontend state
      setMessages(prev => {
        const updated = { ...prev };
        delete updated[chatToDelete];
        return updated;
      });

      // Clear any pending/queue messages for this conversation
      setNewMessages(prev => {
        const updated = { ...prev };
        delete updated[chatToDelete];
        return updated;
      });

      // Reset pagination state
      setHasMoreMessages(prev => {
        const updated = { ...prev };
        delete updated[chatToDelete];
        return updated;
      });

      setSelectedChat(null);
      selectedChatRef.current = null;
      setIsDeleteChatModalOpen(false);

      if (window.showSuccessToast) {
        window.showSuccessToast(t("chat.actions.deleted", { defaultValue: "Conversation deleted successfully" }));
      }

      // Refresh chats list
      loadChats();

    } catch (error) {
      if (window.showErrorToast) {
        window.showErrorToast(t("chat.errors.deleteFailed", { defaultValue: "Failed to delete conversation" }));
      }
    } finally {
      setIsDeletingChat(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }

    // Clean up audio context
    if (audioContext) {
      audioContext.close();
      setAudioContext(null);
    }
    setAnalyserNode(null);

    // Stop all tracks if any are still active
    if (mediaRecorder && mediaRecorder.stream) {
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }

    if (audioStream) {
      audioStream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      setAudioStream(null);
    }

    setIsRecording(false);
    setMediaRecorder(null);
    setAudioChunks([]);
    setRecordingDuration(0);
    setRecordedAudio(null);

  };

  // Send recorded audio
  const sendRecordedAudio = async () => {
    if (!recordedAudio || !selectedChat) {
      return;
    }

    try {
      setIsSending(true);

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const rawProfileImage = userData.profile_image_url;
      const currentUserProfileImage = (rawProfileImage &&
        rawProfileImage !== '' &&
        rawProfileImage !== 'null' &&
        rawProfileImage !== 'undefined')
        ? rawProfileImage
        : "/icons/person.png";

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        senderId: "me",
        senderName: youLabel,
        content: voiceMessageLabel,
        timestamp: formatTimestamp(new Date()),
        originalTimestamp: new Date().toISOString(),
        type: "audio",
        user: {
          id: "me",
          first_name: youLabel,
          last_name: "",
          profile_image_url: currentUserProfileImage
        },
        isOwnMessage: true,
        isRead: false,
        isSending: true,
        media: [{
          url: recordedAudio.url,
          type: recordedAudio.mimeType || 'audio/webm',
          name: recordedAudio.file.name,
          size: recordedAudio.blob.size,
          isImage: false,
          isVideo: false,
          isDocument: false,
          isAudio: true
        }]
      };

      isAddingNewMessageRef.current = true;
      setMessages(prev => {
        const currentMessages = prev[selectedChat] || [];
        const messageExists = currentMessages.some(msg => msg.id === optimisticMessage.id);
        if (messageExists) {
          return prev;
        }
        return {
          ...prev,
          [selectedChat]: [...currentMessages, optimisticMessage]
        };
      });

      setTimeout(() => {
        scrollToBottomSmooth();
      }, 100);

      const currentPath = window.location.pathname;
      // Skip URL reset to preserve parameters as requested

      // Send audio file
      const response = await conversationsAPI.sendMessageWithMedia(selectedChat, voiceMessageLabel, [recordedAudio.file], recordedAudio.duration);


      const realMessage = {
        id: response.data.id,
        senderId: "me",
        senderName: youLabel,
        content: response.data.content || voiceMessageLabel,
        timestamp: formatTimestamp(response.data.created_at),
        originalTimestamp: response.data.created_at,
        type: "audio",
        user: {
          id: "me",
          first_name: youLabel,
          last_name: "",
          profile_image_url: (response.data.user?.profile_image_url &&
            response.data.user.profile_image_url !== '' &&
            response.data.user.profile_image_url !== 'null' &&
            response.data.user.profile_image_url !== 'undefined')
            ? response.data.user.profile_image_url
            : currentUserProfileImage
        },
        isOwnMessage: true,
        isRead: false,
        isSending: false,
        media: response.data.media ? response.data.media.map(mediaItem => ({
          url: mediaItem.url,
          type: mediaItem.mime_type,
          name: mediaItem.file_name,
          size: mediaItem.file_size,
          isImage: mediaItem.is_image,
          isVideo: mediaItem.is_video,
          isDocument: mediaItem.is_document,
          isAudio: mediaItem.is_audio || (mediaItem.mime_type && mediaItem.mime_type.startsWith('audio/'))
        })) : null
      };

      setMessages(prev => {
        const currentMessages = prev[selectedChat] || [];
        const optimisticIndex = currentMessages.findIndex(msg => msg.id === optimisticMessage.id);

        if (optimisticIndex === -1) {
          return {
            ...prev,
            [selectedChat]: [...currentMessages, realMessage]
          };
        }

        const updatedMessages = [...currentMessages];
        updatedMessages[optimisticIndex] = realMessage;
        return {
          ...prev,
          [selectedChat]: updatedMessages
        };
      });

      setChats(prev => {
        const currentChatIndex = prev.findIndex(chat => chat.id === selectedChat);
        if (currentChatIndex === -1) return prev;

        const currentChat = prev[currentChatIndex];
        const otherChats = prev.filter((_, index) => index !== currentChatIndex);

        const updatedCurrentChat = {
          ...currentChat,
          lastMessage: "Voice message",
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          unreadCount: 0
        };

        return [updatedCurrentChat, ...otherChats];
      });

      // Clean up recorded audio
      if (recordedAudio.url) {
        URL.revokeObjectURL(recordedAudio.url);
      }
      setRecordedAudio(null);

    } catch (err) {

      alert(t('chat.errors.sendVoice'));
    } finally {
      setIsSending(false);
    }
  };

  // Format recording duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraLoading(true);

      // Stop any existing stream first
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }

      // Try different constraints
      const constraints = [
        {
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: cameraMode === 'video'
        },
        {
          video: {
            facingMode: 'user'
          },
          audio: cameraMode === 'video'
        },
        {
          video: true,
          audio: cameraMode === 'video'
        }
      ];

      let stream = null;
      let lastError = null;

      for (const constraint of constraints) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraint);
          break;
        } catch (error) {

          lastError = error;
          continue;
        }
      }

      if (!stream) {
        throw lastError || new Error('Failed to access camera');
      }

      // Log stream tracks for debugging
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();


      // Warn if audio is missing in video mode
      if (cameraMode === 'video' && audioTracks.length === 0) {

      }

      setCameraStream(stream);
      setIsCameraOpen(true);
      setCameraLoading(false);

    } catch (error) {

      setCameraLoading(false);
      setIsCameraOpen(false);

      let errorMessage = 'Could not access camera. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on your device.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Camera is being used by another application.';
      } else {
        errorMessage += 'Please check your camera permissions.';
      }

      alert(errorMessage);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
      cameraVideoRef.current.pause();
    }
    setIsCameraOpen(false);
    setIsRecordingVideo(false);
    setVideoRecorder(null);
    setVideoChunks([]);
    setCameraLoading(false);
  };

  // Capture photo
  const capturePhoto = () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) {
      return;
    }

    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFiles(prev => [...prev, file]);
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  // Start video recording
  const startVideoRecording = () => {
    if (!cameraStream) {

      alert('Camera stream not available. Please start the camera first.');
      return;
    }

    // Verify stream has video tracks
    const videoTracks = cameraStream.getVideoTracks();
    if (videoTracks.length === 0) {

      alert('No video track available. Please check your camera.');
      return;
    }

    // Check if video track is active
    if (videoTracks[0].readyState !== 'live') {

      alert('Video track is not active. Please restart the camera.');
      return;
    }

    try {
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        alert('MediaRecorder is not supported in your browser.');
        return;
      }

      // Try different mime types in order of preference
      let mimeType = null;
      const preferredTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
        'video/ogg;codecs=theora,opus',
        'video/ogg'
      ];

      for (const type of preferredTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      if (!mimeType) {
        // Fallback: use default (browser will choose)
        mimeType = '';

      }

      const recorder = new MediaRecorder(cameraStream, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });

      const chunks = [];
      setVideoChunks([]); // Reset chunks

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          // Also update state for tracking
          setVideoChunks(prev => [...prev, event.data]);
        }
      };

      recorder.onstop = () => {

        if (chunks.length === 0) {

          alert('No video data was recorded. Please try again.');
          setIsRecordingVideo(false);
          setVideoRecorder(null);
          setVideoChunks([]);
          return;
        }

        // Determine final mime type and extension
        let finalMimeType = mimeType || 'video/webm';
        let extension = 'webm';

        if (finalMimeType.includes('mp4')) {
          extension = 'mp4';
        } else if (finalMimeType.includes('ogg')) {
          extension = 'ogg';
        }

        const blob = new Blob(chunks, { type: finalMimeType });

        if (blob.size === 0) {

          alert('Recorded video is empty. Please try again.');
          setIsRecordingVideo(false);
          setVideoRecorder(null);
          setVideoChunks([]);
          return;
        }

        const file = new File([blob], `video-${Date.now()}.${extension}`, { type: finalMimeType });

        setSelectedFiles(prev => [...prev, file]);
        stopCamera();
      };

      recorder.onerror = (event) => {

        setIsRecordingVideo(false);
        setVideoRecorder(null);
        setVideoChunks([]);
        alert('Error recording video. Please try again.');
      };

      recorder.onstart = () => {
      };

      // Start recording with timeslice to collect data periodically
      // This ensures data is collected even if stop() is called quickly
      recorder.start(100); // Collect data every 100ms

      setVideoRecorder(recorder);
      setIsRecordingVideo(true);
    } catch (error) {

      setIsRecordingVideo(false);
      setVideoRecorder(null);
      setVideoChunks([]);
      alert(`Could not start video recording: ${error.message || 'Unknown error'}`);
    }
  };

  // Stop video recording
  const stopVideoRecording = () => {
    if (!videoRecorder) {

      return;
    }

    if (videoRecorder.state === 'recording') {
      // Request any remaining data before stopping
      try {
        videoRecorder.requestData();
      } catch (error) {

      }

      // Stop the recorder (this will trigger onstop event)
      videoRecorder.stop();
    } else if (videoRecorder.state === 'inactive') {
      setIsRecordingVideo(false);
      setVideoRecorder(null);
      setVideoChunks([]);
    } else {
      try {
        videoRecorder.stop();
      } catch (error) {

        setIsRecordingVideo(false);
        setVideoRecorder(null);
        setVideoChunks([]);
      }
    }
  };

  // Handle camera button click
  const handleCameraClick = () => {
    if (isCameraOpen) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  // Set stream to video element when camera is open and stream is available
  useEffect(() => {
    if (isCameraOpen && cameraStream) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (cameraVideoRef.current && cameraStream) {
          const video = cameraVideoRef.current;

          // Clear any existing stream first
          if (video.srcObject) {
            const oldStream = video.srcObject;
            oldStream.getTracks().forEach(track => track.stop());
          }

          // Set the new stream
          video.srcObject = cameraStream;

          // Ensure video plays
          video.play().then(() => {
            setCameraLoading(false);
          }).catch(error => {

            setCameraLoading(false);
          });

          // Handle video loaded metadata
          const handleLoadedMetadata = () => {

            setCameraLoading(false);
          };

          // Handle video can play
          const handleCanPlay = () => {
            video.play().catch(error => {

            });
            setCameraLoading(false);
          };

          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('canplay', handleCanPlay);

          // Return cleanup function
          return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('canplay', handleCanPlay);
          };
        }


        return () => {
          clearTimeout(timeoutId);
          // Also cleanup event listeners if video element exists
          if (cameraVideoRef.current) {
            const video = cameraVideoRef.current;
            video.removeEventListener('loadedmetadata', () => { });
            video.removeEventListener('canplay', () => { });
          }
        };
      }, 100);
    }
  }, [isCameraOpen, cameraStream]);

  // Restart camera when mode changes
  useEffect(() => {
    if (isCameraOpen && cameraStream) {
      // Restart camera with new mode settings
      stopCamera();
      setTimeout(() => {
        startCamera();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
        if (mediaRecorder.stream) {
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }
      }
      if (audioContext) {
        audioContext.close();
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (recordedAudio && recordedAudio.url) {
        URL.revokeObjectURL(recordedAudio.url);
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (videoRecorder && videoRecorder.state !== 'inactive') {
        videoRecorder.stop();
      }
    };
  }, [recordingTimer, mediaRecorder, recordedAudio, audioContext, audioStream, cameraStream, videoRecorder]);

  // Use ref for message input to prevent re-renders while typing
  const messageInputRef = useRef(null);

  // Memoize send button disabled state
  const isSendDisabled = useMemo(() => {
    // Prevent sending if chat privacy restricted
    if (currentChat && currentChat.can_send_message === false) return true;

    // Allow sending if there are files selected (even without text) or if there's text
    return (!messageInputText.trim() && selectedFiles.length === 0) || isSending;
  }, [messageInputText, selectedFiles.length, isSending, currentChat?.can_send_message]);

  // Note: sendButtonStyles removed - now using CSS classes

  // Track if messages have been initially loaded for each conversation
  const initialLoadRef = useRef(new Set());

  // Clear initial load tracking when conversation changes
  useEffect(() => {
    if (selectedChat) {
      // Reset initial load tracking for new conversation
      initialLoadRef.current.delete(selectedChat);
    }
  }, [selectedChat]);

  // Auto-scroll to bottom when messages are loaded (only when first opening conversation)
  useEffect(() => {
    if (selectedChat && !messagesLoading && currentMessages.length > 0) {
      // Only scroll if this is the first time loading messages for this conversation
      if (!initialLoadRef.current.has(selectedChat)) {
        initialLoadRef.current.add(selectedChat);
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      }
    }
  }, [selectedChat, messagesLoading]); // Removed memoizedMessages.length to prevent re-renders while typing




  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .chat-sos-badge {
            background-color: #ef4444;
            color: white;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: bold;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            margin-left: 4px;
            vertical-align: middle;
          }
        `}
      </style>
      <div className="chat-page">
        {/* Chat Container */}
        <div
          className="chat-container"
          onTouchStart={(e) => {
            // Only handle swipe on mobile when sidebar is closed
            if (isMobile && !isMobileSidebarOpen) {
              swipeStartRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                time: Date.now()
              }
            }
          }}
          onTouchMove={(e) => {
            // Prevent default scrolling if we're swiping horizontally
            if (swipeStartRef.current && isMobile && !isMobileSidebarOpen) {
              const deltaX = e.touches[0].clientX - swipeStartRef.current.x
              const deltaY = Math.abs(e.touches[0].clientY - swipeStartRef.current.y)

              // If horizontal swipe is more dominant than vertical, prevent scroll
              if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
                e.preventDefault()
              }
            }
          }}
          onTouchEnd={(e) => {
            if (swipeStartRef.current && isMobile && !isMobileSidebarOpen) {
              const deltaX = e.changedTouches[0].clientX - swipeStartRef.current.x
              const deltaY = Math.abs(e.changedTouches[0].clientY - swipeStartRef.current.y)
              const deltaTime = Date.now() - swipeStartRef.current.time

              // Check if it's a right swipe (left to right) and horizontal movement is dominant
              if (deltaX > swipeThreshold && deltaX > deltaY && deltaTime < 300) {
                setIsMobileSidebarOpen(true)
              }

              swipeStartRef.current = null
            }
          }}
        >
          {/* Mobile Overlay */}
          {isMobile && (
            <div
              className={`chat-mobile-overlay ${isMobileSidebarOpen ? 'mobile-open' : ''}`}
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Chat List Sidebar */}
          <div className={`chat-sidebar ${isMobile ? (isMobileSidebarOpen ? 'mobile-open' : 'mobile-hidden') : ''}`}>
            {/* Search Header */}
            <div className="chat-search-header">
              {/* Mobile Close Button - Inside header */}
              {isMobile && (
                <button
                  className="chat-sidebar-close-btn"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  aria-label="Close chat list"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
              <div className="chat-search-container">
                <div className="chat-search-wrapper">
                  <Search className="chat-search-icon" />
                  <input
                    type="text"
                    placeholder={t('chat.search.placeholder')}
                    className="chat-search-input"
                    style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                  />
                </div>
                <button
                  onClick={loadChats}
                  disabled={loading}
                  className="chat-refresh-btn"
                  title={loading ? t('chat.common.loading') : t('chat.search.refresh')}
                >
                  <RefreshCw
                    size={18}
                    className={loading ? "chat-refresh-icon" : ""}
                  />
                </button>
              </div>

              {/* Tab Buttons - Hidden for veterinarians */}
              <div className="chat-tabs">
                <button
                  onClick={() => {
                    setActiveTab('all');
                  }}
                  className={`chat-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                >
                  {tabLabels.all}
                </button>
                <button
                  onClick={() => setActiveTab('social')}
                  className={`chat-tab-btn ${activeTab === 'social' ? 'active' : ''}`}
                >
                  {tabLabels.social}
                </button>
                <button
                  onClick={() => setActiveTab('medical')}
                  className={`chat-tab-btn ${activeTab === 'medical' ? 'active' : ''}`}
                >
                  {tabLabels.medical}
                </button>
              </div>
            </div>

            {/* New Conversation Button */}
            <div className="chat-new-conversation-container">
              <button
                className="chat-new-conversation-btn"
                onClick={() => setShowSelectFriendModal(true)}
                title={t('chat.newConversation.button', 'New Conversation')}
              >
                <MessageSquarePlus size={20} />
                <span>{t('chat.newConversation.button', 'New Conversation')}</span>
              </button>
            </div>

            {/* Chat List */}
            <div className="chat-list">
              {error && (
                <div className="chat-error-container">
                  <p className="chat-error-message" style={{ textAlign: isRTL ? 'right' : 'left' }}>{error}</p>
                  <button
                    onClick={loadChats}
                    className="chat-retry-btn"
                  >
                    {t('chat.list.retry')}
                  </button>
                </div>
              )}
              {!error && filteredChats.length === 0 && !loading && (
                <div className="chat-empty-container">
                  <p style={{ textAlign: isRTL ? 'right' : 'left' }}>
                    {getEmptyStateMessage()}
                  </p>
                </div>
              )}
              {!error && filteredChats.map((chat) => (chat.isempty !== true || chat.id === selectedChat) && (
                <div
                  key={chat.id}
                  onClick={() => {
                    setSelectedChat(chat.id);

                    // Update URL with new routing structure
                    const userId = chat.otherParticipant?.id || chat.receiver_id;
                    const convType = chat.type || 'all';
                    if (chat.id) {
                      navigate(`/chat/${chat.id}/${convType}`);
                    }

                    // Close mobile sidebar when chat is selected
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                    setTimeout(() => {
                      // scrollToBottom();
                    }, 0);
                    // Mark as read will be called automatically by useEffect when selectedChat changes
                  }}
                  className={`chat-item ${chat.type === 'medical' ? 'medical' : ''} ${selectedChat === chat.id ? 'selected' : ''} ${chat.isClosed ? 'closed' : ''} ${recentlyUpdatedChats.has(chat.id) ? 'recently-updated' : ''}`}
                >
                  <div className="chat-item-content">
                    <div className="chat-item-avatar-container">
                      <CachedImage
                        src={chat.avatar || getUserPlaceholderImage(chat.otherParticipant || chat)}
                        alt={chat.name}
                        className="chat-item-avatar"
                      />
                      {chat.isOnline && (
                        <div className="chat-online-indicator" />
                      )}
                      {chat.type === 'medical' && (
                        <div className="chat-medical-badge">
                          🏥
                        </div>
                      )}
                      {chat.isSOS && (
                        <div className="chat-sos-badge" title="Urgent Medical Case">
                          SOS
                        </div>
                      )}
                      {chat.isClosed && (
                        <div
                          className="chat-closed-badge"
                          title={`Chat closed on ${formatTimestamp(chat.closedAt)}`}
                        >
                          🔒
                        </div>
                      )}
                    </div>
                    <div className="chat-item-info" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      <div className="chat-item-header">
                        <h3 className="chat-item-name">
                          {chat.name}
                        </h3>
                        <span className="chat-item-timestamp">
                          {chat.timestamp}
                        </span>
                      </div>
                      <div className="chat-item-footer">
                        {(() => {
                          const typingUsers = getTypingUsersForConversation(chat.id);
                          const isTyping = typingUsers.length > 0;

                          return isTyping ? (
                            <p className="chat-item-message typing-indicator" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', fontStyle: 'italic', color: '#666' }}>
                              {t('chat.typing', 'typing...')}
                            </p>
                          ) : (
                            <p className="chat-item-message" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                              {chat.lastMessage}
                            </p>
                          );
                        })()}
                        {chat.unreadCount > 0 && (
                          <span className="chat-unread-badge">
                            {chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Window */}
          <div className="chat-window" style={{ position: "relative" }}>
            {/* Mobile Menu Toggle Button - Always visible on mobile */}
            {isMobile && (
              <button
                className="chat-mobile-menu-toggle"
                onClick={() => setIsMobileSidebarOpen(true)}
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "15px",
                  width: "40px",
                  height: "40px",
                  minWidth: "44px",
                  minHeight: "44px",
                  background: "#8548A8",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: "10",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                }}
                aria-label="Open chat list"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
            )}

            {currentChat ? (
              <>
                {/* Chat Header */}
                <div className={`chat-header ${currentChat.type === 'medical' ? 'medical' : ''}`}>
                  <div className="chat-header-content">
                    <div className="chat-header-info" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      <div className="chat-header-avatar-container">
                        <CachedImage
                          src={currentChat.avatar || getUserPlaceholderImage(currentChat.otherParticipant || currentChat)}
                          alt={currentChat.name}
                          className="chat-header-avatar"
                        />
                        {currentChat.isOnline && (
                          <div className="chat-online-indicator" />
                        )}
                      </div>
                      <div className="chat-header-details" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {currentChat.name}
                          {currentChat.isSOS && (
                            <span className="chat-sos-badge">
                              SOS
                            </span>
                          )}
                        </h2>
                        <p className={`chat-header-status ${currentChat.isClosed ? 'closed' : (currentChat.isOnline ? 'online' : '')}`} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                          {currentChat.isClosed
                            ? t('chat.header.status.closed', { date: formatTimestamp(currentChat.closedAt) })
                            : usersTyping.length > 0
                              ? t('chat.header.status.typing', { defaultValue: 'typing...' })
                              : currentChat.isOnline
                                ? t('chat.header.status.online')
                                : currentChat.lastSeenAt
                                  ? currentChat.lastSeenAt
                                  : t('chat.header.status.recent')}
                        </p>
                      </div>
                    </div>
                    <div className="chat-header-actions">
                      <button className="chat-header-action-btn">
                        <Video className="chat-header-action-icon" />
                      </button>
                      <button className="chat-header-action-btn">
                        <Phone className="chat-header-action-icon" />
                      </button>

                      {/* Optional Rating Button for SOS cases */}
                      {currentChat.isClosed && (currentChat.isSOS || currentChat.urgent_case_id || currentChat.case_id || currentChat.type === 'medical') && (
                        <button
                          className="chat-header-action-btn rating-btn"
                          onClick={() => {
                            const caseId = currentChat.urgent_case_id || currentChat.case_id || selectedChat;
                            setRatingCaseId(caseId);
                            setShowRatingModal(true);
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#FFF8E1",
                            border: "1px solid #FFD54F",
                            borderRadius: "16px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.2s ease",
                            color: "#F57C00",
                            fontSize: "12px",
                            fontWeight: "600",
                            height: "auto"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFECB3";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#FFF8E1";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <Star className="star-icon" style={{ width: "14px", height: "14px", fill: "#F57C00" }} />
                          {t('chat.actions.rate', { defaultValue: 'Rate' })}
                        </button>
                      )}
                      <div style={{ position: 'relative' }} ref={headerMenuRef}>
                        <button
                          className="chat-header-action-btn"
                          onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                          style={{
                            backgroundColor: showHeaderMenu ? 'rgba(0,0,0,0.05)' : 'transparent'
                          }}
                        >
                          <MoreHorizontal className="chat-header-action-icon" />
                        </button>

                        {/* Dropdown Menu */}
                        {showHeaderMenu && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '8px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: '8px 0',
                            minWidth: '160px',
                            zIndex: 100,
                            border: '1px solid #E5E7EB'
                          }}>
                            <button
                              onClick={() => {
                                setShowHeaderMenu(false);
                                setIsHideChatModalOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                padding: '8px 16px',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: '#4B5563',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                textAlign: 'left',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                              </svg>
                              {t("chat.actions.hide", { defaultValue: "Hide Chat" })}
                            </button>
                            <button
                              onClick={() => {
                                setShowHeaderMenu(false);
                                setIsDeleteChatModalOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                padding: '8px 16px',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: '#EF4444',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                textAlign: 'left',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#FEF2F2'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                              {t("delete_chat", { defaultValue: "Delete Chat" })}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div ref={messagesContainerRef} className="chat-messages-container">
                  {messagesLoading && (
                    <div className="chat-messages-loading">
                      <div className="chat-messages-spinner"></div>
                      <p className="chat-messages-loading-text" style={{ textAlign: isRTL ? 'right' : 'left' }}>{t('chat.messages.loading')}</p>
                    </div>
                  )}
                  {messagesError && (
                    <div className="chat-messages-error">
                      <p className="chat-messages-error-message" style={{ textAlign: isRTL ? 'right' : 'left' }}>{messagesError}</p>
                      <button
                        onClick={() => loadMessages(selectedChat)}
                        className="chat-retry-btn"
                      >
                        {t('chat.list.retry')}
                      </button>
                    </div>
                  )}
                  {!messagesLoading && !messagesError && (
                    <div>
                      {/* Load More Messages Button - Show at top if there are more messages */}
                      {hasMoreMessages[selectedChat] && !loadingOlderMessages && memoizedMessageList.length > 0 && (
                        <div className="chat-load-more-container">
                          <button
                            onClick={loadOlderMessages}
                            disabled={loadingOlderMessages}
                            className="chat-load-more-btn"
                          >
                            {loadingOlderMessages ? (
                              <>
                                <div className="chat-load-more-spinner" />
                                {t('chat.messages.loadMore.loading')}
                              </>
                            ) : (
                              <>
                                <span>↑</span>
                                {t('chat.messages.loadMore.trigger')}
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Messages */}
                      {MemoizedMessagesContainer}

                      {/* Typing Indicator */}
                      {usersTyping.length > 0 && (
                        <TypingIndicator />
                      )}

                      {/* Show "Load more" button if there are more messages */}
                      {/* {hasMoreMessages[selectedChat] && !loadingOlderMessages && currentMessages.length > 0 && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <button
                        onClick={loadOlderMessages}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#4A90E2",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontFamily: '"Red Hat Display", sans-serif',
                        }}
                      >
                        Load Older Messages
                      </button>
                    </div>
                  )} */}
                    </div>
                  )}
                  {/* Scroll to bottom button - only show when there are new messages while scrolled up */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Veterinarian Action Buttons - Only show for medical conversations when user is veterinarian */}
                {currentChat && currentChat.type === 'medical' && currentUserType === USER_TYPES.VETERINARIAN && (
                  <div className="chat-buttons-container">
                    <div className="chat-buttons">
                      <button
                        className="vet-chat-buttons vet-chat-end-button"
                        onClick={handleEndConversation}
                        disabled={currentChat && currentChat.isClosed}
                        style={{
                          opacity: currentChat && currentChat.isClosed ? 0.5 : 1,
                          cursor: (currentChat && currentChat.isClosed) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {currentChat && currentChat.isClosed ? 'Closed' : 'End'}
                      </button>
                      <button
                        className="vet-chat-buttons vet-chat-schedule-button"
                        onClick={handleOpenAppointmentModal}
                        disabled={!currentChat || currentChat.isClosed}
                        style={{
                          opacity: (!currentChat || currentChat.isClosed) ? 0.5 : 1,
                          cursor: (!currentChat || currentChat.isClosed) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Schedule Appointment
                      </button>
                    </div>
                  </div>
                )}

                {/* Message Input - Show for all conversations */}
                <div className={`chat-input-container ${currentChat.type === 'medical' ? 'medical' : ''} ${currentChat.isClosed ? 'closed' : ''}`}>
                  {/* Closed Chat Warning */}
                  {currentChat.isClosed && (
                    <div className="chat-closed-warning">
                      <div className="chat-closed-warning-icon">🔒</div>
                      <div>
                        <p className="chat-closed-warning-title">
                          {currentChat.isSOS ? "Emergency Consultation Closed" : t('chat.messages.closedTitle')}
                        </p>
                        <p className="chat-closed-warning-text">
                          {t('chat.messages.closedDescription', { date: formatTimestamp(currentChat.closedAt) })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Recording Indicator */}
                  {isRecording && (
                    <div className="chat-recording-indicator">
                      <div className="chat-recording-content">
                        <div className="chat-recording-animation">
                          <div className="chat-recording-dot"></div>
                        </div>
                        <div className="chat-recording-waveform">
                          <AudioWaveform
                            audioContext={audioContext}
                            analyserNode={analyserNode}
                            isRecording={isRecording}
                            width={200}
                            crossOrigin="anonymous"
                            height={40}
                            color="#EF4444"
                          />
                        </div>
                        <div className="chat-recording-info">
                          <span className="chat-recording-text">{t('chat.recording.recording')}</span>
                          <span className="chat-recording-duration">{formatDuration(recordingDuration)}</span>
                        </div>
                        <button
                          onClick={cancelRecording}
                          className="chat-recording-cancel-btn"
                          title={t('chat.recording.cancel')}
                        >
                          ✕
                        </button>
                        <button
                          onClick={stopRecording}
                          className="chat-recording-stop-btn"
                          title={t('chat.recording.stopReview')}
                        >
                          {t('chat.recording.stop')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Recorded Audio Preview */}
                  {recordedAudio && !isRecording && (
                    <div className="chat-audio-preview">
                      <div className="chat-audio-preview-content">
                        <div className="chat-audio-preview-info">
                          <div className="chat-audio-preview-icon">🎤</div>
                          <div>
                            <p className="chat-audio-preview-title">{voiceMessageLabel}</p>
                            <p className="chat-audio-preview-duration">{formatDuration(recordedAudio.duration)}</p>
                          </div>
                        </div>
                        <audio
                          crossOrigin="anonymous"
                          src={recordedAudio.url}
                          controls
                          muted={false}
                          className="chat-audio-preview-player"
                          onLoadedMetadata={(e) => {
                            const audio = e.target;
                            audio.muted = false;
                            audio.volume = 1.0;
                          }}
                          onCanPlay={(e) => {
                            const audio = e.target;
                            audio.muted = false;
                            audio.volume = 1.0;
                          }}
                          onPlay={(e) => {
                            const audio = e.target;
                            audio.muted = false;
                            audio.volume = 1.0;
                          }}
                          onVolumeChange={(e) => {
                            const audio = e.target;
                            if (audio.muted) {
                              audio.muted = false;
                            }
                            if (audio.volume < 1.0) {
                              audio.volume = 1.0;
                            }
                          }}
                        />
                        <div className="chat-audio-preview-actions">
                          <button
                            onClick={cancelRecording}
                            className="chat-audio-preview-cancel"
                            title={t('chat.audioPreview.cancel')}
                          >
                            ✕
                          </button>
                          <button
                            onClick={sendRecordedAudio}
                            disabled={isSending}
                            className="chat-audio-preview-send"
                            title={t('chat.audioPreview.send')}
                          >
                            {isSending ? (
                              <div className="chat-input-send-spinner" />
                            ) : (
                              <Send className="chat-input-send-icon" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="chat-files-preview">
                      <div className="chat-files-preview-header">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span className="chat-files-preview-title">
                            {t('chat.files.previewTitle', { count: selectedFiles.length })}
                          </span>
                          <span className="chat-files-preview-disabled-text">
                            {t('chat.files.disabled')}
                          </span>
                        </div>
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="chat-files-preview-clear"
                        >
                          {t('chat.files.clear')}
                        </button>
                      </div>
                      <div className="chat-files-grid">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="chat-file-preview">
                            {/* File Header */}
                            <div className="chat-file-preview-header">
                              <div className="chat-file-preview-header-content">
                                <span className="chat-file-preview-icon">{getFileIcon(file.type)}</span>
                                <div className="chat-file-preview-info">
                                  <p className="chat-file-preview-name">
                                    {file.name}
                                  </p>
                                  <p className="chat-file-preview-size">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => removeFile(index)}
                                className="chat-file-preview-remove"
                              >
                                ✕
                              </button>
                            </div>
                            {/* File Content */}
                            {file.type && file.type.startsWith('image/') && (
                              <div className="chat-file-preview-content">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  loading="lazy"
                                  decoding="async"
                                  className="chat-file-preview-image"
                                  onLoad={(e) => {
                                    e.target.style.opacity = "1";
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                            {file.type && file.type.startsWith('video/') && (
                              <div className="chat-file-preview-content">
                                <video
                                  controls
                                  muted={false}
                                  playsInline
                                  preload="metadata"
                                  className="chat-file-preview-video"
                                  crossOrigin={undefined}
                                  onLoadedMetadata={(e) => {
                                    const video = e.target;
                                    video.muted = false;
                                    video.volume = 1.0;
                                  }}
                                  onLoadedData={(e) => {
                                    const video = e.target;
                                    video.muted = false;
                                    video.volume = 1.0;
                                  }}
                                  onCanPlay={(e) => {
                                    const video = e.target;
                                    video.muted = false;
                                    video.volume = 1.0;
                                  }}
                                  onPlay={(e) => {
                                    const video = e.target;
                                    video.muted = false;
                                    video.volume = 1.0;
                                  }}
                                  onVolumeChange={(e) => {
                                    const video = e.target;
                                    if (video.muted) {
                                      video.muted = false;
                                    }
                                    if (video.volume < 1.0) {
                                      video.volume = 1.0;
                                    }
                                  }}
                                >
                                  <source src={URL.createObjectURL(file)} type={file.type} />
                                  {t('chat.media.videoNotSupported')}
                                </video>
                              </div>
                            )}
                            {(!file.type || (!file.type.startsWith('image/') && !file.type.startsWith('video/'))) && (
                              <div className="chat-file-preview-content">
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                  <span style={{ fontSize: "10px", color: "#6C757D" }}>
                                    {formatFileSize(file.size)}
                                  </span>
                                  <span style={{ fontSize: "10px", color: "#6C757D" }}>
                                    {getFileIcon(file.type)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Back to Adoption Request Button */}
                  {(adoptionRequestId || (currentChat?.case_id && currentChat?.case_type?.includes('AdoptionRequest'))) && (
                    <div className="chat-back-to-adoption-container">
                      <button
                        className="chat-back-to-adoption-btn"
                        onClick={() => {
                          const id = adoptionRequestId || currentChat.case_id;
                          navigate(`/adoption?openRequests=true&highlightRequest=${id}`);
                        }}
                        title={t('chat.header.backToAdoption') || 'Back to Adoption Request'}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        <span>{t('chat.header.backToAdoption') || 'Back to Adoption Request'}</span>
                      </button>
                    </div>
                  )}

                  <div className="chat-input-bar">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                    <button
                      onClick={() => !(currentChat?.can_send_message === false) && fileInputRef.current?.click()}
                      className="chat-input-file-btn"
                      disabled={currentChat?.can_send_message === false}
                    >
                      <Paperclip className="chat-input-icon" />
                    </button>
                    <button
                      onClick={() => !(currentChat?.can_send_message === false) && handleCameraClick()}
                      className="chat-input-file-btn"
                      disabled={currentChat?.can_send_message === false}
                      title="Open camera"
                    >
                      <Camera className="chat-input-icon" />
                    </button>
                    <input
                      ref={messageInputRef}
                      type="text"
                      value={messageInputText}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setMessageInputText(newValue);
                        // Trigger typing indicator - will stop automatically if empty
                        onTyping(e);
                        // Also manually stop typing if input becomes empty
                        if (!newValue.trim()) {
                          stopCurrentUserTyping();
                        }
                      }}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        currentChat?.can_send_message === false
                          ? (t('chat.input.privacyRestricted') || 'You cannot message this user due to their privacy settings')
                          : (selectedFiles.length > 0 ? t('chat.input.mediaSelected') : t('chat.input.placeholder'))
                      }
                      disabled={selectedFiles.length > 0 || currentChat?.can_send_message === false}
                      className="chat-input"
                      style={getRTLStyles(messageInputText)}
                    />
                    <button
                      ref={emojiButtonRef}
                      className="chat-input-file-btn"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Emoji"
                    >
                      <Smile className="chat-input-icon" />
                    </button>
                    <button
                      className="chat-input-file-btn"
                      onClick={() => {
                        if (isRecording) {
                          stopRecording();
                        } else if (recordedAudio) {
                          // If there's a recorded audio, send it
                          sendRecordedAudio();
                        } else {
                          // Start new recording
                          startRecording();
                        }
                      }}
                      disabled={selectedFiles.length > 0 || currentChat?.can_send_message === false}
                      title={isRecording ? t('chat.input.voice.stop') : recordedAudio ? t('chat.input.voice.send') : t('chat.input.voice.start')}
                    >
                      <Mic className="chat-input-icon" style={{ color: currentChat?.can_send_message === false ? '#6C757D' : micButtonColor }} />
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={isSendDisabled}
                      className="chat-input-send-btn"
                    >
                      {isSending ? (
                        <div className="chat-input-send-spinner" />
                      ) : (
                        <Send className="chat-input-send-icon" />
                      )}
                    </button>
                  </div>
                  {showEmojiPicker && (
                    <EmojiPicker
                      onEmojiSelect={insertEmoji}
                      onClose={() => setShowEmojiPicker(false)}
                      positionRef={emojiButtonRef}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="chat-empty-state">
                <div className="chat-empty-content" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', padding: "20px" }}>
                  <div className="chat-empty-icon-container">
                    <Search className="chat-empty-icon" />
                  </div>
                  <h3 className="chat-empty-title">
                    {t('chat.empty.title')}
                  </h3>
                  <p className="chat-empty-text">
                    {t('chat.empty.subtitle')}
                  </p>
                  {/* Mobile hint */}
                  <p className="chat-mobile-hint">
                    👆 Tap the menu button (☰) to view conversations
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Camera Modal */}
          {isCameraOpen && (
            <div className="chat-camera-modal" onClick={stopCamera}>
              <div className="chat-camera-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Close button */}
                <button onClick={stopCamera} className="chat-camera-modal-close">
                  ✕
                </button>

                {/* Camera preview */}
                <div className="chat-camera-preview">
                  {cameraLoading && (
                    <div className="chat-camera-loading">
                      <div className="chat-camera-loading-spinner"></div>
                      <p>Starting camera...</p>
                    </div>
                  )}
                  <video
                    ref={cameraVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="chat-camera-video"
                    style={{ display: cameraLoading ? 'none' : 'block' }}
                    onLoadedMetadata={(e) => {
                      const video = e.target;

                      setCameraLoading(false);
                      video.play().catch(err => {

                      });
                    }}
                    onCanPlay={(e) => {
                      const video = e.target;
                      setCameraLoading(false);
                      video.play().catch(err => {

                      });
                    }}
                    onError={(e) => {

                      setCameraLoading(false);
                    }}
                  />
                  <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />

                  {/* Recording indicator for video mode */}
                  {isRecordingVideo && (
                    <div className="chat-camera-recording-indicator">
                      <div className="chat-camera-recording-dot"></div>
                      <span>Recording...</span>
                    </div>
                  )}
                </div>

                {/* Camera controls */}
                <div className="chat-camera-controls">
                  {/* Mode toggle */}
                  <div className="chat-camera-mode-toggle">
                    <button
                      onClick={() => setCameraMode('photo')}
                      className={`chat-camera-mode-btn ${cameraMode === 'photo' ? 'active' : ''}`}
                    >
                      Photo
                    </button>
                    <button
                      onClick={() => setCameraMode('video')}
                      className={`chat-camera-mode-btn ${cameraMode === 'video' ? 'active' : ''}`}
                    >
                      Video
                    </button>
                  </div>

                  {/* Action buttons */}
                  <div className="chat-camera-actions">
                    {cameraMode === 'photo' ? (
                      <button
                        onClick={capturePhoto}
                        className="chat-camera-capture-btn"
                        title="Capture photo"
                      >
                        <div className="chat-camera-capture-icon"></div>
                      </button>
                    ) : (
                      <>
                        {!isRecordingVideo ? (
                          <button
                            onClick={startVideoRecording}
                            className="chat-camera-record-btn"
                            title="Start recording"
                          >
                            <div className="chat-camera-record-icon"></div>
                          </button>
                        ) : (
                          <button
                            onClick={stopVideoRecording}
                            className="chat-camera-stop-btn"
                            title="Stop recording"
                          >
                            <div className="chat-camera-stop-icon"></div>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media Modal - Rendered via Portal to document body */}
          {showMediaModal && selectedMedia && createPortal(
            <div className="chat-media-modal" onClick={closeMediaModal}>
              <div className="chat-media-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Close button */}
                <button onClick={closeMediaModal} className="chat-media-modal-close">
                  ✕
                </button>

                {/* Media content */}
                {(selectedMedia.type && selectedMedia.type.startsWith('audio/')) || selectedMedia.isAudio ? (
                  null
                ) : selectedMedia.isImage || (selectedMedia.type && selectedMedia.type.startsWith('image/')) ? (
                  <CachedImage
                    src={selectedMedia.url}
                    alt={selectedMedia.name || t('chat.media.fallback')}
                    className="chat-media-modal-image"
                  />
                ) : selectedMedia.isVideo || (selectedMedia.type && selectedMedia.type.startsWith('video/')) ? (
                  <video
                    controls
                    muted={false}
                    playsInline
                    className="chat-media-modal-video"
                    onLoadedMetadata={(e) => {
                      const video = e.target;
                      video.muted = false;
                      video.volume = 1.0;
                    }}
                    onLoadedData={(e) => {
                      const video = e.target;
                      video.muted = false;
                      video.volume = 1.0;
                    }}
                    onCanPlay={(e) => {
                      const video = e.target;
                      video.muted = false;
                      video.volume = 1.0;
                    }}
                    onPlay={(e) => {
                      const video = e.target;
                      video.muted = false;
                      video.volume = 1.0;
                    }}
                    onVolumeChange={(e) => {
                      const video = e.target;
                      if (video.muted) {
                        video.muted = false;
                      }
                      if (video.volume < 1.0) {
                        video.volume = 1.0;
                      }
                    }}
                    onError={() => { }}
                  >
                    <source src={selectedMedia.url} type={selectedMedia.type} />
                    {t('chat.media.videoNotSupported')}
                  </video>
                ) : selectedMedia.isDocument || (selectedMedia.type && selectedMedia.type.startsWith('application/')) ? (
                  <div className="chat-media-modal-file">
                    <div className="chat-media-modal-file-icon">
                      {getFileIcon(selectedMedia.type)}
                    </div>
                    <h3 className="chat-media-modal-file-title">
                      {selectedMedia.name || t('chat.media.document')}
                    </h3>
                    <p className="chat-media-modal-file-size">
                      {formatFileSize(selectedMedia.size || 0)}
                    </p>
                    <a
                      href={selectedMedia.url}
                      download={selectedMedia.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-media-modal-file-download"
                    >
                      {t('chat.media.download')}
                    </a>
                  </div>
                ) : (
                  <div className="chat-media-modal-file">
                    <div className="chat-media-modal-file-icon">
                      {getFileIcon(selectedMedia.type)}
                    </div>
                    <h3 className="chat-media-modal-file-title">
                      {selectedMedia.name || t('chat.media.file')}
                    </h3>
                    <p className="chat-media-modal-file-size">
                      {formatFileSize(selectedMedia.size || 0)}
                    </p>
                    <a
                      href={selectedMedia.url}
                      download={selectedMedia.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-media-modal-file-download"
                    >
                      {t('chat.media.download')}
                    </a>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      <DeleteMessageModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMessageToDelete(null);
        }}
        onConfirm={confirmDeleteMessage}
        t={t}
        isRTL={isRTL}
      />

      {/* SOS Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onRate={({ rating, comment }) => urgentCaseAPI.rateUrgentCase(ratingCaseId, { rating, comment })}
        onSkip={() => setShowRatingModal(false)}
        title={t('ratings.title', { defaultValue: 'Rate Experience' })}
        subtitle={t('ratings.subtitle', { defaultValue: 'How was your experience with this urgent case?' })}
      />

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        onSave={handleSaveAppointment}
        vets={clinicMembers?.filter(member => member.role === 'doctor') || []}
        workingHours={clinic?.working_hours || []}
        appointmentTypes={(targetClinicData?.appointment_types || clinicData?.appointment_types) || []}
        isAppointments={true}
        initialVet={initialAppointmentValues.initialVet}
        initialPetOwner={initialAppointmentValues.initialPetOwner}
        initialPet={initialAppointmentValues.initialPet}
      />

      {/* Select Friend Modal */}
      <SelectFriendModal
        isOpen={showSelectFriendModal}
        onClose={() => setShowSelectFriendModal(false)}
        onSelectFriend={handleFriendSelect}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteChatModalOpen}
        onClose={() => setIsDeleteChatModalOpen(false)}
        onConfirm={handleDeleteChat}
        isLoading={isDeletingChat}
        title={t("delete_chat_confirm_title")}
        message={t("delete_chat_confirm_message")}
        confirmText={t("delete_chat")}
      />

      <DeleteConfirmationModal
        isOpen={isHideChatModalOpen}
        onClose={() => setIsHideChatModalOpen(false)}
        onConfirm={handleHideChat}
        isLoading={isHidingChat}
        title={t("hide_chat_confirm_title")}
        message={t("hide_chat_confirm_message")}
        confirmText={t("chat.actions.hide", { defaultValue: "Hide Chat" })}
      />
    </>
  );
};

export default ChatPage;